import { appConfirm } from '../../services/appDialogs';
import { AppSelect } from '../ui/AppSelect';
import { VIDEO_PROVIDER_VOLCENGINE_SEEDANCE, DOMESTIC_SEEDANCE_MODELS, getDomesticSeedanceResolutions, isDomesticOfficialSeedance, normalizeDomesticSeedanceResolution } from '../../utils/domesticSeedanceCapabilities';
import { ModelArkReferenceStatus } from '../assets/ModelArkReferenceStatus';
import { useModelArkAssets } from '../../hooks/useModelArkAssets';
import { getModelArkReferenceState, usesModelArkAssetLibrary } from '../../utils/modelArkAssets';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  BookOpen,
  Camera,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clipboard,
  Copy,
  Download,
  Film,
  History,
  Image as ImageIcon,
  Lightbulb,
  Loader2,
  MessageSquareWarning,
  MoreHorizontal,
  Move3d,
  Music,
  Play,
  Plus,
  RefreshCw,
  RemoveFormatting,
  Save,
  Search,
  Settings2,
  SlidersHorizontal,
  Sparkles,
  SquareDashed,
  Trash2,
  Upload,
  Users,
  Video,
  X,
} from "lucide-react";
import { API_HOST } from "../../utils/constants";
import { authenticatedFetch } from "../../utils/api";
import { runTaskToCompletion } from "../../services/taskService";
import {
  fetchMiniStoryboardMusicCues,
  generateMusicAudio,
  planMiniStoryboardMusicCues,
  updateMiniStoryboardMusicCue,
} from "../../services/audioService";
import {
  isDurableVideoPollingProvider,
  startPostProcessPolling,
  submitEraseSubtitle,
  submitVideoTask,
  submitWandStoryboardTask,
  VIDEO_POLLING_MAX_MS,
} from "../../services/videoService";
import { useToast } from "../../context/ToastContext";
import { ConfirmDialog } from "@ui";
import {
  buildGuaVideoContents,
  firstText,
  getAssetGuaVideoMeta,
  getAssetImageUrl,
  getAssetMetaForUrl,
  normalizeImageUrl,
} from "./episode-storyboard-board/utils";
import { useAssetStatusMap } from "../../hooks/useAssetStatusMap";
import { useDelayedUnmount } from "../../hooks/useDelayedUnmount";
import { LIGHTING_PRESETS } from "../storyboard/common/LightingPresets";
import HoverImagePreview from "./episode-storyboard-board/HoverImagePreview";
import MiniStoryboardTimeline from "./mini-storyboard/MiniStoryboardTimeline";
import { findLinkedMiniStoryboardSegment } from '../../utils/miniStoryboardNavigation';
import { getMiniStoryboardHistoryKey, matchesMiniStoryboardHistoryItem } from '../../utils/miniStoryboardHistorySafety';
import { getMiniStoryboardSourcePageKey, resolveMiniStoryboardSourcePage } from '../../utils/miniStoryboardSourcePaging';
import { appendDetachedLightingPrompt, extractLegacyLightingPrompt, stripStandaloneLightingBlocks } from '../../utils/miniStoryboardLighting';
import { fetchVerifiedVideoZip } from '../../utils/episodeVideoZipDownload';
import "./MiniStoryboardWorkbench.css";
import {
  getProjectDefaultVideoModel,
  getProjectDefaultVideoRatio,
  getProjectVideoSubmissionProvider,
  getVideoProviderLabel,
  getVideoAssetApiBase,
  isSd25Project,
  isWan3Project,
  isOverseasVideoProvider,
  isSeedanceVideoProvider,
  normalizeVideoProvider,
  supportsVideoAssetIngest,
  VIDEO_PROVIDER_OMINI_SEEDANCE,
} from "../../utils/projectRuntime";
import { uploadFileToCos } from "../../utils/fileUtils";
import {
  applyAssetCreateResult,
  assetLibraryLabel,
  assetStatusLabel,
  getEffectiveAssetMeta,
  isSameVideoAssetLibrary,
} from "../../utils/guaVideoAssetStatus";
import {
  mergePreparedVideoAssetMetadata,
  pollVideoAssetUntilTerminal,
  preflightVideoAssetContents,
  sanitizeVideoAuxiliaryReferenceItems,
} from "../../utils/videoAssetPreflight";
import {
  findReferenceMentions,
  hasCjkReferenceText,
  isSafeAutomaticReferenceCandidate,
  normalizeReferenceText,
  referenceNamesOverlap,
  referenceTextContainsCandidate,
  replaceReferenceMentions,
  sameReferenceName,
} from "../../utils/referenceTextMatching";
import MosaicEditorModal from "../storyboard/common/MosaicEditorModal";
import { fetchEpisodeBlocking } from "../../services/episodeBlockingService";
import {
  AI_ACTING_CARDS,
  AI_ACTING_FILTERS,
  AI_ACTING_LEVELS,
} from "../acting-guide/aiActingGuideData";
import EstimatedPoints from "../common/EstimatedPoints";
import {
  DEFAULT_SEEDANCE25_PROVIDER,
  SEEDANCE25_MODEL,
  assertSeedance25SubmissionProvider,
  SEEDANCE25_PLANNING_MAX_DURATION,
  SEEDANCE25_PROVIDER_OPTIONS,
  buildSeedance25ReferencePayload,
  getSeedance25Capabilities,
  isSeedance25NonImageReference,
  getSeedance25ReferenceMediaType,
  getSeedance25ReferenceUrl,
  supportsSeedance25MediaReferences,
  normalizeSeedance25ReferencePrompt,
  isSeedance25Provider,
  normalizeSeedance25Duration,
  normalizeSeedance25SubmissionProvider,
  normalizeSeedance25Ratio,
  normalizeSeedance25Resolution,
  resolveSeedance25Provider,
  validateSeedance25References,
} from "../../utils/seedance25Capabilities";
import {
  WANX_VIDEO_MEDIA_TYPES,
  WANX_VIDEO_MODELS,
  WANX_VIDEO_PROVIDER,
  WANX_VIDEO_RATIOS,
  WANX_VIDEO_RESOLUTIONS,
  isWanxVideoModel,
  normalizeWanxVideoDuration,
  normalizeWanxVideoModel,
  normalizeWanxVideoRatio,
  normalizeWanxVideoResolution,
  validateWanxVideoRequest,
} from "../../utils/wanxVideoCapabilities";
import {
  WAND_VEGA_PROVIDER,
  WAND_VEGA_MODELS,
  WAND_VEGA_RESOLUTIONS,
  WAND_VEGA_RATIOS,
  WAND_VEGA_STORYBOARD_REFERENCE_LIMIT,
  isWandVegaModel,
  getWandVegaDurationLimits,
  normalizeWandVegaResolution,
  normalizeWandVegaRatio,
  isPublicWandVegaImageUrl,
  buildWandVegaStoryboardRequest,
} from "../../utils/miniStoryboardVega";
import {
  MINI_STORYBOARD_DURATION_PROFILE_SEEDANCE25,
  MINI_STORYBOARD_DURATION_PROFILE_STANDARD,
  buildMiniStoryboardDurationSaveFields,
  getMiniStoryboardDurationLimits,
} from "../../utils/miniStoryboardDurationProfile";
import {
  buildOneClickFlowSplitPayload,
  getOneClickFlowDurationError,
  getOneClickFlowTimingLabel,
  isOneClickFlowGenerationResult,
  isOneClickFlowSegment,
  supportsOneClickFlowRule,
  transformOneClickFlowPrompt,
} from "../../utils/miniStoryboardOneClickFlow";
import { matchesMiniStoryboardBoardSnapshot } from "../../utils/miniStoryboardBoardRevision";

const BlockingEditorModal = React.lazy(() => import("./mini-storyboard/blocking/BlockingEditorModal"));

const DEFAULT_STYLE = "全程无文字、无字幕、无水印、禁止bgm";
const DOMESTIC_VIDEO_MODELS = [
  { id: "doubao-seedance-2-0-260128", name: "Guavideo" },
  { id: "doubao-seedance-2-0-fast-260128", name: "Guavideo Fast" },
];
const OVERSEAS_VIDEO_MODELS = [
  { id: "dreamina-seedance-2-0", name: "Dreamina 2.0" },
  { id: "dreamina-seedance-2-0-fast", name: "Dreamina 2.0 Fast" },
];
const OMINI_VIDEO_MODELS = [
  { id: "dreamina-seedance-2-0", name: "GuaOmini 2.0" },
  { id: "dreamina-seedance-2-0-fast", name: "GuaOmini 2.0 Fast" },
];
const SEEDANCE25_VIDEO_MODEL = { id: SEEDANCE25_MODEL, name: "即梦 2.5" };
const WAN3_VIDEO_MODELS = WANX_VIDEO_MODELS.map((item) => ({
  id: item.value,
  name: `万相 3.0 ${item.label}`,
})).concat(WAND_VEGA_MODELS);
const STORY_MODES = [
  { id: "drama", name: "实拍短剧" },
  { id: "narration", name: "解说剧" },
];
const STORYBOARD_ENHANCEMENT_MODES = [
  { id: "none", name: "无选项", description: "使用原有拆分逻辑，不额外加入增强约束。" },
  { id: "director_depth", name: "导演深度分镜", description: "强化站位、轴线、切点、反应镜头和节奏。" },
  { id: "mx_shell", name: "mx-shell风格分镜", description: "强化画面构造、可拍动作、视觉落点和电影化描述。" },
  { id: "sequence_contract", name: "片段契约", description: "强化片段目标、起止状态、连续性锁和禁止提前演出的信息。" },
  { id: "action_fight", name: "动作打斗", description: "强化起势、接触、冲击、余波和身体力学。" },
  { id: "vfx_shot", name: "特效镜头", description: "强化特效来源、路径、尺度、环境互动和消散。" },
  { id: "emotion_micro", name: "情绪微反应", description: "参照演员表演，根据潜台词和情绪落点补充克制的微表情。" },
  { id: "physical_realism", name: "物理真实感", description: "强化动作起点、路径、接触点、终点和连续性。" },
  { id: "composition_visual", name: "构图画面", description: "强化前中后景、视觉锚点、光线和画面层次。" },
  { id: "prompt_self_check", name: "提示词优化", description: "强化输出前自检，减少抽象句、乱用资产和格式漂移。" },
];
const STORYBOARD_ENHANCEMENT_IDS = STORYBOARD_ENHANCEMENT_MODES
  .filter((item) => item.id !== "none")
  .map((item) => item.id);
const VIDEO_RESOLUTIONS = ["480p", "720p", "1080p"];
const FAST_VIDEO_RESOLUTIONS = ["480p", "720p"];
const VIDEO_RATIOS = ["9:16", "16:9", "21:9"];
const videoTaskKey = (item = {}) => firstText(
  item?.internalTaskId,
  item?.providerTaskId,
  item?.taskId,
);
const videoUrlKey = (item = {}) => firstText(
  item?.videoUrl,
  item?.resultUrl,
  item?.url,
);
const sameVideoVersion = (left = {}, right = {}) => {
  const leftTask = videoTaskKey(left);
  const rightTask = videoTaskKey(right);
  if (leftTask && rightTask) return leftTask === rightTask;
  const leftUrl = videoUrlKey(left);
  const rightUrl = videoUrlKey(right);
  return Boolean(leftUrl && rightUrl && leftUrl === rightUrl);
};
const isFastVideoModel = (model) => firstText(model).toLowerCase().includes("fast");
const normalizeMiniStoryboardVideoResolution = (resolution, model) => {
  const value = firstText(resolution, "720p").toLowerCase();
  if (isFastVideoModel(model) && value === "1080p") return "720p";
  return VIDEO_RESOLUTIONS.includes(value) ? value : "720p";
};
const STANDARD_MINI_SEGMENT_MAX_SECONDS = 15;
const STANDARD_VIDEO_MIN_DURATION_SECONDS = 4;
const TIMELINE_SEGMENT_PX_PER_SECOND = 15;
const TIMELINE_MIN_SEGMENT_WIDTH = 150;
const TIMELINE_INSERT_WIDTH = 22;
const TIMELINE_GAP = 4;
const ASSET_PAGE_SIZE = 30;
const MINI_WORKSPACE_COLUMN_DEFAULTS = [27, 53, 20];
const MINI_WORKSPACE_COLUMN_MIN_WIDTHS = [240, 430, 260];
const MINI_WORKSPACE_COLUMN_STORAGE_PREFIX = "mini_storyboard_workspace_columns";
const MINI_VIDEO_SETTINGS_STORAGE_PREFIX = "mini_storyboard_video_settings";
const MINI_VIDEO_SETTINGS_VERSION = 2;
const MINI_STORYBOARD_DURATION_PROFILE_WAN3 = "wan3";
const WAN3_MAX_REFERENCE_IMAGES = 10;
const WAN3_PLANNING_MAX_DURATION = 30;
// 优先级徽标：主题语义色 + color-mix 派生底
const ISSUE_PRIORITIES = [
  { id: "P0", name: "P0 必修", color: "var(--color-error)", background: "color-mix(in srgb, var(--color-error) 16%, transparent)" },
  { id: "P1", name: "P1 重要", color: "var(--color-accent-secondary)", background: "color-mix(in srgb, var(--color-accent-secondary) 16%, transparent)" },
  { id: "P2", name: "P2 普通", color: "var(--color-warning)", background: "color-mix(in srgb, var(--color-warning) 16%, transparent)" },
  { id: "P3", name: "P3 建议", color: "var(--color-success)", background: "color-mix(in srgb, var(--color-success) 14%, transparent)" },
];
const ISSUE_TYPES = [
  { id: "story", name: "剧情缺失" },
  { id: "continuity", name: "连续性" },
  { id: "character", name: "人物/脸" },
  { id: "action", name: "动作调度" },
  { id: "vfx", name: "特效" },
  { id: "shot", name: "镜头" },
  { id: "sound", name: "声音" },
  { id: "other", name: "其他" },
];
const ISSUE_STATUSES = [
  { id: "open", name: "待处理" },
  { id: "doing", name: "处理中" },
  { id: "resolved", name: "已解决" },
  { id: "ignored", name: "暂不处理" },
];

const getTimelineSegmentWidth = (durationSeconds) => (
  Math.max(TIMELINE_MIN_SEGMENT_WIDTH, (Number(durationSeconds) || 0) * TIMELINE_SEGMENT_PX_PER_SECOND)
);

const formatSeconds = (value) => {
  const seconds = Math.max(0, Math.round(Number(value) || 0));
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const formatSegmentTimelineRange = (segment) => {
  const start = Number(segment?.startSeconds);
  const end = Number(segment?.endSeconds);
  if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
    return `${formatSeconds(start)}-${formatSeconds(end)}`;
  }
  const duration = Math.max(0, Math.round(Number(segment?.durationSeconds) || 0));
  return duration ? `${duration}s` : "00:00-00:00";
};

const resolveSegmentSeedance25Provider = (segment = {}, fallbackProvider = "") => (
  resolveSeedance25Provider({
    provider: firstText(segment?.videoProvider, segment?.provider),
    model: firstText(segment?.videoModel, segment?.model, segment?.subModel),
    fallback: fallbackProvider,
  })
);

const getSubmissionVideoDuration = (segmentOrDuration, videoProvider = "", fallback = 15) => {
  const segment = segmentOrDuration && typeof segmentOrDuration === "object"
    ? segmentOrDuration
    : null;
  const segmentDuration = Math.max(
    1,
    Math.round(Number(segment?.durationSeconds ?? segmentOrDuration) || fallback),
  );
  if (normalizeVideoProvider(videoProvider) === WANX_VIDEO_PROVIDER) {
    return normalizeWanxVideoDuration(segmentDuration, normalizeWanxVideoDuration(fallback, 30));
  }
  if (videoProvider === WAND_VEGA_PROVIDER) return segmentDuration;
  return isSeedance25Provider(videoProvider)
    ? normalizeSeedance25Duration(videoProvider, segmentDuration)
    : clamp(segmentDuration, STANDARD_VIDEO_MIN_DURATION_SECONDS, STANDARD_MINI_SEGMENT_MAX_SECONDS);
};

const splitReferenceUrls = (value) => {
  if (Array.isArray(value)) return value.map(firstText).filter(Boolean).slice(0, 12);
  return firstText(value)
    .split(/[\n,，;；]+/)
    .map(firstText)
    .filter(Boolean)
    .slice(0, 12);
};

const issuePriorityMeta = (priority) => (
  ISSUE_PRIORITIES.find((item) => item.id === firstText(priority).toUpperCase()) || ISSUE_PRIORITIES[2]
);

const issueTypeName = (type) => (
  ISSUE_TYPES.find((item) => item.id === firstText(type))?.name || "其他"
);

const issueStatusName = (status) => (
  ISSUE_STATUSES.find((item) => item.id === firstText(status))?.name || "待处理"
);

const formatHistoryTime = (value) => {
  const timestamp = Number(value) || 0;
  if (!timestamp) return "未知时间";
  try {
    return new Date(timestamp).toLocaleString("zh-CN", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "未知时间";
  }
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizeMiniWorkspaceColumnPercents = (value) => {
  const source = Array.isArray(value) && value.length === 3
    ? value.map((item, index) => Number(item) || MINI_WORKSPACE_COLUMN_DEFAULTS[index])
    : MINI_WORKSPACE_COLUMN_DEFAULTS;
  const total = source.reduce((sum, item) => sum + item, 0) || 100;
  const normalized = source.map((item) => clamp((item / total) * 100, 8, 82));
  const normalizedTotal = normalized.reduce((sum, item) => sum + item, 0) || 100;
  const next = normalized.map((item) => Math.round((item / normalizedTotal) * 1000) / 10);
  const diff = Math.round((100 - next.reduce((sum, item) => sum + item, 0)) * 10) / 10;
  next[1] = Math.round((next[1] + diff) * 10) / 10;
  return next;
};

const getMiniWorkspaceColumnStorageKey = (projectId) => (
  `${MINI_WORKSPACE_COLUMN_STORAGE_PREFIX}_${projectId || "global"}`
);

const loadMiniWorkspaceColumnPercents = (projectId) => {
  if (typeof window === "undefined") return MINI_WORKSPACE_COLUMN_DEFAULTS;
  try {
    const raw = window.localStorage.getItem(getMiniWorkspaceColumnStorageKey(projectId));
    if (!raw) return MINI_WORKSPACE_COLUMN_DEFAULTS;
    return normalizeMiniWorkspaceColumnPercents(JSON.parse(raw));
  } catch {
    return MINI_WORKSPACE_COLUMN_DEFAULTS;
  }
};

const saveMiniWorkspaceColumnPercents = (projectId, value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      getMiniWorkspaceColumnStorageKey(projectId),
      JSON.stringify(normalizeMiniWorkspaceColumnPercents(value)),
    );
  } catch {
    // Ignore storage failures; dragging should still work for the current session.
  }
};

const getMiniVideoSettingsStorageKey = (projectId, episodeId) => (
  `${MINI_VIDEO_SETTINGS_STORAGE_PREFIX}_${projectId || "global"}_${episodeId || "global"}`
);

const loadMiniVideoSettings = (projectId, episodeId) => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(getMiniVideoSettingsStorageKey(projectId, episodeId));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const resolveMiniVideoSettings = (projectId, episodeId, modelOptions = []) => {
  const stored = loadMiniVideoSettings(projectId, episodeId);
  const allowedModels = new Set((modelOptions || []).map((item) => firstText(item?.id)).filter(Boolean));
  const projectModel = getProjectDefaultVideoModel(projectId);
  const projectProvider = getProjectVideoSubmissionProvider(projectId);
  const storedModel = firstText(stored.videoModel);
  const sd25Project = isSd25Project(projectId);
  const wan3Project = isWan3Project(projectId)
    || normalizeVideoProvider(projectProvider) === WANX_VIDEO_PROVIDER;
  const savedVegaSelection = firstText(stored.videoProvider) === WAND_VEGA_PROVIDER
    && allowedModels.has(storedModel)
    && isWandVegaModel(storedModel);
  const videoModel = savedVegaSelection
    ? storedModel
    : wan3Project
    ? normalizeWanxVideoModel(
          allowedModels.has(storedModel) && isWanxVideoModel(storedModel) ? storedModel : projectModel,
        )
    : sd25Project && allowedModels.has(SEEDANCE25_MODEL)
    ? SEEDANCE25_MODEL
    : storedModel && (!allowedModels.size || allowedModels.has(storedModel))
    ? storedModel
    : allowedModels.has(projectModel) || !allowedModels.size
      ? projectModel
      : firstText(modelOptions?.[0]?.id, projectModel);
  const projectRatio = getProjectDefaultVideoRatio(projectId);
  const storedRatio = firstText(stored.videoRatio, projectRatio);
  const sd25 = videoModel === SEEDANCE25_MODEL;
  const videoProvider = savedVegaSelection
    ? WAND_VEGA_PROVIDER
    : wan3Project
    ? WANX_VIDEO_PROVIDER
    : sd25
    ? normalizeSeedance25SubmissionProvider(resolveSeedance25Provider({
        provider: stored.videoProvider,
        // A versionless saved sd-2.5 selection is legacy New API data.
        model: stored.videoProvider ? "" : storedModel,
        fallback: isSeedance25Provider(projectProvider) ? projectProvider : DEFAULT_SEEDANCE25_PROVIDER,
      })) || DEFAULT_SEEDANCE25_PROVIDER
    : isDomesticOfficialSeedance(stored.videoProvider) && !isOverseasVideoProvider(projectProvider)
      ? VIDEO_PROVIDER_VOLCENGINE_SEEDANCE : projectProvider;
  return {
    version: MINI_VIDEO_SETTINGS_VERSION,
    videoProvider,
    videoModel,
    videoResolution: savedVegaSelection
      ? normalizeWandVegaResolution(stored.videoResolution)
      : wan3Project
      ? normalizeWanxVideoResolution(stored.videoResolution, "1080P")
      : sd25
      ? normalizeSeedance25Resolution(videoProvider, stored.videoResolution)
      : isDomesticOfficialSeedance(videoProvider)
      ? normalizeDomesticSeedanceResolution(videoModel, stored.videoResolution)
      : normalizeMiniStoryboardVideoResolution(stored.videoResolution, videoModel),
    videoRatio: savedVegaSelection
      ? normalizeWandVegaRatio(storedRatio)
      : wan3Project
      ? normalizeWanxVideoRatio(storedRatio, normalizeWanxVideoRatio(projectRatio, "adaptive"))
      : sd25
      ? normalizeSeedance25Ratio(videoProvider, storedRatio, projectRatio)
      : VIDEO_RATIOS.includes(storedRatio) ? storedRatio : projectRatio,
  };
};

const saveMiniVideoSettings = (projectId, episodeId, settings) => {
  if (typeof window === "undefined" || !projectId || !episodeId) return;
  try {
    window.localStorage.setItem(
      getMiniVideoSettingsStorageKey(projectId, episodeId),
      JSON.stringify(settings),
    );
  } catch {
    // Storage is an enhancement; current-session model selection must remain usable.
  }
};

const MiniColumnResizeHandle = ({ onPointerDown, title }) => (
  <div
    className="mini-workbench__resize-handle flex h-full min-h-[260px] items-center justify-center cursor-col-resize group/mini-resize"
    onPointerDown={onPointerDown}
    role="separator"
    aria-orientation="vertical"
    title={title}
    style={{ touchAction: "none" }}
  >
    <div
      className="mini-workbench__resize-grip h-16 w-1 rounded-full"
      style={{ backgroundColor: "var(--color-border-secondary)" }}
    />
  </div>
);

const sanitizeFileNamePart = (value, fallback = "未命名") => firstText(value, fallback)
  .replace(/[\\/:*?"<>|]/g, "_")
  .replace(/\s+/g, "")
  .replace(/_+/g, "_")
  .slice(0, 80) || fallback;

const videoFileExtension = (url) => {
  const clean = firstText(url).split("?")[0].toLowerCase();
  const match = clean.match(/\.(mp4|mov|webm|m4v)$/);
  return match ? match[0] : ".mp4";
};

const buildEpisodeDownloadLabel = (episode, fallbackEpisodeId) => {
  const order = episode?.sortOrder || episode?.episodeNumber;
  const rawTitle = firstText(episode?.title, episode?.name, fallbackEpisodeId ? `第${fallbackEpisodeId}集` : "当前集");
  if (!order) return rawTitle;
  const cleanTitle = rawTitle
    .replace(new RegExp(`^第\\s*${order}\\s*集\\s*[：:·\\-—_\\s]*`), "")
    .replace(/^第\s*\d+\s*集\s*[：:·\-—_\s]*/, "")
    .trim();
  return cleanTitle ? `第${order}集_${cleanTitle}` : `第${order}集`;
};

const downloadHeaders = () => {
  const headers = {
    "X-User-ID": encodeURIComponent(localStorage.getItem("app_user_id") || "default_user"),
    "X-Ai-Provider": localStorage.getItem("ai_provider") || "omini",
  };
  const token = localStorage.getItem("app_token") || "";
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const normalizeStoryboardEnhancementModes = (value) => {
  const normalizeOne = (item) => firstText(item)
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, "_");
  let raw = value;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        raw = JSON.parse(trimmed);
      } catch {
        raw = trimmed;
      }
    }
  }
  const parts = Array.isArray(raw)
    ? raw
    : firstText(raw).split(/[,\n，、;；|]+/);
  const selected = [];
  parts.forEach((part) => {
    const mode = normalizeOne(part);
    if (!mode || mode === "none") return;
    if (mode === "director_deep" || mode === "deep_director" || mode === "director") {
      selected.push("director_depth");
    } else if (mode === "mxshell" || mode === "mx_shell_style" || mode === "mx_shell_storyboard") {
      selected.push("mx_shell");
    } else if (STORYBOARD_ENHANCEMENT_IDS.includes(mode)) {
      selected.push(mode);
    }
  });
  return STORYBOARD_ENHANCEMENT_IDS.filter((id) => selected.includes(id));
};

const normalizeEpisodeScript = (episode) => firstText(
  episode?.scriptContent,
  episode?.script_content,
  episode?.content,
  episode?.script,
  episode?.screenplay,
  episode?.text,
  episode?.episodeContent,
  episode?.originalText,
  episode?.adaptedScript,
  episode?.finalScript,
);

const getAssetName = (asset) => firstText(
  asset?.displayName,
  asset?.name,
  asset?.title,
  asset?.characterName,
  asset?.sceneName,
  asset?.propName,
  `资产 ${asset?.id || ""}`,
);

const stripVariantSuffix = (value, variantTitle) => {
  const name = firstText(value);
  const variant = firstText(variantTitle);
  if (!name || !variant || name === variant) return name;
  const suffixes = [
    `-${variant}`,
    `—${variant}`,
    `_${variant}`,
    `/${variant}`,
    `|${variant}`,
    `｜${variant}`,
    `·${variant}`,
    ` ${variant}`,
    `（${variant}）`,
    `(${variant})`,
  ];
  const suffix = suffixes.find((item) => name.endsWith(item));
  return suffix ? name.slice(0, -suffix.length).trim() : name;
};

const getAssetPromptName = (asset) => {
  const parentName = firstText(asset?.parentName);
  if (asset?.isVariant && parentName) return parentName;
  if (asset?.isVariant) return stripVariantSuffix(getAssetName(asset), asset?.variantTitle);
  return getAssetName(asset);
};

const getAssetSubtitle = (asset) => firstText(asset?.role, asset?.type, asset?.description, asset?.prompt, asset?.appearancePrompt);

const toAssetAliasList = (value) => {
  if (Array.isArray(value)) return value.map(firstText).filter(Boolean);
  return firstText(value).split(/[,\n、，;；/|｜]/).map(firstText).filter(Boolean);
};

const assetMatches = (asset, query) => {
  const q = firstText(query).toLowerCase();
  if (!q) return true;
  return [
    getAssetName(asset),
    getAssetSubtitle(asset),
    asset?.parentName,
    asset?.variantTitle,
    ...toAssetAliasList(asset?.aliases),
    asset?.tags,
    asset?.keywords,
  ]
    .join(" ")
    .toLowerCase()
    .includes(q);
};

const formatSeedancePrompt = (prompt) => {
  const value = firstText(prompt).replace(/\r\n?/g, "\n").trim();
  if (!value) return "";
  return value
    .replace(/\s*([-–—]?\s*分镜\s*\d+\s*[:：])/g, "\n\n$1")
    .replace(/\s+(-\s*(画面|机位\/运动|声音|文本|解说|转场\/节拍)\s*[:：])/g, "\n$1")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const findLightingPresetByPrompt = (prompt) => (
  LIGHTING_PRESETS.find((preset) => firstText(preset.prompt) === firstText(prompt)) || null
);
const LIGHTING_PICKER_OPTIONS = [
  { id: "none", name: "无打光", prompt: "", description: "生成时不附加额外打光词" },
  ...LIGHTING_PRESETS,
];

const inferLightingPrompt = (segment) => segment?.lightingPrompt != null
  ? firstText(segment.lightingPrompt)
  : isOneClickFlowSegment(segment) ? "" : extractLegacyLightingPrompt(segment?.seedancePrompt);

const inferGlobalLightingPresetId = (segments = [], wan3Selected = false) => {
  const rows = segments.filter((segment) => firstText(segment?.seedancePrompt) && (!wan3Selected || isOneClickFlowSegment(segment)));
  if (!rows.length) return "";
  const prompts = rows.map(inferLightingPrompt).filter(Boolean);
  if (!prompts.length) return "none";
  if (prompts.length !== rows.length) return "";
  const unique = [...new Set(prompts)];
  if (unique.length !== 1) return "";
  return findLightingPresetByPrompt(unique[0])?.id || "";
};

const referenceEntryName = (entry) => {
  if (typeof entry === "string") return firstText(entry);
  if (!entry || typeof entry !== "object") return "";
  return firstText(
    entry.displayName,
    entry.name,
    entry.label,
    entry.title,
    entry.parentName && entry.variantTitle ? `${entry.parentName}-${entry.variantTitle}` : "",
  );
};

const toReferenceNameList = (value) => {
  if (Array.isArray(value)) return value.map(referenceEntryName).filter(Boolean);
  if (typeof value === "string") return value.split(/[,\n、，;；]/).map(firstText).filter(Boolean);
  return [];
};

const referenceEntryIdentityCandidates = (entry = {}) => {
  if (!entry || typeof entry !== "object") return [];
  return [
    entry.url,
    entry.imageUrl,
    entry.previewUrl,
    entry.sourceAssetId != null ? String(entry.sourceAssetId) : "",
    entry.assetId != null ? String(entry.assetId) : "",
    entry.id != null ? String(entry.id) : "",
    entry.guaVideoAssetId ? `guavideo:${entry.guaVideoAssetId}` : "",
    entry.engGuaVideoAssetId ? `engguavideo:${entry.engGuaVideoAssetId}` : "",
    entry.guaOminiAssetId ? `omini_seedance:${entry.guaOminiAssetId}` : "",
    entry.dreaminaProxyAssetId ? `dreamina_proxy:${entry.dreaminaProxyAssetId}` : "",
  ].map(firstText).filter(Boolean);
};

const assetIdentityCandidates = (asset = {}) => [
  getAssetImageUrl(asset),
  asset?.assetId != null ? String(asset.assetId) : "",
  asset?.id != null ? String(asset.id) : "",
  asset?.guaVideoAssetId ? `guavideo:${asset.guaVideoAssetId}` : "",
  asset?.engGuaVideoAssetId ? `engguavideo:${asset.engGuaVideoAssetId}` : "",
  asset?.guaOminiAssetId ? `omini_seedance:${asset.guaOminiAssetId}` : "",
  asset?.dreaminaProxyAssetId ? `dreamina_proxy:${asset.dreaminaProxyAssetId}` : "",
].map(firstText).filter(Boolean);

const referenceGroupIdentity = (item = {}) => {
  const idPrefix = firstText(item?.id).split(":")[0];
  const value = firstText(item?.groupKey, item?.sourceType, idPrefix, item?.type).toLowerCase();
  if (value === "characters" || value === "character" || value.includes("角色") || value.includes("人物")) return "characters";
  if (value === "scenes" || value === "scene" || value.includes("场景") || value.includes("地点")) return "scenes";
  if (value === "props" || value === "prop" || value.includes("道具")) return "props";
  return "";
};

const referenceGroupsConflict = (left = {}, right = {}) => {
  const leftGroup = referenceGroupIdentity(left);
  const rightGroup = referenceGroupIdentity(right);
  return Boolean(leftGroup && rightGroup && leftGroup !== rightGroup);
};

const isVariantAssetCandidate = (asset = {}) => (
  Boolean(asset?.isVariant) || Number(asset?.variantIndex) >= 0
);

const isVariantSpecificReferenceLabel = (asset = {}, label = "") => {
  if (!isVariantAssetCandidate(asset) || !firstText(label)) return false;
  if (asset?.parentName && sameReferenceName(label, asset.parentName)) return false;
  const variantCandidates = [
    asset?.displayName,
    asset?.name,
    asset?.variantTitle,
    asset?.parentName && asset?.variantTitle ? `${asset.parentName}-${asset.variantTitle}` : "",
  ].map(firstText).filter(isSafeAutomaticReferenceCandidate);
  return variantCandidates.some((candidate) => referenceNamesOverlap(candidate, label));
};

const scoreExplicitReferenceIdentity = (bindingEntry, asset) => {
  if (!bindingEntry || typeof bindingEntry !== "object" || !asset) return 0;
  if (referenceGroupsConflict(bindingEntry, asset)) return 0;
  const bindingUrl = normalizeImageUrl(firstText(bindingEntry.url, bindingEntry.imageUrl, bindingEntry.previewUrl));
  const assetUrl = normalizeImageUrl(firstText(asset?.url, asset?.imageUrl, asset?.previewUrl, getAssetImageUrl(asset)));
  if (bindingUrl && assetUrl && bindingUrl === assetUrl) return 30000;

  const bindingAssetId = firstText(
    bindingEntry.assetId != null ? String(bindingEntry.assetId) : "",
    bindingEntry.id != null ? String(bindingEntry.id) : "",
  );
  const assetId = firstText(asset?.id != null ? String(asset.id) : "");
  const bindingVariantIndex = Number(bindingEntry.variantIndex);
  const assetVariantIndex = Number(asset?.variantIndex);
  const bindingIsVariant = Boolean(bindingEntry?.isVariant) || (Number.isFinite(bindingVariantIndex) && bindingVariantIndex >= 0);
  const assetIsVariant = Boolean(asset?.isVariant) || (Number.isFinite(assetVariantIndex) && assetVariantIndex >= 0);
  if (bindingAssetId && assetId && bindingAssetId === assetId) {
    if (!bindingIsVariant && !assetIsVariant) return 29000;
    if (
      bindingIsVariant
      && assetIsVariant
      && Number.isFinite(bindingVariantIndex)
      && Number.isFinite(assetVariantIndex)
      && bindingVariantIndex === assetVariantIndex
    ) {
      return 29000;
    }
  }

  const bindingSourceAssetId = firstText(bindingEntry.sourceAssetId != null ? String(bindingEntry.sourceAssetId) : "");
  const assetSourceAssetId = firstText(
    asset?.sourceAssetId != null ? String(asset.sourceAssetId) : "",
    asset?.assetId != null ? String(asset.assetId) : "",
  );
  if (
    bindingSourceAssetId
    && assetSourceAssetId
    && bindingSourceAssetId === assetSourceAssetId
    && referenceGroupIdentity(bindingEntry)
    && referenceGroupIdentity(bindingEntry) === referenceGroupIdentity(asset)
    && Number.isFinite(bindingVariantIndex)
    && Number.isFinite(assetVariantIndex)
    && bindingVariantIndex === assetVariantIndex
  ) {
    return 28000;
  }

  const matchingProviderAssetId = [
    "guaVideoAssetId",
    "engGuaVideoAssetId",
    "guaOminiAssetId",
    "dreaminaProxyAssetId",
  ].some((field) => (
    firstText(bindingEntry?.[field])
    && firstText(bindingEntry?.[field]) === firstText(asset?.[field])
  ));
  if (matchingProviderAssetId) return 27000;

  return 0;
};

const assetNameCandidates = (asset) => {
  const name = getAssetName(asset);
  // Chinese names commonly use a suffix such as "苏婉-冬装". Splitting an
  // English name here creates dangerous fragments (for example "of").
  const cjkNameParts = hasCjkReferenceText(name)
    ? name.split(/[-—_/|｜（(]/).filter(hasCjkReferenceText)
    : [];
  return [...new Set([
    name,
    ...cjkNameParts,
    asset?.displayName,
    asset?.title,
    asset?.parentName,
    asset?.variantTitle,
    asset?.parentName && asset?.variantTitle ? `${asset.parentName}-${asset.variantTitle}` : "",
    asset?.characterName,
    asset?.sceneName,
    asset?.propName,
    ...toAssetAliasList(asset?.aliases),
  ].map(firstText).filter(isSafeAutomaticReferenceCandidate))];
};

const isSpecificReferenceName = (value) => {
  if (!isSafeAutomaticReferenceCandidate(value)) return false;
  const generic = [
    "当前场景", "场景", "地点", "人物", "角色", "道具", "无",
    "正面视角", "反面视角", "背面视角", "侧面视角", "正面", "反面", "背面", "侧面",
    "全身", "半身", "近景", "远景", "中景", "特写",
  ];
  return !generic.some((item) => sameReferenceName(item, value));
};

const scoreAssetReferenceMatch = (asset, names = [], context = "", exactNamesOnly = false) => {
  const candidates = assetNameCandidates(asset);
  const referenceNames = names.filter(isSpecificReferenceName);
  let score = 0;
  referenceNames.forEach((name) => {
    candidates.forEach((candidate) => {
      if (sameReferenceName(candidate, name)) {
        score = Math.max(score, 2200 + normalizeReferenceText(name).length);
      } else if (!exactNamesOnly && referenceNamesOverlap(candidate, name)) {
        score = Math.max(score, 2000 + Math.max(candidate.length, name.length));
      }
    });
  });
  candidates.forEach((candidate) => {
    if (referenceTextContainsCandidate(context, candidate)) {
      score = Math.max(score, 1000 + normalizeReferenceText(candidate).length);
    }
  });
  return score;
};

const VISIBLE_REFERENCE_CONTEXT_EXCLUDED_LINE_RE = /^\s*[-–—]?\s*(文本|对白|台词|字幕|旁白|解说|画外音|VO|OS|声音|参考图绑定|本段出[镜境]参考)\s*[：:]/i;
const VISIBLE_REFERENCE_CONTEXT_INCLUDED_LINE_RE = /^\s*[-–—]?\s*(画面|机位\/运动|地点|场景的天气|整体光线基调|打光提示词)\s*[：:]/i;

const SPEAKER_STOP_NAMES = new Set([
  "无", "旁白", "解说", "画外音", "系统音", "声音", "音效", "环境音",
  "vo", "os", "v.o", "o.s", "narration", "narrator", "voiceover", "voice over", "sfx",
].map(normalizeReferenceText));

const cleanVisibleCharacterName = (value) => {
  const clean = firstText(value)
    .replace(/@图片\s*\d+/g, "")
    .replace(/^[=＝：:\s]+/, "")
    .replace(/^(角色|人物|Character|Person)\s*[：:]/i, "")
    .split(/[，,；;\n。]/)[0]
    .replace(/\s+(穿|身穿|拿着|握着|站在|坐在|走向|转身|抬头|低头|看向|冲向|面对|进入|离开|from|wearing|holding|standing|sitting|walking|turning|looking|entering|leaving).*$/i, "")
    .trim();
  return isSpecificReferenceName(clean) ? clean : "";
};

const addUniqueVisibleName = (names, value) => {
  const clean = cleanVisibleCharacterName(value);
  const normalized = normalizeReferenceText(clean);
  if (!normalized || names.some((item) => normalizeReferenceText(item) === normalized)) return;
  names.push(clean);
};

const extractSpeakerName = (value) => {
  const text = firstText(value);
  const cnIndex = text.indexOf("：");
  const enIndex = text.indexOf(":");
  const colonIndex = [cnIndex, enIndex].filter((index) => index >= 0).sort((a, b) => a - b)[0];
  if (!Number.isFinite(colonIndex) || colonIndex <= 0 || colonIndex > 24) return "";
  const speakerLabel = text.slice(0, colonIndex).trim();
  const normalizedLabel = normalizeReferenceText(speakerLabel);
  if (/[（(][^)）]*(?:vo|os|v\.o|o\.s|旁白|画外音)[^)）]*[）)]/i.test(speakerLabel)
    || /(?:vo|os|v\.o|o\.s)$/.test(normalizedLabel)) {
    return "";
  }
  const raw = speakerLabel.replace(/[（(].*?[）)]/g, "").trim();
  const normalized = normalizeReferenceText(raw);
  if (!normalized || SPEAKER_STOP_NAMES.has(normalized)) return "";
  return cleanVisibleCharacterName(raw);
};

const collectVisibleCharacterNames = (segment = {}) => {
  const names = [];
  const spatial = segment?.spatial || {};
  const positions = Array.isArray(spatial.characterPositions) ? spatial.characterPositions : [];
  positions.forEach((item) => {
    addUniqueVisibleName(names, item?.character || item?.name);
  });

  (Array.isArray(segment?.shots) ? segment.shots : []).forEach((shot) => {
    addUniqueVisibleName(names, extractSpeakerName(shot?.text));

    const visual = firstText(shot?.visual);
    const typedRe = /(角色|人物)\s*[：:]\s*([^，,；;\n]+)/g;
    let match;
    while ((match = typedRe.exec(visual)) !== null) {
      addUniqueVisibleName(names, match[2]);
    }
  });

  return names;
};

const buildSegmentVisibleContext = (segment = {}) => {
  const parts = [];
  const add = (value) => {
    const text = firstText(value);
    if (text) parts.push(text);
  };

  add(segment?.location);
  add(segment?.weather);

  (Array.isArray(segment?.shots) ? segment.shots : []).forEach((shot) => {
    add(shot?.visual);
    add(shot?.camera);
    const layers = shot?.wan3Layers || shot?.wan3 || shot?.layers || {};
    add(layers?.roleReferences || layers?.roles || layers?.references);
    add(layers?.spatialBlocking || layers?.blocking || layers?.space);
    add(layers?.performance || layers?.actionPerformance || layers?.action);
  });

  const spatial = segment?.spatial || {};
  ["sceneKey", "startBlocking", "endBlocking", "movement", "endState", "cameraAxis"].forEach((key) => add(spatial?.[key]));
  if (spatial?.sceneAnchors && typeof spatial.sceneAnchors === "object") {
    Object.values(spatial.sceneAnchors).forEach(add);
  }
  if (Array.isArray(spatial?.characterPositions)) {
    spatial.characterPositions.forEach((item) => {
      add([
        item?.character || item?.name,
        item?.anchor,
        item?.posture,
        item?.facing,
        item?.screenPosition,
        item?.relationshipToScene,
      ].map(firstText).filter(Boolean).join(" "));
    });
  }

  formatSeedancePrompt(segment?.seedancePrompt).split("\n").forEach((line) => {
    const clean = firstText(line);
    if (!clean || VISIBLE_REFERENCE_CONTEXT_EXCLUDED_LINE_RE.test(clean)) return;
    if (VISIBLE_REFERENCE_CONTEXT_INCLUDED_LINE_RE.test(clean)) add(clean);
  });

  return parts.join("\n");
};

const isReferenceNameVisible = (name, visibleNames = [], visibleContext = "") => {
  if (!isSpecificReferenceName(name)) return false;
  // Structured positions/speakers are authoritative. Falling back to fuzzy context here
  // reintroduces relation-only people and prefix collisions such as “苏婉” / “苏婉母亲”.
  if (visibleNames.length) return visibleNames.some((visibleName) => sameReferenceName(visibleName, name));
  return referenceNameAppearsInVisibleContext(name, visibleContext);
};

const referenceNameAppearsInVisibleContext = (
  name,
  visibleContext = "",
  { allowPossessive = false } = {},
) => {
  const label = firstText(name);
  const context = firstText(visibleContext);
  if (!referenceTextContainsCandidate(context, label)) return false;
  const rawMatches = findReferenceMentions(context, label);
  // English references must have a real token-boundary match. The CJK branch
  // keeps compact matching so punctuation/spacing in Chinese names still works.
  if (!rawMatches.length) return hasCjkReferenceText(label);
  return rawMatches.some((match) => {
    if (allowPossessive) return true;
    const after = context.slice(match.index + match.length).trimStart();
    const normalizedAfter = after.toLowerCase();
    return !(
      after.startsWith("的")
      || after.startsWith("之")
      || normalizedAfter.startsWith("'s")
      || normalizedAfter.startsWith("’s")
    );
  });
};

const REFERENCE_GROUPS = [
  { key: "scenes", type: "场景", priority: 1 },
  { key: "characters", type: "角色", priority: 2 },
  { key: "props", type: "道具", priority: 3 },
];

const groupKeyForReferenceType = (type) => {
  const value = firstText(type);
  if (!value) return "";
  if (value.includes("场景") || value.includes("地点")) return "scenes";
  if (value.includes("道具")) return "props";
  return "characters";
};

const referenceTypeLabelForGroup = (groupKey) => (
  REFERENCE_GROUPS.find((item) => item.key === groupKey)?.type || "角色"
);

const cleanEditableReferenceName = (value) => firstText(value)
  .replace(/@图片\s*\d+/g, "")
  .replace(/@视频\s*\d+/g, "")
  .replace(/^[=＝：:\s]+/, "")
  .replace(/^(角色|人物|场景|地点|道具)\s*[：:]/, "")
  .replace(/[，,；;。]+$/g, "")
  .trim();

const referenceMediaType = getSeedance25ReferenceMediaType;

const referenceMarkerPrefix = (item = {}) => ({ image: "图片", video: "视频", audio: "音频" }[referenceMediaType(item)]);

const referenceMarker = (item = {}, index = 0) => `@${referenceMarkerPrefix(item)}${index + 1}`;

const getReferencePromptName = (item, fallback = "参考图") => (
  item?.isVariant && firstText(item?.parentName)
    ? firstText(item.parentName)
    : stripVariantSuffix(
      firstText(item?.displayName, firstText(item?.name).replace(/^(角色|场景|道具)：/, ""), fallback),
      item?.variantTitle,
    )
);

const buildAutoExtendPrompt = (sourceSegment, reference) => {
  const segmentLabel = `分镜组 ${String(sourceSegment?.segmentIndex || "").padStart(2, "0")}`;
  const sourceTitle = firstText(sourceSegment?.title, segmentLabel);
  const sourceText = firstText(sourceSegment?.sourceText, sourceSegment?.narrationText);
  const sourcePromptLines = formatSeedancePrompt(sourceSegment?.seedancePrompt)
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line && !/^参考图绑定\s*[：:]/.test(line))
    .slice(-10)
    .join("\n");
  return formatSeedancePrompt([
    "延长@视频1",
    "",
    `参考图绑定：@视频1=${getReferencePromptName(reference, "参考视频")}`,
    "",
    "自动延长提示词：",
    "从@视频1的最后一帧自然延续，不重演前面已经发生的动作。",
    "继承上一段的站位、镜头轴线、光线方向、环境氛围、人物服装和表演状态。",
    sourceTitle ? `上一段标题：${sourceTitle}` : "",
    sourceText ? `上一段剧情：${sourceText}` : "",
    sourcePromptLines ? `上一段提示词要点：\n${sourcePromptLines}` : "",
    "新段落只写延续后的下一步动作、反应或情绪落点；保持动作连续，不突然跳场景，不新增无关人物，不出现字幕、水印、屏幕文字或bgm。",
  ].filter(Boolean).join("\n"));
};

const hasExplicitReferenceBindingLine = (prompt) => (
  /^\s*参考图绑定\s*[：:]/m.test(firstText(prompt))
);

const extractExplicitReferenceBindingLabels = (prompt) => {
  const lines = formatSeedancePrompt(prompt)
    .split("\n")
    .filter((line) => /^\s*参考图绑定\s*[：:]/.test(line));
  if (!lines.length) return null;

  const labels = [];
  const markerRe = /@(图片|视频|音频)\s*(\d+)(?:\s*[=＝]\s*([^，,；;\n]+)|\s+([^，,；;\n]+))?/g;
  lines.forEach((line) => {
    const bindingText = line.replace(/^\s*参考图绑定\s*[：:]\s*/, "").trim();
    if (!bindingText || normalizeReferenceText(bindingText) === normalizeReferenceText("无")) return;

    let hasMarker = false;
    let match;
    markerRe.lastIndex = 0;
    while ((match = markerRe.exec(line)) !== null) {
      hasMarker = true;
      const index = Number(match[2]) - 1;
      const label = cleanEditableReferenceName(match[3] || match[4]);
      if (Number.isFinite(index) && index >= 0 && isSpecificReferenceName(label)) {
        labels[index] = label;
      }
    }

    if (!hasMarker) {
      bindingText
        .split(/[，,；;\n]/)
        .map(cleanEditableReferenceName)
        .filter(isSpecificReferenceName)
        .forEach((label) => labels.push(label));
    }
  });

  return labels.filter(Boolean);
};

const addAssetRefName = (refs, groupKey, name) => {
  if (!refs[groupKey]) return;
  const value = cleanEditableReferenceName(name);
  if (!isSpecificReferenceName(value)) return;
  const list = refs[groupKey] || [];
  const normalized = normalizeReferenceText(value);
  if (!list.some((item) => normalizeReferenceText(item) === normalized)) {
    list.push(value);
  }
  refs[groupKey] = list;
};

const buildReferenceItemFromAsset = (asset, groupKey) => {
  const typeLabel = referenceTypeLabelForGroup(groupKey);
  const url = normalizeImageUrl(getAssetImageUrl(asset));
  const guaMeta = getAssetGuaVideoMeta(asset, url);
  const sourceAssetId = asset?.sourceAssetId ?? asset?.id;
  const promptName = getAssetPromptName(asset);
  return {
    id: asset?.id || `${groupKey}:${sourceAssetId || getAssetName(asset)}`,
    groupKey,
    sourceType: groupKey,
    role: "reference_image",
    type: typeLabel,
    name: `${typeLabel}：${promptName}`,
    displayName: promptName,
    assetDisplayName: getAssetName(asset),
    aliases: assetNameCandidates(asset).filter(isSpecificReferenceName),
    url,
    originalUrl: asset?.originalUrl || asset?.originalImageUrl,
    sourceUrl: asset?.sourceUrl || asset?.sourceImageUrl,
    imageUrl: asset?.imageUrl,
    sourceAssetId,
    variantIndex: Number.isFinite(Number(asset?.variantIndex)) ? Number(asset.variantIndex) : -1,
    variantTitle: firstText(asset?.variantTitle),
    parentName: firstText(asset?.parentName),
    isVariant: Boolean(asset?.isVariant),
    guaVideoAssetId: guaMeta.guaVideoAssetId,
    guaVideoAssetStatus: guaMeta.guaVideoAssetStatus,
    engGuaVideoAssetId: guaMeta.engGuaVideoAssetId,
    engGuaVideoAssetStatus: guaMeta.engGuaVideoAssetStatus,
    guaOminiAssetId: guaMeta.guaOminiAssetId,
    guaOminiAssetStatus: guaMeta.guaOminiAssetStatus,
    dreaminaProxyAssetId: guaMeta.dreaminaProxyAssetId,
    dreaminaProxyAssetStatus: guaMeta.dreaminaProxyAssetStatus,
    assetProvider: guaMeta.assetProvider,
    ...(groupKey === "characters" ? {
      voiceSampleUrl: firstText(asset?.voiceSampleUrl),
      voiceSampleName: firstText(asset?.voiceSampleName),
      voiceBoundAt: asset?.voiceBoundAt ?? null,
    } : {}),
  };
};

const buildReferenceItemFromBinding = (bindingEntry = {}, fallbackLabel = "参考图") => ({
  id: firstText(bindingEntry.assetId, bindingEntry.id, bindingEntry.url),
  groupKey: firstText(bindingEntry.groupKey) || groupKeyForReferenceType(bindingEntry.type),
  sourceType: firstText(bindingEntry.groupKey) || groupKeyForReferenceType(bindingEntry.type),
  role: firstText(bindingEntry.role, `reference_${referenceMediaType(bindingEntry)}`),
  type: firstText(bindingEntry.type, bindingEntry.mediaType === "video" ? "视频" : "参考"),
  mediaType: referenceMediaType(bindingEntry),
  name: firstText(bindingEntry.name, fallbackLabel, bindingEntry.mediaType === "video" ? "参考视频" : "参考图"),
  displayName: firstText(bindingEntry.displayName, bindingEntry.name, fallbackLabel),
  assetDisplayName: firstText(bindingEntry.displayName, bindingEntry.name, fallbackLabel),
  url: normalizeImageUrl(getSeedance25ReferenceUrl(bindingEntry)),
  sourceAssetId: bindingEntry.sourceAssetId,
  variantIndex: Number.isFinite(Number(bindingEntry.variantIndex)) ? Number(bindingEntry.variantIndex) : -1,
  variantTitle: firstText(bindingEntry.variantTitle),
  parentName: firstText(bindingEntry.parentName),
  isVariant: Boolean(bindingEntry.isVariant) || Number(bindingEntry.variantIndex) >= 0,
  guaVideoAssetId: firstText(bindingEntry.guaVideoAssetId),
  guaVideoAssetStatus: firstText(bindingEntry.guaVideoAssetStatus),
  engGuaVideoAssetId: firstText(bindingEntry.engGuaVideoAssetId),
  engGuaVideoAssetStatus: firstText(bindingEntry.engGuaVideoAssetStatus),
  guaOminiAssetId: firstText(bindingEntry.guaOminiAssetId),
  guaOminiAssetStatus: firstText(bindingEntry.guaOminiAssetStatus),
  dreaminaProxyAssetId: firstText(bindingEntry.dreaminaProxyAssetId),
  dreaminaProxyAssetStatus: firstText(bindingEntry.dreaminaProxyAssetStatus),
  assetProvider: bindingEntry.assetProvider,
  voiceSampleUrl: firstText(bindingEntry.voiceSampleUrl),
  voiceSampleName: firstText(bindingEntry.voiceSampleName),
  voiceBoundAt: bindingEntry.voiceBoundAt ?? null,
  referencePurpose: firstText(bindingEntry.referencePurpose),
  purpose: firstText(bindingEntry.purpose),
});

const buildReferenceItemFromUpload = (url, file, index = 0) => {
  const filename = firstText(file?.name, `上传参考图${index + 1}`);
  const displayName = filename.replace(/\.[^.]+$/, "") || `上传参考图${index + 1}`;
  return {
    id: `uploaded-reference-${Date.now()}-${index}`,
    role: `reference_${getSeedance25ReferenceMediaType({ type: file?.type, url })}`,
    type: "上传",
    mediaType: getSeedance25ReferenceMediaType({ type: file?.type, url }),
    name: displayName,
    displayName,
    assetDisplayName: displayName,
    aliases: [displayName].filter(isSpecificReferenceName),
    url: normalizeImageUrl(url),
    sourceAssetId: "",
    variantIndex: -1,
    assetProvider: "",
  };
};

const getReferenceAssetStatusMeta = (item = {}, preferredProvider = "guavideo") => {
  return getEffectiveAssetMeta(item, preferredProvider);
};

const getReferenceAssetStatusStyle = (status) => {
  const value = firstText(status);
  if (value === "Active" || value === "URL_MODE") {
    return { backgroundColor: "color-mix(in srgb, var(--color-success) 18%, transparent)", color: "var(--color-success)", borderColor: "color-mix(in srgb, var(--color-success) 45%, transparent)" };
  }
  if (value === "Processing") {
    return { backgroundColor: "color-mix(in srgb, var(--color-warning) 16%, transparent)", color: "var(--color-warning)", borderColor: "color-mix(in srgb, var(--color-warning) 40%, transparent)" };
  }
  if (value === "Failed") {
    return { backgroundColor: "color-mix(in srgb, var(--color-error) 16%, transparent)", color: "var(--color-error)", borderColor: "color-mix(in srgb, var(--color-error) 40%, transparent)" };
  }
  return { backgroundColor: "color-mix(in srgb, var(--color-text-muted) 14%, transparent)", color: "var(--color-text-muted)", borderColor: "color-mix(in srgb, var(--color-text-muted) 28%, transparent)" };
};

const isReferenceAssetActive = (item = {}, provider = "guavideo") => {
  if (isSeedance25Provider(provider)) return validateSeedance25References(provider, [item]).accepted.length > 0;
  if (provider === WAND_VEGA_PROVIDER) return isPublicWandVegaImageUrl(item?.url);
  if (provider === WANX_VIDEO_PROVIDER) return Boolean(firstText(item?.url));
  const status = firstText(getReferenceAssetStatusMeta(item, provider).status).toLowerCase();
  return status === "active" || status === "url_mode";
};

const getReferenceBindingRowStyle = (item, provider, selected = false, currentLibraryActive) => {
  const active = currentLibraryActive ?? isReferenceAssetActive(item, provider);
  return {
    backgroundColor: active ? "color-mix(in srgb, var(--color-success) 9%, transparent)" : "color-mix(in srgb, var(--color-text-muted) 7%, transparent)",
    borderColor: active ? "color-mix(in srgb, var(--color-success) 52%, transparent)" : "color-mix(in srgb, var(--color-text-muted) 24%, transparent)",
    boxShadow: selected ? "inset 3px 0 0 var(--color-accent-primary)" : "none",
  };
};

const ReferenceAssetStatusBadge = ({ item, provider }) => {
  if (referenceMediaType(item) === "video" || isSeedance25Provider(provider)
    || provider === WAND_VEGA_PROVIDER || provider === WANX_VIDEO_PROVIDER) return null;
  const meta = getReferenceAssetStatusMeta(item, provider);
  const label = assetStatusLabel(meta.status, "未入库");
  return (
    <span
      className="absolute bottom-0.5 left-0.5 max-w-[calc(100%-4px)] truncate rounded px-1 py-0.5 text-[10px] leading-none backdrop-blur"
      style={{
        ...getReferenceAssetStatusStyle(meta.status),
        border: `1px solid ${getReferenceAssetStatusStyle(meta.status).borderColor}`,
      }}
      title={label}
    >
      {label}
    </span>
  );
};

const ReferenceAssetIngestButton = ({ item, provider, loading = false, onIngest }) => {
  if (!supportsVideoAssetIngest(provider) || !onIngest || referenceMediaType(item) === "video" || !firstText(item?.url)) return null;
  const meta = getReferenceAssetStatusMeta(item, provider);
  const status = firstText(meta.status).toLowerCase();
  if (status === "active" || status === "url_mode") return null;
  const processing = status === "processing";
  const failed = status === "failed";
  const providerLabel = assetLibraryLabel(provider);
  const label = loading ? "提交中" : processing ? "刷新" : failed ? "重试" : "入库";
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onIngest(item);
      }}
      disabled={loading}
      className="inline-flex h-7 shrink-0 items-center gap-1 rounded-full px-2 text-[11px] font-medium transition-colors disabled:cursor-wait disabled:opacity-60"
      style={processing || failed ? ghostButtonStyle : primaryButtonStyle}
      title={`${label}到${providerLabel}`}
    >
      {loading ? <Loader2 size={11} className="animate-spin" /> : processing ? <RefreshCw size={11} /> : <Upload size={11} />}
      {label}
    </button>
  );
};

const assetRefsFromReferenceItems = (
  references = [],
  bindingMode = "manual",
  { omitProviderAssetMetadata = false } = {},
) => {
  const refs = { characters: [], scenes: [], props: [], _bindings: [], _bindingMode: bindingMode };
  references.forEach((item) => {
    const groupKey = groupKeyForReferenceType(item?.type);
    const promptName = getReferencePromptName(item);
    const displayName = firstText(
      item?.assetDisplayName,
      item?.displayName,
      item?.isVariant && item?.parentName && item?.variantTitle
        ? `${item.parentName}-${item.variantTitle}`
        : "",
      promptName,
    );
    const entry = {
      type: item?.type,
      name: promptName,
      displayName,
      url: item?.url,
      mediaType: referenceMediaType(item),
      role: firstText(item?.role, `reference_${referenceMediaType(item)}`),
      sourceAssetId: item?.sourceAssetId,
      assetId: item?.id,
      variantIndex: item?.variantIndex,
      variantTitle: item?.variantTitle,
      parentName: item?.parentName,
      isVariant: item?.isVariant,
      guaVideoAssetId: omitProviderAssetMetadata ? undefined : item?.guaVideoAssetId,
      guaVideoAssetStatus: omitProviderAssetMetadata ? undefined : item?.guaVideoAssetStatus,
      engGuaVideoAssetId: omitProviderAssetMetadata ? undefined : item?.engGuaVideoAssetId,
      engGuaVideoAssetStatus: omitProviderAssetMetadata ? undefined : item?.engGuaVideoAssetStatus,
      guaOminiAssetId: omitProviderAssetMetadata ? undefined : item?.guaOminiAssetId,
      guaOminiAssetStatus: omitProviderAssetMetadata ? undefined : item?.guaOminiAssetStatus,
      dreaminaProxyAssetId: omitProviderAssetMetadata ? undefined : item?.dreaminaProxyAssetId,
      dreaminaProxyAssetStatus: omitProviderAssetMetadata ? undefined : item?.dreaminaProxyAssetStatus,
      assetProvider: omitProviderAssetMetadata ? undefined : item?.assetProvider,
      voiceSampleUrl: omitProviderAssetMetadata ? undefined : item?.voiceSampleUrl,
      voiceSampleName: omitProviderAssetMetadata ? undefined : item?.voiceSampleName,
      voiceBoundAt: omitProviderAssetMetadata ? undefined : item?.voiceBoundAt,
      referencePurpose: item?.referencePurpose,
      purpose: item?.purpose,
    };
    if (refs[groupKey]) refs[groupKey].push(entry);
    refs._bindings.push({ ...entry, groupKey });
  });
  return refs;
};

const cloneSegmentValue = (value, fallback) => {
  try {
    return value == null ? fallback : JSON.parse(JSON.stringify(value));
  } catch {
    return fallback;
  }
};

const copySemanticSpatialPlan = (value) => {
  const spatial = cloneSegmentValue(value, {});
  ["source", "guaVideoMode", "sourceSegmentIndex", "frameSecond", "mosaic"].forEach((key) => {
    delete spatial[key];
  });
  spatial.manual = true;
  return spatial;
};

const setPromptReferenceBinding = (prompt, references = []) => {
  const cleanLines = formatSeedancePrompt(prompt)
    .split("\n")
    .filter((line) => !/^\s*参考图绑定\s*[：:]/.test(line))
    .filter((line) => !/^\s*本段出[镜境]参考\s*[：:]/.test(line));
  const cleanPrompt = cleanLines.join("\n").trim();
  if (!references.length) return formatSeedancePrompt(`参考图绑定：无${cleanPrompt ? `\n\n${cleanPrompt}` : ""}`);
  const bindingLine = references.map((item, index) => {
    const label = getReferencePromptName(item, `参考图${index + 1}`);
    return `${referenceMarker(item, index)}=${label}`;
  }).join("，");
  return formatSeedancePrompt(`参考图绑定：${bindingLine}\n\n${cleanPrompt}`);
};

const referenceIdentityCandidates = (item = {}) => [
  item.url,
  item.id != null ? String(item.id) : "",
  item.guaVideoAssetId ? `guavideo:${item.guaVideoAssetId}` : "",
  item.engGuaVideoAssetId ? `engguavideo:${item.engGuaVideoAssetId}` : "",
  item.guaOminiAssetId ? `omini_seedance:${item.guaOminiAssetId}` : "",
  item.dreaminaProxyAssetId ? `dreamina_proxy:${item.dreaminaProxyAssetId}` : "",
  item.displayName,
  firstText(item.name).replace(/^(角色|场景|道具)：/, ""),
].map(firstText).filter(Boolean);

const hasStableReferenceIdentity = (item = {}) => referenceEntryIdentityCandidates(item).length > 0;

const referencePlainLabel = (item, fallback = "参考图") => firstText(
  getReferencePromptName(item, fallback),
  fallback,
);

const findReferenceIndex = (needle, haystack = []) => {
  const stableIndex = haystack.findIndex((item) => scoreExplicitReferenceIdentity(needle, item) > 0);
  if (stableIndex >= 0) return stableIndex;
  const candidates = referenceIdentityCandidates(needle);
  if (!candidates.length) return -1;
  return haystack.findIndex((item) => {
    if (referenceGroupsConflict(needle, item)) return false;
    // 双方都有 URL/资产 ID 时，稳定身份未命中就明确视为不同资产。
    // 名称回退只服务缺少历史身份字段的旧绑定，避免重名资产出现双勾选。
    if (hasStableReferenceIdentity(needle) && hasStableReferenceIdentity(item)) return false;
    const nextCandidates = new Set(referenceIdentityCandidates(item));
    return candidates.some((candidate) => nextCandidates.has(candidate));
  });
};

const remapPromptReferenceMarkers = (prompt, previousReferences = [], nextReferences = []) => {
  const value = formatSeedancePrompt(prompt);
  if (!value || !previousReferences.length) return value;

  const markerMap = new Map();
  previousReferences.forEach((item, index) => {
    const nextIndex = findReferenceIndex(item, nextReferences);
    const nextItem = nextIndex >= 0 ? nextReferences[nextIndex] : null;
    markerMap.set(index + 1, nextIndex >= 0 ? referenceMarker(nextItem, nextIndex) : referencePlainLabel(item, `已移除参考图${index + 1}`));
  });

  return formatSeedancePrompt(value.replace(/@(图片|视频|音频)\s*(\d+)/g, (match, _kind, rawIndex) => (
    markerMap.has(Number(rawIndex)) ? markerMap.get(Number(rawIndex)) : match
  )));
};

const buildSegmentReferenceItems = (segment, assetGroups = {}, referenceLimit = 9) => {
  const maxReferences = Math.max(1, Number(referenceLimit) || 9);
  const visibleContext = buildSegmentVisibleContext(segment);
  const visibleCharacterNames = collectVisibleCharacterNames(segment);
  const refs = segment?.assetRefs || {};
  const persistedSegment = segment?.id != null;
  const persistedBindings = (Array.isArray(refs?._bindings) ? refs._bindings : [])
    .filter((binding) => firstText(binding?.url));
  if (persistedBindings.length) {
    return persistedBindings
      .slice(0, maxReferences)
      .map((binding, index) => buildReferenceItemFromBinding(binding, `参考图${index + 1}`));
  }

  if (persistedSegment) {
    // Legacy persisted rows may only contain reference names. Treat every stored entry as one
    // immutable binding slot: resolve at most one exact asset, never expand one name into every
    // same-name asset and never infer additions/removals from the current prompt.
    const selected = [];
    const seen = new Set();
    REFERENCE_GROUPS.forEach(({ key: groupKey }) => {
      const storedEntries = Array.isArray(refs[groupKey])
        ? refs[groupKey]
        : refs[groupKey] == null ? [] : [refs[groupKey]];
      storedEntries.forEach((entry, entryIndex) => {
        if (selected.length >= maxReferences) return;
        if (entry && typeof entry === "object" && firstText(entry.url)) {
          const item = buildReferenceItemFromBinding({
            ...entry,
            groupKey,
            type: firstText(entry.type, referenceTypeLabelForGroup(groupKey)),
          }, `参考图${selected.length + 1}`);
          const url = normalizeImageUrl(item.url);
          if (url && !seen.has(url)) {
            seen.add(url);
            selected.push(item);
          }
          return;
        }

        const storedName = cleanEditableReferenceName(referenceEntryName(entry));
        if (!storedName) return;
        const storedIdentities = new Set(referenceEntryIdentityCandidates(entry));
        let best = null;
        (assetGroups[groupKey] || []).forEach((asset, assetIndex) => {
          if (isVariantAssetCandidate(asset) && !isVariantSpecificReferenceLabel(asset, storedName)) return;
          const identityMatch = storedIdentities.size > 0
            && assetIdentityCandidates(asset).some((candidate) => storedIdentities.has(candidate));
          const exactNameMatch = assetNameCandidates(asset).some((candidate) => sameReferenceName(candidate, storedName));
          if (!identityMatch && !exactNameMatch) return;
          const score = identityMatch ? 2 : 1;
          if (!best || score > best.score || (score === best.score && assetIndex < best.assetIndex)) {
            best = { score, assetIndex, asset };
          }
        });
        if (!best && groupKey !== "characters") {
          const overlappingAssets = (assetGroups[groupKey] || [])
            .map((asset, assetIndex) => ({ asset, assetIndex }))
            .filter(({ asset }) => (
              !isVariantAssetCandidate(asset)
              && assetNameCandidates(asset).some((candidate) => referenceNamesOverlap(candidate, storedName))
            ));
          // Historical/fallback rows can contain a scene/prop short name instead of the
          // canonical asset label. Resolve it only when the overlap identifies one asset;
          // ambiguous names stay unbound instead of silently picking the first DB row.
          if (overlappingAssets.length === 1) {
            best = { score: 0, ...overlappingAssets[0] };
          }
        }
        if (!best) return;
        const item = buildReferenceItemFromAsset(best.asset, groupKey);
        const url = normalizeImageUrl(item.url);
        if (!url || seen.has(url)) return;
        seen.add(url);
        selected.push({
          ...item,
          storedReferenceIndex: entryIndex,
        });
      });
    });
    return selected;
  }

  const explicitLabels = extractExplicitReferenceBindingLabels(segment?.seedancePrompt);
  if (explicitLabels) {
    const explicitBindings = Array.isArray(refs?._bindings) ? refs._bindings : [];
    const selected = [];
    const seen = new Set();
    explicitLabels.slice(0, maxReferences).forEach((label, index) => {
      const bindingEntry = explicitBindings[index];
      if (firstText(bindingEntry?.url)) {
        selected.push(buildReferenceItemFromBinding(bindingEntry, label));
        seen.add(normalizeImageUrl(bindingEntry.url));
        return;
      }
      const expectedGroupKey = firstText(bindingEntry?.groupKey) || groupKeyForReferenceType(bindingEntry?.type);
      const identityCandidates = new Set(referenceEntryIdentityCandidates(bindingEntry));
      const bindingLabel = referenceEntryName(bindingEntry) || label;
      let best = null;
      REFERENCE_GROUPS.forEach(({ key: groupKey, priority }) => {
        if (expectedGroupKey && expectedGroupKey !== groupKey) return;
        (assetGroups[groupKey] || []).forEach((asset) => {
          const candidates = assetNameCandidates(asset);
          const identityScore = scoreExplicitReferenceIdentity(bindingEntry, asset);
          let score = identityScore ? identityScore + priority : 0;
          if (!score && identityCandidates.size > 0 && assetIdentityCandidates(asset).some((candidate) => identityCandidates.has(candidate))) {
            score = Math.max(score, 10000 + priority);
          }
          if (score) {
            // Stable identity already selected this asset; name matching is only a fallback below.
          } else if (isVariantAssetCandidate(asset) && !isVariantSpecificReferenceLabel(asset, bindingLabel)) {
            // 没有稳定身份时，"苏婉" 这种本体名不能靠名字兜底选中所有变体。
            // 变体只接受显式身份，或明确写了变体名/完整变体名的 label。
          } else {
            candidates.forEach((candidate) => {
              if (sameReferenceName(candidate, bindingLabel)) {
                score = Math.max(score, 5000 + priority);
              } else if (groupKey !== "characters" && referenceNamesOverlap(candidate, bindingLabel)) {
                score = Math.max(score, 3000 + Math.min(candidate.length, bindingLabel.length) + priority);
              }
            });
          }
          if (score && (!best || score > best.score)) {
            best = {
              score,
              groupKey,
              item: buildReferenceItemFromAsset(asset, groupKey),
            };
          }
        });
      });
      const url = normalizeImageUrl(best?.item?.url);
      if (url && !seen.has(url)) {
        seen.add(url);
        selected.push(best.item);
      }
    });
    return selected;
  }

  const matches = [];
  const seen = new Set();
  REFERENCE_GROUPS.forEach(({ key: groupKey, priority }) => {
    let refNames = toReferenceNameList(refs[groupKey]).map(cleanEditableReferenceName).filter(Boolean);
    let context = visibleContext;
    if (groupKey === "characters") {
      if (visibleCharacterNames.length) {
        refNames = [...new Set([
          ...visibleCharacterNames,
          ...refNames.filter((name) => isReferenceNameVisible(name, visibleCharacterNames, visibleContext)),
        ])];
        context = "";
      } else {
        // 没有结构化出场信号时，只允许画面/机位/spatial 中真实出现的角色名进入参考。
        // 不再无条件信任 AI 返回的 assetRefs.characters，避免单段塞入整集人物。
        refNames = refNames.filter((name) => isReferenceNameVisible(name, [], visibleContext));
        context = "";
      }
    } else if (visibleContext) {
      // 不能无条件信任 AI 返回的 scenes / props。英文资产名里常见的
      // of、in、on 等短词曾把完全无关的整条资产带进当前片段。
      // 自动引用必须在本段地点、画面、机位或站位描述中完整出现；
      // 显式的用户绑定已在上方 explicitLabels 分支处理，不受此过滤影响。
      refNames = refNames.filter((name) => referenceNameAppearsInVisibleContext(
        name,
        visibleContext,
        { allowPossessive: true },
      ));
    }
    (assetGroups[groupKey] || []).forEach((asset) => {
      // 自动文本匹配只选主体资产。变体必须来自显式绑定/用户手动替换，
      // 否则“苏婉”会同时命中“苏婉”的所有变体图。
      if (
        isVariantAssetCandidate(asset)
        && !refNames.some((name) => (
          assetNameCandidates(asset).some((candidate) => sameReferenceName(candidate, name))
        ))
      ) return;
      const score = scoreAssetReferenceMatch(
        asset,
        refNames,
        context,
        groupKey === "characters",
      );
      if (!score) return;
      const url = normalizeImageUrl(getAssetImageUrl(asset));
      if (!url || seen.has(url)) return;
      seen.add(url);
      matches.push({
        score,
        priority,
        ...buildReferenceItemFromAsset(asset, groupKey),
      });
    });
  });
  return matches
    .sort((a, b) => b.score - a.score || b.priority - a.priority)
    .slice(0, maxReferences)
    .map((match) => {
      const item = { ...match };
      delete item.score;
      delete item.priority;
      return item;
    });
};

const REFERENCE_MARKER_PROTECTED_LINE_RE = /^\s*[-–—]?\s*(文本|对白|台词|字幕|旁白|解说|画外音|VO|OS|声音|参考图绑定|本段出[镜境]参考)\s*[：:]/i;

const mergeGuaMetaForReferences = (references = [], assetMetaMap = {}) => (
  references.map((item) => {
    const meta = getAssetMetaForUrl(assetMetaMap, item.url);
    return {
      ...item,
      guaVideoAssetId: firstText(meta?.guaVideoAssetId, item.guaVideoAssetId),
      guaVideoAssetStatus: firstText(meta?.guaVideoAssetStatus, item.guaVideoAssetStatus),
      engGuaVideoAssetId: firstText(meta?.engGuaVideoAssetId, item.engGuaVideoAssetId),
      engGuaVideoAssetStatus: firstText(meta?.engGuaVideoAssetStatus, item.engGuaVideoAssetStatus),
      guaOminiAssetId: firstText(meta?.guaOminiAssetId, item.guaOminiAssetId),
      guaOminiAssetStatus: firstText(meta?.guaOminiAssetStatus, item.guaOminiAssetStatus),
      dreaminaProxyAssetId: firstText(meta?.dreaminaProxyAssetId, item.dreaminaProxyAssetId),
      dreaminaProxyAssetStatus: firstText(meta?.dreaminaProxyAssetStatus, item.dreaminaProxyAssetStatus),
      assetProvider: firstText(meta?.provider, item.assetProvider, item.provider),
      voiceSampleUrl: firstText(item.voiceSampleUrl),
      voiceSampleName: firstText(item.voiceSampleName),
      voiceBoundAt: item.voiceBoundAt ?? null,
    };
  })
);

const applyReferenceMarkersToPrompt = (prompt, references = []) => {
  let result = formatSeedancePrompt(prompt);
  const hasExplicitBinding = hasExplicitReferenceBindingLine(result);
  const used = new Set();
  const replacements = references.map((item, index) => {
    const marker = referenceMarker(item, index);
    const aliases = [...new Set([
      getReferencePromptName(item),
      item.displayName,
      item.assetDisplayName,
      item.parentName,
      item.variantTitle,
      firstText(item.name).replace(/^(角色|场景|道具)：/, ""),
      ...(item.aliases || []),
    ].map(firstText).filter(isSpecificReferenceName))]
      .sort((a, b) => b.length - a.length);
    return { index, marker, aliases };
  });

  result = result.split("\n").map((line) => {
    if (REFERENCE_MARKER_PROTECTED_LINE_RE.test(line)) return line;
    let nextLine = line;
    replacements.forEach(({ index, marker, aliases }) => {
      aliases.forEach((alias) => {
        const replacement = replaceReferenceMentions(nextLine, alias, marker);
        if (replacement.replaced) {
          used.add(index);
          nextLine = replacement.text;
        }
      });
    });
    return nextLine;
  }).join("\n");

  references.forEach((item, index) => {
    const marker = referenceMarker(item, index);
    if (result.includes(marker)) {
      used.add(index);
    }
  });
  const referenceHintLines = references
    .map((item, index) => ({ item, index }))
    .filter(({ index }) => !used.has(index))
    .map(({ item, index }) => {
      const label = getReferencePromptName(item, `参考图${index + 1}`);
      return `${referenceMarker(item, index)}${label ? ` ${label}` : ""}`;
    });
  if (!hasExplicitBinding && referenceHintLines.length) {
    result = `${result}\n\n本段出镜参考：${referenceHintLines.join("；")}`;
  }
  if (references.length) {
    const bindingLine = references.map((item, index) => {
      const label = getReferencePromptName(item, `参考图${index + 1}`);
      return `${referenceMarker(item, index)}${label ? `=${label}` : ""}`;
    }).join("，");
    if (!hasExplicitBinding) {
      result = `参考图绑定：${bindingLine}\n\n${result}`;
    }
  }
  return formatSeedancePrompt(result);
};

const applySegmentReferenceMarkersToPrompt = (segment, references = []) => (
  isOneClickFlowSegment(segment)
    ? transformOneClickFlowPrompt(segment?.seedancePrompt, (prompt) => applyReferenceMarkersToPrompt(prompt, references))
    : applyReferenceMarkersToPrompt(segment?.seedancePrompt, references)
);

const remapSegmentPromptReferenceMarkers = (segment, prompt, previousReferences, references) => (
  isOneClickFlowSegment(segment)
    ? transformOneClickFlowPrompt(prompt, (text) => remapPromptReferenceMarkers(text, previousReferences, references))
    : remapPromptReferenceMarkers(prompt, previousReferences, references)
);

const hasEditableReferenceHints = (prompt) => (
  /参考图绑定|本段出[镜境]参考|@(图片|视频|音频)\s*\d+/.test(firstText(prompt))
);

const reorderReferencesByPromptBinding = (prompt, references = []) => {
  if (!references.length || !hasEditableReferenceHints(prompt)) return references;
  const markerLabels = new Map();
  const markerRe = /@(图片|视频|音频)\s*(\d+)(?:\s*[=＝]\s*([^，,；;\n]+)|\s+([^，,；;\n]+))?/g;
  formatSeedancePrompt(prompt).split("\n").forEach((line) => {
    if (!line.includes("参考图绑定") && !/本段出[镜境]参考/.test(line)) return;
    let match;
    while ((match = markerRe.exec(line)) !== null) {
      const index = Number(match[2]) - 1;
      const label = cleanEditableReferenceName(match[3] || match[4]);
      if (Number.isFinite(index) && index >= 0 && label) {
        markerLabels.set(index, label);
      }
    }
  });
  if (!markerLabels.size) return references;

  const remaining = [...references];
  const ordered = [];
  const maxIndex = Math.max(...markerLabels.keys());
  for (let index = 0; index <= maxIndex; index += 1) {
    const label = markerLabels.get(index);
    if (!label) continue;
    const foundIndex = remaining.findIndex((item) => {
      const candidates = [
        getReferencePromptName(item),
        item.displayName,
        item.assetDisplayName,
        item.parentName,
        item.variantTitle,
        firstText(item.name).replace(/^(角色|场景|道具)：/, ""),
        ...(item.aliases || []),
      ].map(firstText).filter(isSafeAutomaticReferenceCandidate);
      return candidates.some((candidate) => referenceNamesOverlap(candidate, label));
    });
    if (foundIndex >= 0) {
      ordered[index] = remaining.splice(foundIndex, 1)[0];
    }
  }

  const result = [];
  for (let index = 0; index <= maxIndex; index += 1) {
    result.push(ordered[index] || remaining.shift());
  }
  return result.concat(remaining).filter(Boolean);
};

const buildSegmentPromptWithReferences = (segment, assetGroups = {}, assetMetaMap = {}, referenceLimit = 9) => {
  const references = reorderReferencesByPromptBinding(
    segment?.seedancePrompt,
    mergeGuaMetaForReferences(buildSegmentReferenceItems(segment, assetGroups, referenceLimit), assetMetaMap),
  );
  return {
    prompt: applySegmentReferenceMarkersToPrompt(segment, references),
    references,
  };
};

// —— 站位参考：显示层注入（与后端 enhanceVideoSubmission 同构，只用于展示，不持久化）——
const shiftPromptReferenceMarkers = (prompt, delta) => (
  firstText(prompt).replace(/@(图片|视频|音频)(\s*)(\d+)/g, (match, kind, space, num) => (
    `@${kind}${space}${Number(num) + delta}`
  ))
);

const buildBlockingConstraintText = (plan) => {
  const bindingNote = firstText(plan?.environment?.prompt);
  return [
    "【站位关系约束】",
    "@图片1是站位参考图，只参考其中人物的站位、朝向、距离、遮挡、视线、景别和构图。",
    "不要使用或复刻站位参考图中人物的脸、外貌、发型、服装或身份。",
    bindingNote ? `站位绑定说明：${bindingNote}` : "",
    "最终人物形象必须以各自角色参考图为准，不得新增、删减、调换或复制人物。",
    "【/站位关系约束】",
  ].filter(Boolean).join("\n");
};

const buildBlockingReferenceItem = (plan, assetMetaMap = {}) => {
  if (!plan || !firstText(plan?.modelReferenceUrl)) return null;
  const referenceAsset = plan?.environment?.referenceAsset || {};
  const meta = getAssetMetaForUrl(assetMetaMap, plan.modelReferenceUrl);
  const provider = firstText(meta?.provider, referenceAsset?.provider, "guavideo");
  return {
    id: `blocking-plan:${plan.id}`,
    type: "站位关系",
    name: firstText(plan.name, "站位关系"),
    displayName: firstText(plan.name, "站位关系"),
    role: "reference_image",
    referencePurpose: "blocking_reference",
    mediaType: "image",
    url: plan.modelReferenceUrl,
    blockingPlanId: plan.id,
    guaVideoAssetId: firstText(meta?.guaVideoAssetId, provider === "guavideo" ? referenceAsset?.assetId : ""),
    guaVideoAssetStatus: firstText(meta?.guaVideoAssetStatus, provider === "guavideo" ? referenceAsset?.status : ""),
    engGuaVideoAssetId: firstText(meta?.engGuaVideoAssetId, provider === "engguavideo" ? referenceAsset?.assetId : ""),
    engGuaVideoAssetStatus: firstText(meta?.engGuaVideoAssetStatus, provider === "engguavideo" ? referenceAsset?.status : ""),
    guaOminiAssetId: firstText(meta?.guaOminiAssetId, provider === VIDEO_PROVIDER_OMINI_SEEDANCE ? referenceAsset?.assetId : ""),
    guaOminiAssetStatus: firstText(meta?.guaOminiAssetStatus, provider === VIDEO_PROVIDER_OMINI_SEEDANCE ? referenceAsset?.status : ""),
    dreaminaProxyAssetId: firstText(meta?.dreaminaProxyAssetId, referenceAsset?.dreaminaProxyAssetId),
    dreaminaProxyAssetStatus: firstText(meta?.dreaminaProxyAssetStatus, referenceAsset?.dreaminaProxyAssetStatus),
    assetProvider: provider,
  };
};

const extractAssetRefsFromEditablePrompt = (prompt, references = []) => {
  // Editing ordinary prompt text must not turn the current automatic match snapshot
  // into a permanent manual binding. Only the reference picker writes mode=manual.
  const promptBindings = assetRefsFromReferenceItems(references, "prompt_snapshot");
  const refs = {
    characters: [],
    scenes: [],
    props: [],
    _bindings: promptBindings._bindings || [],
    _bindingMode: "prompt_snapshot",
  };
  const markerBindings = new Map();
  const lines = formatSeedancePrompt(prompt).split("\n");

  lines.forEach((line) => {
    if (line.includes("参考图绑定") || /本段出[镜境]参考/.test(line)) {
      const markerRe = /@(图片|视频|音频)\s*(\d+)(?:\s*[=＝]\s*([^，,；;\n]+)|\s+([^，,；;\n]+))?/g;
      let markerMatch;
      while ((markerMatch = markerRe.exec(line)) !== null) {
        const index = Number(markerMatch[2]) - 1;
        const item = references[index];
        const groupKey = groupKeyForReferenceType(item?.type);
        const name = cleanEditableReferenceName(markerMatch[3] || markerMatch[4] || getReferencePromptName(item, "") || item?.name);
        if (!name) continue;
        markerBindings.set(index, { groupKey, name });
        addAssetRefName(refs, groupKey, name);
      }
    }

    const typedRe = /(角色|人物|场景|地点|道具)\s*[：:]\s*([^，,；;\n]+)/g;
    let typedMatch;
    while ((typedMatch = typedRe.exec(line)) !== null) {
      const groupKey = groupKeyForReferenceType(typedMatch[1]);
      addAssetRefName(refs, groupKey, typedMatch[2]);
    }
  });

  const markerRe = /@(图片|视频|音频)\s*(\d+)/g;
  let match;
  const value = firstText(prompt);
  while ((match = markerRe.exec(value)) !== null) {
    const index = Number(match[2]) - 1;
    const binding = markerBindings.get(index);
    if (binding) {
      addAssetRefName(refs, binding.groupKey, binding.name);
      continue;
    }
    const item = references[index];
    if (!item) continue;
    addAssetRefName(refs, groupKeyForReferenceType(item.type), getReferencePromptName(item, "") || item.name);
  }

  return refs;
};

const ReferenceChip = ({ item, index }) => {
  const label = getReferencePromptName(item, `图片${index + 1}`);
  const isVideo = referenceMediaType(item) !== "image";
  const marker = referenceMarker(item, index);
  const variantHint = item?.isVariant && firstText(item?.variantTitle)
    ? `（使用变体图：${firstText(item.variantTitle)}）`
    : "";
  if (isVideo) {
    return (
      <span
        className="mini-workbench__reference-chip mx-1 inline-flex max-w-[260px] translate-y-[3px] items-center gap-1.5 rounded-md px-1.5 py-1 align-baseline"
        style={{
          backgroundColor: "var(--color-bg-tertiary)",
          border: "1px solid var(--color-border-secondary)",
          color: "var(--color-text-primary)",
        }}
        title={`提交给视频生成：${marker}=${label}`}
      >
        <Video size={14} className="shrink-0" />
        <span className="truncate text-xs">{label}</span>
      </span>
    );
  }
  return (
    <HoverImagePreview
      src={item.url}
      title={`${label}${variantHint}`}
      className="mx-1 inline-block translate-y-[3px] align-baseline"
      previewWidth={360}
      previewHeight={460}
    >
      <span
        className="mini-workbench__reference-chip inline-flex max-w-[260px] items-center gap-1.5 rounded-md px-1.5 py-1"
        style={{
          backgroundColor: "var(--color-bg-tertiary)",
          border: "1px solid var(--color-border-secondary)",
          color: "var(--color-text-primary)",
        }}
        title={`提交给视频生成：${marker}=${label}${variantHint}`}
      >
        <img src={item.url} alt={label} className="h-5 w-5 shrink-0 rounded object-cover" />
        <span className="truncate text-xs">{label}</span>
      </span>
    </HoverImagePreview>
  );
};

const PromptWithReferenceChips = ({ prompt, references = [], className = "", textStyle = {} }) => {
  const parts = firstText(prompt).split(/(@(?:图片|视频|音频)\d+)/g);
  return (
    <div
      className={`mini-workbench__prompt-copy ${className} whitespace-pre-wrap break-words`}
      style={{ ...subtleSurfaceStyle, ...textStyle, color: "var(--color-text-secondary)" }}
    >
      {parts.map((part, index) => {
        const match = part.match(/^@(图片|视频|音频)(\d+)$/);
        if (!match) return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
        const refIndex = Number(match[2]) - 1;
        const ref = references[refIndex];
        if (!ref) return <React.Fragment key={`missing-${index}`}>{part}</React.Fragment>;
        return <ReferenceChip key={`ref-${refIndex}-${index}`} item={ref} index={refIndex} />;
      })}
    </div>
  );
};

const buildEmptySegment = (index = 1) => ({
  segmentIndex: index,
  storyMode: "drama",
  narrationText: "",
  title: `分镜组 ${String(index).padStart(2, "0")}`,
  durationSeconds: 12,
  timeRange: `${formatSeconds((index - 1) * 12)}-${formatSeconds(index * 12)}`,
  location: "当前场景",
  weather: "日",
  sourceText: "",
  shots: [],
  spatial: {},
  assetRefs: {},
  seedancePrompt: "",
  status: "draft",
});

const loadVideoForFrameCapture = async (videoUrl) => {
  const url = firstText(videoUrl);
  if (!url) throw new Error("缺少可截取的视频链接");
  const video = document.createElement("video");
  video.crossOrigin = "anonymous";
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.src = url;

  await new Promise((resolve, reject) => {
    const cleanup = () => {
      video.onloadedmetadata = null;
      video.onerror = null;
    };
    video.onloadedmetadata = () => {
      cleanup();
      resolve();
    };
    video.onerror = () => {
      cleanup();
      reject(new Error("视频加载失败，无法截帧"));
    };
  });
  return video;
};

const seekVideoToSecond = async (video, seconds = 0) => {
  const duration = Number(video.duration) || 0;
  const targetSecond = clamp(Number(seconds) || 0, 0, Math.max(0, duration - 0.05));
  await new Promise((resolve, reject) => {
    const cleanup = () => {
      video.onseeked = null;
      video.onerror = null;
    };
    video.onseeked = () => {
      cleanup();
      resolve();
    };
    video.onerror = () => {
      cleanup();
      reject(new Error("定位视频帧失败"));
    };
    video.currentTime = targetSecond;
  });
  return targetSecond;
};

const drawVideoFrameToCanvas = (video, maxWidth = null) => {
  const width = video.videoWidth || 720;
  const height = video.videoHeight || 1280;
  const canvas = document.createElement("canvas");
  if (maxWidth && width > maxWidth) {
    canvas.width = maxWidth;
    canvas.height = Math.max(1, Math.round((height / width) * maxWidth));
  } else {
    canvas.width = width;
    canvas.height = height;
  }
  const context = canvas.getContext("2d");
  if (!context) throw new Error("当前浏览器无法创建截帧画布");
  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas;
};

const cleanupFrameCaptureVideo = (video) => {
  if (!video) return;
  video.pause?.();
  video.removeAttribute("src");
  video.load?.();
};

const captureVideoFrameToFile = async (videoUrl, seconds = 0) => {
  const video = await loadVideoForFrameCapture(videoUrl);
  try {
    await seekVideoToSecond(video, seconds);
    const canvas = drawVideoFrameToCanvas(video);
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob((nextBlob) => {
        if (nextBlob) resolve(nextBlob);
        else reject(new Error("截帧失败，可能是视频跨域限制"));
      }, "image/png", 0.92);
    });
    return new File([blob], `mini-storyboard-frame-${Date.now()}.png`, { type: "image/png" });
  } finally {
    cleanupFrameCaptureVideo(video);
  }
};

const captureVideoFrameToDataUrl = async (videoUrl, seconds = 0, maxWidth = 720) => {
  const video = await loadVideoForFrameCapture(videoUrl);
  try {
    await seekVideoToSecond(video, seconds);
    const canvas = drawVideoFrameToCanvas(video, maxWidth);
    return canvas.toDataURL("image/jpeg", 0.86);
  } finally {
    cleanupFrameCaptureVideo(video);
  }
};

const buildVideoFramePreviewStrip = async (videoUrl, durationHint = 0, count = 10) => {
  const video = await loadVideoForFrameCapture(videoUrl);
  try {
    const duration = Number(video.duration) || Number(durationHint) || 0;
    const safeDuration = Math.max(0.1, duration);
    const frameCount = Math.max(4, Math.min(12, count, Math.ceil(safeDuration) + 1));
    const seconds = Array.from({ length: frameCount }, (_, index) => {
      if (frameCount === 1) return 0;
      return clamp((safeDuration * index) / (frameCount - 1), 0, Math.max(0, safeDuration - 0.05));
    });
    const previews = [];
    for (const second of seconds) {
      const actualSecond = await seekVideoToSecond(video, second);
      const canvas = drawVideoFrameToCanvas(video, 132);
      previews.push({
        second: Number(actualSecond.toFixed(2)),
        imageUrl: canvas.toDataURL("image/jpeg", 0.78),
      });
    }
    return previews;
  } finally {
    cleanupFrameCaptureVideo(video);
  }
};

const isVideoSuccess = (segment) => segment?.status === "video_success" && firstText(segment?.videoUrl);
const isVideoProcessing = (segment) => segment?.status === "video_processing";
const isVideoFailed = (segment) => segment?.status === "video_failed";
const isMusicCueSuccess = (cue) => firstText(cue?.audioUrl) && firstText(cue?.status).toLowerCase() !== "audio_failed";
const getMusicCueDurationForGeneration = (cue) => {
  const duration = Number(cue?.durationSeconds) || Math.max(1, (Number(cue?.endSeconds) || 0) - (Number(cue?.startSeconds) || 0));
  return clamp(Math.ceil(duration / 30) * 30, 60, 180);
};
const getMusicCueTags = (cue) => {
  if (Array.isArray(cue?.tags)) return cue.tags.map(firstText).filter(Boolean);
  return firstText(cue?.styleTags, cue?.style)
    .split(/[，,、;；]/)
    .map(firstText)
    .filter(Boolean)
    .slice(0, 8);
};
const getMusicCompletionResult = (completion) => completion?.result || completion?.data || completion || {};

const statusLabel = (segment) => {
  if (isVideoSuccess(segment)) return "已生成";
  if (isVideoProcessing(segment)) return "生成中";
  if (isVideoFailed(segment)) return "失败";
  if (segment?.seedancePrompt) return "待生成";
  return "待拆分";
};

const statusStyle = (segment) => {
  if (isVideoSuccess(segment)) {
    return { backgroundColor: "var(--color-green-light)", color: "var(--color-success)" };
  }
  if (isVideoProcessing(segment)) {
    return { backgroundColor: "var(--color-blue-light)", color: "var(--color-info)" };
  }
  if (isVideoFailed(segment)) {
    return { backgroundColor: "var(--color-red-light)", color: "var(--color-error)" };
  }
  if (segment?.seedancePrompt) {
    return { backgroundColor: "var(--color-accent-light)", color: "var(--color-accent-primary)" };
  }
  return { backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" };
};

const historyStatusLabel = (item) => {
  const status = firstText(item?.status).toLowerCase();
  if (["success", "video_success", "completed", "done", "succeeded"].includes(status)) return "成功";
  if (["processing", "video_processing", "pending"].includes(status)) return "生成中";
  if (["failed", "video_failed", "error", "canceled"].includes(status)) return "失败";
  return firstText(item?.status, "未知");
};

const historyStatusStyle = (item) => {
  const status = firstText(item?.status).toLowerCase();
  if (["success", "video_success", "completed", "done", "succeeded"].includes(status)) {
    return { backgroundColor: "var(--color-green-light)", color: "var(--color-success)" };
  }
  if (["processing", "video_processing", "pending"].includes(status)) {
    return { backgroundColor: "var(--color-blue-light)", color: "var(--color-info)" };
  }
  if (["failed", "video_failed", "error", "canceled"].includes(status)) {
    return { backgroundColor: "var(--color-red-light)", color: "var(--color-error)" };
  }
  return { backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" };
};

const panelStyle = {
  backgroundColor: "var(--color-bg-card)",
  border: "1px solid var(--color-border-primary)",
  boxShadow: "var(--shadow-card)",
};

const subtleSurfaceStyle = {
  backgroundColor: "var(--color-bg-overlay)",
  border: "1px solid var(--color-border-secondary)",
};

const inputSurfaceStyle = {
  backgroundColor: "var(--color-bg-overlay)",
  border: "1px solid var(--color-border-primary)",
  color: "var(--color-text-primary)",
};

const primaryButtonStyle = {
  backgroundColor: "var(--color-accent-primary)",
  color: "var(--color-accent-contrast, #fff)",
};

const ghostButtonStyle = {
  backgroundColor: "var(--color-bg-secondary)",
  border: "1px solid var(--color-border-primary)",
  color: "var(--color-text-secondary)",
};

const AssetCard = ({ asset, type, onSelect, selectable = false, selected = false }) => {
  const imageUrl = getAssetImageUrl(asset);
  const title = getAssetName(asset);
  return (
    <button
      type="button"
      onClick={onSelect ? () => onSelect(asset, type) : undefined}
      className={`mini-workbench__asset-card min-w-0 text-left ${selectable ? "cursor-pointer" : "cursor-default"}`}
      data-selectable={selectable}
      data-selected={selected}
      aria-pressed={selectable ? selected : undefined}
      title={selectable ? `${selected ? "取消选择" : "选择"} ${title}` : title}
    >
      <HoverImagePreview
        src={imageUrl}
        title={title}
        className="block"
        previewWidth={360}
        previewHeight={500}
      >
        <div
          className="mini-workbench__asset-image relative aspect-[3/4] overflow-hidden rounded-md"
          style={{
            backgroundColor: "var(--color-bg-tertiary)",
            border: selected ? "1px solid var(--color-accent-primary)" : "1px solid var(--color-border-secondary)",
          }}
        >
          {imageUrl ? (
            <img src={imageUrl} alt={title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center" style={{ color: "var(--color-text-muted)" }}>
              {type === "characters" ? <Users size={24} /> : <ImageIcon size={24} />}
            </div>
          )}
          {selected && (
            <span
              className="absolute right-1.5 top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full shadow"
              style={primaryButtonStyle}
            >
              <Check size={13} />
            </span>
          )}
        </div>
      </HoverImagePreview>
      <div className="mini-workbench__asset-name mt-1 truncate text-xs" style={{ color: "var(--color-text-primary)" }}>{title}</div>
      <div className="mini-workbench__asset-meta truncate text-[11px]" style={{ color: "var(--color-text-muted)" }}>{getAssetSubtitle(asset)}</div>
    </button>
  );
};

const SegmentPreview = ({ segment, videoRef, onTimeUpdate, onEnded }) => {
  const currentVideoUrl = firstText(segment?.videoUrl);
  const videoReady = Boolean(isVideoSuccess(segment));
  const videoPreviewPhase = videoReady ? "video-result" : "video";
  const previewContainerRef = useRef(null);
  const previousVideoStateRef = useRef(null);
  const segmentIdentity = firstText(
    segment?.id != null ? String(segment.id) : "",
    segment?.segmentIndex != null ? String(segment.segmentIndex) : "",
  );
  const videoStatus = firstText(segment?.status);

  useLayoutEffect(() => {
    const previous = previousVideoStateRef.current;
    const awaitingCompletedVideo = previous
      && previous.identity === segmentIdentity
      && previous.status === "video_processing"
      && videoStatus === "video_success"
      && !videoReady;
    if (!awaitingCompletedVideo) {
      previousVideoStateRef.current = { identity: segmentIdentity, status: videoStatus };
    }
    const completedCurrentGeneration = previous
      && previous.identity === segmentIdentity
      && previous.status === "video_processing"
      && videoStatus === "video_success"
      && videoReady;
    const element = previewContainerRef.current;
    if (!completedCurrentGeneration || !element || typeof element.animate !== "function") return undefined;

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const rootStyle = window.getComputedStyle(document.documentElement);
    const duration = reduced
      ? 100
      : Number.parseFloat(rootStyle.getPropertyValue("--motion-duration-base")) || 200;
    const easing = rootStyle.getPropertyValue("--motion-ease-enter").trim() || "cubic-bezier(0.16, 1, 0.3, 1)";
    const animation = element.animate(
      reduced
        ? [{ opacity: 0 }, { opacity: 1 }]
        : [
            { opacity: 0, transform: "translate3d(0, 0, 0) scale(0.985)" },
            { opacity: 1, transform: "translate3d(0, 0, 0) scale(1)" },
          ],
      { duration, easing, fill: "both" },
    );
    animation.finished.then(() => animation.cancel()).catch(() => {});
    return () => animation.cancel();
  }, [segmentIdentity, videoReady, videoStatus]);

  if (currentVideoUrl) {
    return (
      <div
        ref={previewContainerRef}
        data-preview-phase={videoPreviewPhase}
        className="relative h-full w-full"
      >
        <video
          ref={videoRef}
          src={currentVideoUrl}
          crossOrigin="anonymous"
          preload="auto"
          playsInline
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
          className="h-full w-full rounded-lg object-contain"
          style={{ backgroundColor: "var(--color-bg-overlay)" }}
        />
        <span className="absolute left-3 top-3 rounded-full px-2 py-1 text-xs" style={statusStyle(segment)}>
          {statusLabel(segment)}
        </span>
      </div>
    );
  }
  if (segment?.thumbnailUrl) {
    return (
      <img
        key="thumbnail"
        data-preview-phase="thumbnail"
        src={segment.thumbnailUrl}
        alt={segment.title}
        className="h-full w-full rounded-lg object-contain"
      />
    );
  }
  return (
    <div
      key="placeholder"
      data-preview-phase="placeholder"
      className="flex h-full w-full flex-col justify-end rounded-lg p-5"
      style={{
        backgroundColor: "var(--color-bg-tertiary)",
        border: "1px solid var(--color-border-secondary)",
      }}
    >
      <div className="mb-auto flex items-center justify-between">
        <div className="inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs" style={ghostButtonStyle}>
          <Film size={14} />
          {statusLabel(segment)}
        </div>
        {isVideoProcessing(segment) && <Loader2 size={18} className="animate-spin" style={{ color: "var(--color-info)" }} />}
      </div>
      <div className="text-2xl font-semibold" style={{ color: "var(--color-text-secondary)" }}>{String(segment?.segmentIndex || 1).padStart(2, "0")}</div>
      <div className="mt-3 text-sm" style={{ color: "var(--color-text-secondary)" }}>{firstText(segment?.title, "分镜组")}</div>
      <div className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>{formatSegmentTimelineRange(segment)}</div>
      {isVideoFailed(segment) && (
        <div className="mt-3 line-clamp-3 rounded-md p-2 text-xs leading-5" style={statusStyle(segment)}>
          {firstText(segment?.error, "视频生成失败")}
        </div>
      )}
    </div>
  );
};

const MissingScriptPanel = ({ projectId, episodeId, onRefresh }) => {
  const navigate = useNavigate();
  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <div className="w-full max-w-[760px] rounded-xl border p-6 text-center" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg" style={{ backgroundColor: "var(--color-accent-light)", color: "var(--color-accent-primary)" }}>
          <BookOpen size={24} />
        </div>
        <div className="text-lg font-semibold" style={{ color: "var(--color-text-primary)" }}>当前集缺少正式剧本</div>
        <div className="mx-auto mt-2 max-w-[560px] text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>
          小故事板会按本集剧本拆分视频提示词。请先在剧本中心导入总剧本并拆出当前集，再回来生成。
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => navigate(`/project/${projectId}/script-analysis`)}
            className="inline-flex h-9 items-center gap-2 rounded-md border px-4 text-sm font-semibold"
            style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-card)", color: "var(--color-text-primary)" }}
          >
            <BookOpen size={15} />
            去剧本中心拆剧本
          </button>
          {episodeId && (
            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex h-9 items-center gap-2 rounded-md border px-4 text-sm font-semibold"
              style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-card)", color: "var(--color-text-primary)" }}
            >
              <RefreshCw size={15} />
              重新检查
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const MiniStoryboardLoadingState = () => (
  <div className="flex min-h-0 flex-1 items-center justify-center p-6">
    <div className="w-full max-w-[920px] rounded-2xl p-6" style={panelStyle}>
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-md" style={{ backgroundColor: "var(--color-accent-light)", color: "var(--color-accent-primary)" }}>
          <Loader2 size={20} className="animate-spin" />
        </span>
        <div>
          <div className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>正在加载小故事板</div>
          <div className="mt-1 text-sm" style={{ color: "var(--color-text-muted)" }}>正在读取剧本、分镜组、视频状态和资产参考图。</div>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-[180px_minmax(0,1fr)_180px] gap-4">
        <div className="h-64 animate-pulse rounded-xl" style={subtleSurfaceStyle} />
        <div className="space-y-3">
          <div className="h-10 animate-pulse rounded-xl" style={subtleSurfaceStyle} />
          <div className="h-52 animate-pulse rounded-xl" style={subtleSurfaceStyle} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((item) => (
            <div key={item} className="h-24 animate-pulse rounded-xl" style={subtleSurfaceStyle} />
          ))}
        </div>
      </div>
      <div className="mt-4 h-24 animate-pulse rounded-xl" style={subtleSurfaceStyle} />
    </div>
  </div>
);

const LightingPresetPicker = ({ disabled, onSelect, label = "打光预设", menuPlacement = "up", activeId = "", activeName = "" }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const menuVisibility = useDelayedUnmount(Boolean(open && menuPosition), 120);

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current || typeof window === "undefined") return;

    const rect = buttonRef.current.getBoundingClientRect();
    const width = Math.min(420, Math.max(280, window.innerWidth - 24));
    const height = Math.min(320, Math.max(180, window.innerHeight - 24));
    const maxLeft = Math.max(12, window.innerWidth - width - 12);
    const left = Math.min(Math.max(12, rect.right - width), maxLeft);

    let top = menuPlacement === "down"
      ? rect.bottom + 8
      : rect.top - height - 8;
    if (menuPlacement === "down" && top + height > window.innerHeight - 12) {
      top = rect.top - height - 8;
    }
    if (menuPlacement !== "down" && top < 12) {
      top = rect.bottom + 8;
    }
    top = Math.min(Math.max(12, top), Math.max(12, window.innerHeight - height - 12));

    setMenuPosition({ top, left, width, maxHeight: height });
  }, [menuPlacement]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (buttonRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const menu = menuVisibility.mounted && menuPosition && typeof document !== "undefined" ? createPortal(
    <div
      ref={menuRef}
      role="group"
      aria-label={label}
      className={`fixed grid grid-cols-2 gap-2 overflow-y-auto rounded-xl p-3 shadow-2xl mini-storyboard-menu-scrollbar ${menuVisibility.exiting ? "ui-popover-exit" : "ui-popover-enter"}`}
      style={{
        ...panelStyle,
        top: menuPosition.top,
        left: menuPosition.left,
        width: menuPosition.width,
        maxHeight: menuPosition.maxHeight,
        zIndex: 1000,
        backgroundColor: "var(--color-bg-secondary)",
      }}
    >
      {LIGHTING_PICKER_OPTIONS.map((preset) => {
        const active = preset.id === activeId;
        return <button
          key={preset.id}
          type="button"
          title={preset.prompt || preset.description}
          aria-pressed={active}
          onClick={() => {
            onSelect(preset);
            setOpen(false);
          }}
          className="min-h-12 cursor-pointer rounded-lg border p-2 text-left transition-colors duration-200 hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent-primary)]"
          style={{
            borderColor: active ? "var(--color-accent-primary)" : "var(--color-border-primary)",
            backgroundColor: active ? "var(--color-accent-light)" : "var(--color-bg-overlay)",
            color: active ? "var(--color-accent-primary)" : "var(--color-text-primary)",
          }}
        >
          <span className="flex items-center justify-between gap-2 text-xs font-semibold">
            {preset.name}
            {active && <Check size={13} aria-hidden="true" />}
          </span>
          <span className="mt-1 line-clamp-2 block text-[11px] leading-4" style={{ color: "var(--color-text-muted)" }}>
            {preset.prompt || preset.description}
          </span>
        </button>;
      })}
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative shrink-0">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label={`${label}：${activeName || "未选择"}`}
        className="inline-flex h-8 max-w-[170px] cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-3 text-xs font-medium transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent-primary)] disabled:cursor-not-allowed disabled:opacity-45"
        style={activeId ? {
          backgroundColor: "var(--color-accent-light)",
          border: "1px solid var(--color-accent-primary)",
          color: "var(--color-accent-primary)",
        } : ghostButtonStyle}
      >
        <Lightbulb size={14} />
        <span className="max-w-[120px] truncate">{activeName || label}</span>
        <ChevronDown size={13} className={`transition ${open ? "rotate-180" : ""}`} />
      </button>
      {menu}
    </div>
  );
};

const StoryboardEnhancementPicker = ({ disabled, selectedModes, onToggle }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const menuVisibility = useDelayedUnmount(Boolean(open && menuPosition), 120);
  const selected = normalizeStoryboardEnhancementModes(selectedModes);
  const selectedItems = STORYBOARD_ENHANCEMENT_MODES.filter((item) => selected.includes(item.id));
  const activeLabel = selectedItems.length === 0
    ? "无增强"
    : selectedItems.length === 1
      ? selectedItems[0].name
      : `已选 ${selectedItems.length} 项`;

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current || typeof window === "undefined") return;
    const rect = buttonRef.current.getBoundingClientRect();
    const width = Math.min(380, Math.max(300, window.innerWidth - 24));
    const height = Math.min(420, Math.max(220, window.innerHeight - 24));
    const maxLeft = Math.max(12, window.innerWidth - width - 12);
    const left = Math.min(Math.max(12, rect.right - width), maxLeft);
    let top = rect.bottom + 8;
    if (top + height > window.innerHeight - 12) top = rect.top - height - 8;
    top = Math.min(Math.max(12, top), Math.max(12, window.innerHeight - height - 12));
    setMenuPosition({ top, left, width, maxHeight: height });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (buttonRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const menu = menuVisibility.mounted && menuPosition && typeof document !== "undefined" ? createPortal(
    <div
      ref={menuRef}
      className={`fixed overflow-y-auto rounded-xl p-3 shadow-2xl mini-storyboard-menu-scrollbar ${menuVisibility.exiting ? "ui-popover-exit" : "ui-popover-enter"}`}
      style={{
        ...panelStyle,
        top: menuPosition.top,
        left: menuPosition.left,
        width: menuPosition.width,
        maxHeight: menuPosition.maxHeight,
        zIndex: 1000,
        backgroundColor: "var(--color-bg-secondary)",
      }}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>分镜增强模式</div>
          <div className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>可多选组合，建议一次 1-3 项。</div>
        </div>
        <button
          type="button"
          onClick={() => onToggle("none")}
          className="shrink-0 rounded-full px-3 py-1.5 text-xs transition hover:opacity-85"
          style={selected.length === 0 ? primaryButtonStyle : ghostButtonStyle}
        >
          清空
        </button>
      </div>
      <div className="space-y-2">
        {STORYBOARD_ENHANCEMENT_MODES.filter((item) => item.id !== "none").map((item) => {
          const active = selected.includes(item.id);
          return (
            <button
              key={item.id}
              type="button"
              aria-pressed={active}
              onClick={() => onToggle(item.id)}
              className="flex w-full items-start gap-3 rounded-lg border p-3 text-left transition hover:opacity-90"
              style={{
                borderColor: active ? "var(--color-accent-primary)" : "var(--color-border-primary)",
                backgroundColor: active ? "var(--color-accent-light)" : "var(--color-bg-overlay)",
              }}
            >
              <span
                className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border"
                style={{
                  borderColor: active ? "var(--color-accent-primary)" : "var(--color-border-primary)",
                  backgroundColor: active ? "var(--color-accent-primary)" : "transparent",
                  color: active ? "white" : "transparent",
                }}
              >
                <Check size={12} />
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>{item.name}</span>
                <span className="mt-1 block text-[11px] leading-4" style={{ color: "var(--color-text-muted)" }}>{item.description}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative shrink-0">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-9 max-w-[170px] items-center gap-2 whitespace-nowrap rounded-full px-3 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-45"
        style={selected.length > 0 ? primaryButtonStyle : inputSurfaceStyle}
        title={selectedItems.length ? selectedItems.map((item) => item.name).join("、") : "无增强"}
      >
        <Sparkles size={15} />
        <span className="max-w-[108px] truncate">{activeLabel}</span>
        <ChevronDown size={13} className={`transition ${open ? "rotate-180" : ""}`} />
      </button>
      {menu}
    </div>
  );
};

const MiniSettingsModal = ({ open, title, description, onClose, children, maxWidthClass = "max-w-2xl" }) => {
  const visibility = useDelayedUnmount(Boolean(open), 160);
  if (!visibility.mounted || typeof document === "undefined") return null;
  return createPortal(
    <div
      className={`fixed inset-0 z-[900] flex items-center justify-center bg-[var(--overlay-medium)] px-4 py-6 ${visibility.exiting ? "ui-backdrop-exit" : "ui-backdrop-enter"}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={`studio-glass-dialog flex max-h-[86vh] w-full ${maxWidthClass} flex-col overflow-hidden rounded-2xl shadow-2xl ${visibility.exiting ? "ui-modal-exit" : "ui-modal-enter"}`} style={{ ...panelStyle, backgroundColor: "var(--color-bg-secondary)" }}>
        <div className="flex shrink-0 items-start justify-between gap-4 px-5 py-4" style={{ borderBottom: "1px solid var(--color-border-primary)" }}>
          <div className="min-w-0">
            <div className="text-base font-semibold">{title}</div>
            {description && (
              <div className="mt-1 text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>{description}</div>
            )}
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full" style={ghostButtonStyle} aria-label="关闭">
            <X size={16} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-5 custom-scrollbar">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const ACTING_FILTER_OPTIONS = AI_ACTING_FILTERS.filter((item) => item.id !== "all");
const actingCardKey = (card = {}) => `${firstText(card.type)}:${Number(card.level) || 0}:${firstText(card.name)}`;
const actingCharacterKey = (character = {}) => firstText(
  character.id != null ? String(character.id) : "",
  character.sourceAssetId != null ? String(character.sourceAssetId) : "",
  character.imageUrl,
  character.name,
);
const actingCardMatchesFilter = (card = {}, filterId = "joy") => (
  card.type === filterId || (Array.isArray(card.tags) && card.tags.includes(filterId))
);
const ENGLISH_EMOTION_LABELS = {
  joy: "contained joy",
  anger: "anger",
  sad: "sadness",
  happy: "relaxed pleasure",
};
const ENGLISH_INTENSITY_LABELS = [
  "latent",
  "subtle",
  "clear",
  "strong",
  "restrained",
  "outburst",
  "aftershock",
];

const buildActingDirectionPrompt = ({ character, card, shotScope, overseas = false }) => {
  if (!character || !card) return "";
  const characterName = firstText(character.name, "人物");
  const shotIndex = shotScope === "segment" ? 0 : Number(shotScope) || 0;
  const scopeText = shotIndex > 0 ? `分镜${shotIndex}` : "整个片段";
  if (overseas) {
    const emotion = ENGLISH_EMOTION_LABELS[card.type] || "natural emotion";
    const intensity = ENGLISH_INTENSITY_LABELS[Number(card.level) || 0] || "subtle";
    const scope = shotIndex > 0 ? `shot ${shotIndex}` : "the whole segment";
    return [
      `${characterName} performance direction (${scope}):`,
      `Emotional target: ${emotion}, ${intensity}, readable but natural.`,
      "Let eye focus change first, followed by a slight shift in the lips, breathing and a short reaction pause.",
      "Keep the character's existing blocking, movement and prop actions from the script and storyboard.",
      "Do not add table slamming, throwing objects, standing up, walking, embracing or new prop interaction unless it is explicitly written in the original action.",
      "Avoid exaggerated facial acting, repetitive gestures and unrelated body movement.",
    ].join("\n");
  }
  return [
    `${characterName}人物表演（${scopeText}）：`,
    `情绪落点：${firstText(card.core, `${card.emotion}${card.name}`)}`,
    `眼神：${firstText(card.eyes, "眼神变化先于动作，保持自然聚焦。")}`,
    `面部：${firstText(card.mouth, "只做小幅、可读的嘴角与下颌变化。")}`,
    `呼吸与停顿：${firstText(card.breath, "用轻微呼吸变化和短暂停顿承接情绪。")}`,
    "动作边界：沿用原剧本和原分镜已有的站位、走位与道具动作；只补充眼神、嘴角、呼吸和反应停顿。不得新增拍桌、摔物、突然起身、突然走动、拥抱或道具互动，除非原文明确要求。",
    `避免：${firstText(card.avoid, "避免夸张表情、重复动作和与剧情无关的肢体行为。")}`,
  ].join("\n");
};

const actingDirectionMarkers = ({ characterName, shotIndex, overseas = false } = {}) => {
  const name = firstText(characterName, overseas ? "Character" : "人物");
  const normalizedShotIndex = Number(shotIndex) || 0;
  const scope = normalizedShotIndex > 0
    ? (overseas ? `Shot ${normalizedShotIndex}` : `分镜${normalizedShotIndex}`)
    : (overseas ? "Whole segment" : "本段");
  return overseas
    ? {
        start: `[Character Performance Direction: ${name} | ${scope}]`,
        end: `[End Character Performance Direction: ${name} | ${scope}]`,
      }
    : {
        start: `【人物表演指导：${name}｜${scope}】`,
        end: `【人物表演指导结束：${name}｜${scope}】`,
      };
};

const upsertActingDirectionPrompt = (basePrompt, direction = {}) => {
  const base = firstText(basePrompt);
  const performancePrompt = firstText(direction.prompt);
  if (!performancePrompt) return base;

  const { start, end } = actingDirectionMarkers(direction);
  const block = `${start}\n${performancePrompt}\n${end}`;
  const startIndex = base.indexOf(start);
  if (startIndex >= 0) {
    const endIndex = base.indexOf(end, startIndex + start.length);
    if (endIndex >= 0) {
      return `${base.slice(0, startIndex)}${block}${base.slice(endIndex + end.length)}`
        .replace(/\n{3,}/g, "\n\n")
        .trim();
    }
  }
  return [base, block].filter(Boolean).join("\n\n").trim();
};

const ActingDirectionModal = ({
  open,
  segment,
  characters = [],
  overseas = false,
  saving = false,
  onClose,
  onSave,
}) => {
  const segmentIdentity = firstText(
    segment?.id != null ? String(segment.id) : "",
    segment?.segmentIndex != null ? String(segment.segmentIndex) : "",
  );
  const characterSignature = characters.map(actingCharacterKey).join("|");
  const [characterId, setCharacterId] = useState("");
  const [filterId, setFilterId] = useState("joy");
  const [cardId, setCardId] = useState("");
  const [shotScope, setShotScope] = useState("segment");
  const [directionPrompt, setDirectionPrompt] = useState("");

  const visibleCards = useMemo(() => {
    const matches = AI_ACTING_CARDS.filter((card) => actingCardMatchesFilter(card, filterId));
    return matches.length ? matches : AI_ACTING_CARDS.slice(0, 7);
  }, [filterId]);
  const selectedCharacter = characters.find((item) => actingCharacterKey(item) === characterId) || characters[0] || null;
  const selectedCard = visibleCards.find((item) => actingCardKey(item) === cardId) || visibleCards[0] || null;

  useEffect(() => {
    if (!open) return;
    const initialFilter = "joy";
    const initialCards = AI_ACTING_CARDS.filter((card) => actingCardMatchesFilter(card, initialFilter));
    setCharacterId(actingCharacterKey(characters[0] || {}));
    setFilterId(initialFilter);
    setCardId(actingCardKey(initialCards[0] || AI_ACTING_CARDS[0] || {}));
    setShotScope("segment");
  }, [characterSignature, open, segmentIdentity]);

  useEffect(() => {
    if (!open || !selectedCharacter || !selectedCard) {
      setDirectionPrompt("");
      return;
    }
    setDirectionPrompt(buildActingDirectionPrompt({
      character: selectedCharacter,
      card: selectedCard,
      shotScope,
      overseas,
    }));
  }, [cardId, characterId, filterId, open, overseas, shotScope]);

  const chooseFilter = (nextFilter) => {
    const matches = AI_ACTING_CARDS.filter((card) => actingCardMatchesFilter(card, nextFilter));
    setFilterId(nextFilter);
    setCardId(actingCardKey(matches[0] || AI_ACTING_CARDS[0] || {}));
  };

  const saveCurrentCue = async () => {
    if (!selectedCharacter || !selectedCard || !firstText(directionPrompt)) return;
    const shotIndex = shotScope === "segment" ? null : Number(shotScope) || null;
    const saved = await onSave?.({
      characterName: firstText(selectedCharacter.name, "人物"),
      shotIndex,
      overseas,
      prompt: directionPrompt.trim(),
    });
    if (saved) onClose?.();
  };

  return (
    <MiniSettingsModal
      open={open}
      title="人物表演指导"
      description="选择角色和情绪强度，生成后直接写入当前片段 Prompt，可继续手动编辑。"
      onClose={onClose}
      maxWidthClass="max-w-4xl"
    >
      <div className="space-y-5">
        <section>
          <div className="mb-2 text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>1. 选择角色</div>
          {characters.length ? (
            <div className="flex flex-wrap gap-2">
              {characters.map((character) => {
                const key = actingCharacterKey(character);
                const active = key === actingCharacterKey(selectedCharacter || {});
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setCharacterId(key)}
                    className="inline-flex h-11 max-w-[210px] items-center gap-2 rounded-lg px-2.5 text-left"
                    style={active ? primaryButtonStyle : inputSurfaceStyle}
                  >
                    {character.imageUrl ? <img src={character.imageUrl} alt="" className="h-7 w-7 shrink-0 rounded-md object-cover" /> : <Users size={16} />}
                    <span className="truncate text-xs font-medium">{character.displayName || character.name}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed px-4 py-5 text-center text-xs" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border-primary)" }}>
              本段还没有角色参考图。请先在参考图绑定中添加角色，再设置人物表演。
            </div>
          )}
        </section>

        <section>
          <div className="mb-2 text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>2. 选择情绪和强度</div>
          <div className="flex flex-wrap gap-2">
            {ACTING_FILTER_OPTIONS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                aria-pressed={filterId === filter.id}
                onClick={() => chooseFilter(filter.id)}
                className="h-8 rounded-full px-3 text-xs"
                style={filterId === filter.id ? primaryButtonStyle : ghostButtonStyle}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {visibleCards.map((card) => {
              const key = actingCardKey(card);
              const level = AI_ACTING_LEVELS.find((item) => Number(item.level) === Number(card.level));
              const active = key === actingCardKey(selectedCard || {});
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setCardId(key)}
                  className="min-h-[76px] rounded-lg px-3 py-2.5 text-left"
                  style={active ? { ...inputSurfaceStyle, borderColor: "var(--color-accent-primary)", backgroundColor: "var(--color-accent-light)" } : inputSurfaceStyle}
                >
                  <span className="block text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>{card.name}</span>
                  <span className="mt-1 block text-[11px]" style={{ color: "var(--color-text-muted)" }}>{level?.label || `强度 ${card.level}`} · {card.core}</span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-[180px_minmax(0,1fr)]">
          <label className="min-w-0">
            <span className="mb-2 block text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>3. 作用范围</span>
            <AppSelect value={shotScope} onChange={(event) => setShotScope(event.target.value)} className="h-10 w-full rounded-lg px-3 text-xs outline-none" style={inputSurfaceStyle}>
              <option value="segment">整个片段</option>
              {(segment?.shots || []).map((_, index) => <option key={index + 1} value={String(index + 1)}>仅分镜 {index + 1}</option>)}
            </AppSelect>
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>表演提示词</span>
            <textarea
              value={directionPrompt}
              onChange={(event) => setDirectionPrompt(event.target.value)}
              className="min-h-[190px] w-full resize-y rounded-lg px-3 py-3 text-xs leading-6 outline-none"
              style={inputSurfaceStyle}
              placeholder="选择角色和表情后生成，可继续手动修改。"
            />
          </label>
        </section>

        <div className="flex items-center justify-end gap-2 border-t pt-4" style={{ borderColor: "var(--color-border-primary)" }}>
          <button type="button" onClick={onClose} className="h-9 rounded-full px-4 text-xs" style={ghostButtonStyle}>取消</button>
          <button
            type="button"
            disabled={saving || !selectedCharacter || !firstText(directionPrompt)}
            onClick={saveCurrentCue}
            className="inline-flex h-9 items-center gap-2 rounded-full px-5 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-40"
            style={primaryButtonStyle}
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            写入 Prompt
          </button>
        </div>
      </div>
    </MiniSettingsModal>
  );
};

const MiniMenuItem = ({ icon, title, description, badge, danger = false, disabled = false, onClick, close }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => {
      if (disabled) return;
      close?.();
      onClick?.();
    }}
    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
    style={{
      backgroundColor: "var(--color-bg-overlay)",
      border: "1px solid var(--color-border-secondary)",
      color: danger ? "var(--color-error)" : "var(--color-text-primary)",
    }}
  >
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full" style={ghostButtonStyle}>{icon}</span>
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-2 text-xs font-semibold">
        <span className="truncate">{title}</span>
        {badge != null && (
          <span className="rounded-full px-1.5 py-0.5 text-[10px]" style={{ backgroundColor: "var(--color-accent-light)", color: "var(--color-accent-primary)" }}>
            {badge}
          </span>
        )}
      </span>
      {description && (
        <span className="mt-0.5 block truncate text-[11px]" style={{ color: danger ? "var(--color-error)" : "var(--color-text-muted)" }}>{description}</span>
      )}
    </span>
  </button>
);

const MiniActionMenu = ({ label = "更多", title, icon, disabled = false, active = false, children }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const menuVisibility = useDelayedUnmount(Boolean(open && menuPosition), 120);

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current || typeof window === "undefined") return;
    const rect = buttonRef.current.getBoundingClientRect();
    const width = Math.min(300, Math.max(240, window.innerWidth - 24));
    const maxLeft = Math.max(12, window.innerWidth - width - 12);
    const left = Math.min(Math.max(12, rect.right - width), maxLeft);
    const estimatedHeight = Math.min(460, Math.max(220, window.innerHeight - 24));
    let top = rect.bottom + 8;
    if (top + estimatedHeight > window.innerHeight - 12) top = rect.top - estimatedHeight - 8;
    top = Math.min(Math.max(12, top), Math.max(12, window.innerHeight - estimatedHeight - 12));
    setMenuPosition({ top, left, width, maxHeight: estimatedHeight });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (buttonRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const menu = menuVisibility.mounted && menuPosition && typeof document !== "undefined" ? createPortal(
    <div
      ref={menuRef}
      className={`fixed space-y-2 overflow-y-auto rounded-xl p-2 shadow-2xl mini-storyboard-menu-scrollbar ${menuVisibility.exiting ? "ui-popover-exit" : "ui-popover-enter"}`}
      style={{
        ...panelStyle,
        top: menuPosition.top,
        left: menuPosition.left,
        width: menuPosition.width,
        maxHeight: menuPosition.maxHeight,
        zIndex: 1000,
        backgroundColor: "var(--color-bg-secondary)",
      }}
    >
      {children(() => setOpen(false))}
    </div>,
    document.body,
  ) : null;

  return (
    <div className="relative shrink-0">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs disabled:cursor-not-allowed disabled:opacity-45"
        style={active ? primaryButtonStyle : ghostButtonStyle}
        title={title || label}
      >
        {icon || <MoreHorizontal size={15} />}
        <span>{label}</span>
        <ChevronDown size={13} className={`transition ${open ? "rotate-180" : ""}`} />
      </button>
      {menu}
    </div>
  );
};

const SideAccordionHeader = ({ open, controls, icon, title, subtitle, detail, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={open}
    aria-controls={controls}
    className="mini-workbench__side-accordion-trigger flex min-h-14 w-full cursor-pointer items-center justify-between gap-3 px-3 py-2 text-left transition-colors"
    style={{ color: "var(--color-text-primary)" }}
  >
    <span className="min-w-0">
      <span className="flex items-center gap-2 text-sm font-semibold">{icon}{title}</span>
      <span className="mt-0.5 block truncate text-xs" style={{ color: "var(--color-text-muted)" }}>
        {subtitle}
      </span>
    </span>
    <span className="flex shrink-0 items-center gap-2" style={{ color: "var(--color-text-muted)" }}>
      {detail && <span className="text-xs tabular-nums">{detail}</span>}
      <ChevronRight size={16} className="ui-accordion-chevron" data-open={open} aria-hidden="true" />
    </span>
  </button>
);

const SourceTextAccordion = ({ open, title, selectedTitle, text, page, pageKey, fallbackToEpisodeScript, onToggle, onPrevious, onNext }) => (
  <section className={`mini-workbench__panel mini-workbench__side-accordion mini-workbench__source-accordion flex min-h-0 flex-col overflow-hidden rounded-xl ${open ? "flex-1" : "shrink-0"}`} style={panelStyle}>
    <SideAccordionHeader
      open={open}
      controls="mini-storyboard-source-content"
      icon={<BookOpen size={16} />}
      title="查看原文"
      subtitle={open ? title : selectedTitle}
      detail={open && page.total > 0 ? `${page.index + 1} / ${page.total}` : ""}
      onToggle={onToggle}
    />
    <div id="mini-storyboard-source-content" className={`ui-accordion-grid min-h-0 ${open ? "flex-1" : ""}`} data-open={open} aria-hidden={!open} inert={!open}>
      <div className="ui-accordion-content flex min-h-0 flex-col" style={{ borderTop: "1px solid var(--color-border-primary)" }}>
        {page.total > 0 && (
          <nav className="flex items-center justify-between gap-1 px-3 py-1" aria-label="分镜原文翻页">
        <button
          type="button"
          onClick={onPrevious}
          disabled={page.index <= 0}
          className="inline-flex h-11 items-center gap-1 rounded-full px-2 text-xs transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
          style={ghostButtonStyle}
          aria-label="查看上一分镜原文"
        >
          <ChevronLeft size={15} />上一分镜
        </button>
        <span className="shrink-0 text-xs tabular-nums" style={{ color: "var(--color-text-muted)" }} aria-live="polite">
          {page.index + 1} / {page.total}
        </span>
        <button
          type="button"
          onClick={onNext}
          disabled={page.index >= page.total - 1}
          className="inline-flex h-11 items-center gap-1 rounded-full px-2 text-xs transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
          style={ghostButtonStyle}
          aria-label="查看下一分镜原文"
        >
          下一分镜<ChevronRight size={15} />
        </button>
          </nav>
        )}
        {fallbackToEpisodeScript && (
          <div className="mx-3 mt-2 rounded-lg px-3 py-2 text-xs leading-5" style={subtleSurfaceStyle}>
            当前分镜没有单独保存原文，以下显示本集完整剧本。
          </div>
        )}
        <div key={pageKey || "episode"} className="mini-workbench__source-scroll overflow-y-auto px-4 py-3 custom-scrollbar">
          <pre className="whitespace-pre-wrap break-words text-sm leading-7" style={{ color: "var(--color-text-secondary)", fontFamily: "inherit" }}>
            {firstText(text, "暂无原文内容")}
          </pre>
        </div>
      </div>
    </div>
  </section>
);

const VideoHistoryDrawer = ({ items, stats, loading, switching, error, segment, onClose, onRetry, onSelect, onDownload }) => {
  const activeVideoUrl = firstText(segment?.videoUrl);
  const displayItems = useMemo(() => {
    const rows = Array.isArray(items) ? items : [];
    const totalAttempts = Math.max(Number(stats?.attemptCount) || 0, rows.length);
    const numberedRows = rows.map((item, index) => ({
      ...item,
      __attemptNumber: Math.max(1, totalAttempts - index),
    }));
    if (!activeVideoUrl) return numberedRows;
    const exists = rows.some((item) => sameVideoVersion(item, segment));
    if (exists) return numberedRows;
    return [
      {
        ...segment,
        id: `current-${segment?.segmentIndex || ""}`,
        videoUrl: activeVideoUrl,
        resultUrl: activeVideoUrl,
        status: "success",
        __currentPreview: true,
      },
      ...numberedRows,
    ];
  }, [activeVideoUrl, items, segment, stats?.attemptCount]);

  return (
    <aside
      className="absolute inset-x-4 bottom-4 top-[74px] z-30 flex min-h-0 flex-col rounded-xl shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-200"
      style={{ ...panelStyle, backgroundColor: "var(--color-bg-secondary)" }}
    >
      <div className="flex shrink-0 items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--color-border-primary)" }}>
        <div>
          <div className="text-sm font-semibold">生成历史</div>
          <div className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>
            {firstText(segment?.title, "当前片段")}
            {stats && (
              <span>
                {" · "}累计 {Number(stats.attemptCount) || 0} 次
                {" · "}成功 {Number(stats.successCount) || 0}
                {" · "}失败 {Number(stats.failedCount) || 0}
                {Number(stats.inProgressCount) > 0 ? ` · 进行中 ${Number(stats.inProgressCount)}` : ""}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full transition hover:opacity-80"
          style={ghostButtonStyle}
          aria-label="关闭生成历史"
        >
          <X size={15} />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3 custom-scrollbar">
        {loading ? (
          <div className="flex h-full items-center justify-center text-sm" style={{ color: "var(--color-text-muted)" }}>
            <Loader2 size={17} className="mr-2 animate-spin" />
            正在加载历史
          </div>
        ) : error ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-4 text-center text-xs leading-5" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border-secondary)" }}>
            <AlertTriangle size={22} style={{ color: "var(--color-error)" }} />
            <span>生成历史加载失败：{error}</span>
            <button type="button" onClick={onRetry} className="inline-flex h-8 items-center gap-1 rounded-full px-3" style={ghostButtonStyle}>
              <RefreshCw size={13} /> 重试
            </button>
          </div>
        ) : !displayItems.length ? (
          <div className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed p-4 text-center text-xs leading-5" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border-secondary)" }}>
            <History size={22} />
            <span className="mt-2">当前片段还没有可用生成历史</span>
          </div>
        ) : (
          <div className="space-y-2">
            {displayItems.map((item, index) => {
              const videoUrl = firstText(item.videoUrl, item.resultUrl, item.url);
              const active = sameVideoVersion(item, segment);
              const clickable = !switching && Boolean(videoUrl) && historyStatusLabel(item) === "成功";
              return (
                <div
                  key={item.id || item.internalTaskId || item.taskId || index}
                  role="button"
                  tabIndex={clickable ? 0 : -1}
                  aria-disabled={!clickable}
                  onClick={() => clickable && onSelect(item)}
                  onKeyDown={(event) => {
                    if (!clickable) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelect(item);
                    }
                  }}
                  className="group grid w-full grid-cols-[78px_minmax(0,1fr)] gap-3 rounded-lg border p-2 text-left transition"
                  style={{
                    backgroundColor: active ? "var(--color-accent-light)" : "var(--color-bg-overlay)",
                    borderColor: active ? "var(--color-accent-primary)" : "var(--color-border-secondary)",
                    cursor: clickable ? "pointer" : "not-allowed",
                    opacity: clickable ? 1 : 0.55,
                  }}
                >
                  <div className="relative h-24 overflow-hidden rounded-md" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                    {videoUrl ? (
                      <video src={videoUrl} preload="none" muted className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center" style={{ color: "var(--color-text-muted)" }}>
                        <Film size={22} />
                      </div>
                    )}
                    {active && <Check size={15} className="absolute bottom-2 right-2" style={{ color: "var(--color-accent-primary)" }} />}
                  </div>
                  <div className="min-w-0 py-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>
                        {item.__currentPreview ? "当前预览" : `第 ${item.__attemptNumber || 1} 次生成`}
                      </span>
                      <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px]" style={historyStatusStyle(item)}>
                        {historyStatusLabel(item)}
                      </span>
                    </div>
                    <div className="mt-1 truncate text-[11px]" style={{ color: "var(--color-text-muted)" }}>
                      {formatHistoryTime(item.finishedAt || item.createdAt)}
                    </div>
                    <div className="mt-2 line-clamp-2 text-xs leading-5" style={{ color: "var(--color-text-secondary)" }}>
                      {firstText(item.model, item.provider, "未知模型")}
                      {item.cost != null ? ` · ¥${Number(item.cost).toFixed(2)}` : ""}
                    </div>
                    {videoUrl && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onDownload?.(item);
                        }}
                        className="mt-2 inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-[11px] font-semibold transition hover:opacity-85"
                        style={ghostButtonStyle}
                      >
                        <Download size={12} />
                        下载此版本
                      </button>
                    )}
                    {item.errorMessage && (
                      <div className="mt-1 line-clamp-2 text-[11px]" style={{ color: "var(--color-error)" }}>
                        {item.errorMessage}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
};

const ReferenceBindingPanel = ({
  projectId,
  references,
  activeIndex,
  onReplace,
  onRemove,
  onAdd,
  onUpload,
  selectionMode = false,
  saving = false,
  uploading = false,
  ingestingUrls = new Set(),
  videoProvider = "guavideo",
  onIngest,
  onConfirmSelection,
  onCancelSelection,
  open = true,
  onToggle,
}) => {
  const usesOfficialLibrary = usesModelArkAssetLibrary(videoProvider);
  const registry = useModelArkAssets({
    projectId,
    projectProvider: videoProvider,
    references,
    enabled: usesOfficialLibrary,
    refreshIntervalMs: 15000,
    refreshOnFocus: true,
  });
  return (
  <div className="mini-workbench__reference-panel mt-3 shrink-0 overflow-hidden rounded-xl" style={subtleSurfaceStyle}>
    <div className="flex flex-wrap items-center justify-between gap-2 p-3">
      <button
        type="button"
        onClick={onToggle}
        className="min-w-28 flex-1 text-left"
        disabled={!onToggle}
      >
        <div className="flex min-w-0 items-center gap-2">
          <div className="truncate text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>参考图绑定</div>
          <span className="rounded-full px-2 py-0.5 text-[11px]" style={ghostButtonStyle}>{references.length}</span>
        </div>
        <div className="mt-0.5 truncate text-[11px]" style={{ color: "var(--color-text-muted)" }}>
          {selectionMode ? "在上方资产库多选参考图，点确定后保存" : "选择一项后，在上方资产库点击图片替换"}
        </div>
      </button>
      <div className="ml-auto flex shrink-0 items-center gap-1">
      {usesOfficialLibrary && (
        <button
          type="button"
          onClick={registry.refresh}
          disabled={registry.loading || registry.submitting}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full disabled:opacity-40"
          style={ghostButtonStyle}
          aria-label="刷新参考图当前库状态"
          title="刷新参考图在当前模型素材库中的登记状态"
        >
          <RefreshCw size={12} className={registry.loading ? "animate-spin" : ""} />
        </button>
      )}
      {selectionMode ? (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onUpload}
            disabled={saving || uploading}
            className="inline-flex h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 text-[11px] disabled:opacity-40"
            style={ghostButtonStyle}
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            上传
          </button>
          <button
            type="button"
            onClick={onCancelSelection}
            disabled={saving}
            className="inline-flex h-7 shrink-0 items-center whitespace-nowrap rounded-full px-2 text-[11px] disabled:opacity-40"
            style={ghostButtonStyle}
          >
            取消
          </button>
          <button
            type="button"
            onClick={onConfirmSelection}
            disabled={saving}
            className="inline-flex h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 text-[11px] font-medium disabled:opacity-40"
            style={primaryButtonStyle}
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            确定
          </button>
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={onUpload}
            disabled={uploading}
            className="inline-flex h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 text-[11px] disabled:opacity-40"
            style={ghostButtonStyle}
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            上传
          </button>
          <button
            type="button"
            onClick={onAdd}
            className="inline-flex h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 text-[11px]"
            style={ghostButtonStyle}
          >
            <Plus size={12} />
            添加
          </button>
          {onToggle && (
            <button
              type="button"
              onClick={onToggle}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
              style={ghostButtonStyle}
              aria-label={open ? "收起参考图绑定" : "展开参考图绑定"}
            >
              <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>
          )}
        </>
      )}
      </div>
    </div>
    <div className={`mini-workbench__reference-content ${open ? "max-h-56 opacity-100" : "max-h-0 opacity-0"}`} inert={!open}>
      <div className="max-h-48 space-y-2 overflow-y-auto px-3 pb-3 pr-2 custom-scrollbar">
        {usesOfficialLibrary && registry.error && (
          <p role="status" className="break-words text-[11px]" style={{ color: "var(--color-error)" }}>{registry.error}</p>
        )}
        {references.length ? references.map((item, index) => (
          <div
            key={`${item.id || item.url || item.displayName}-${index}`}
            className={`mini-workbench__reference-row flex gap-3 rounded-lg border p-2.5 transition-colors duration-200 ${usesOfficialLibrary ? "items-start" : "items-center"}`}
            style={getReferenceBindingRowStyle(item, videoProvider, activeIndex === index,
              usesOfficialLibrary ? getModelArkReferenceState(registry, item).status === 'Active' : undefined)}
          >
            {referenceMediaType(item) === "audio" ? (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md"><Music size={18} /></div>
            ) : referenceMediaType(item) === "video" ? (
              <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-md" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                {item.url ? (
                  <video src={item.url} className="h-full w-full object-cover" muted preload="none" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center" style={{ color: "var(--color-text-muted)" }}>
                    <Video size={15} />
                  </div>
                )}
                <span className="absolute bottom-0.5 right-0.5 rounded bg-[var(--overlay-medium)] p-0.5 text-white">
                  <Video size={9} />
                </span>
              </div>
            ) : (
              <HoverImagePreview
                src={item.url}
                title={`${getReferencePromptName(item, `图片${index + 1}`)}${item?.isVariant && firstText(item?.variantTitle) ? `（使用变体图：${firstText(item.variantTitle)}）` : ""}`}
                previewWidth={360}
                previewHeight={460}
              >
                <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-md" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                  {item.url ? (
                    <img src={item.url} alt={getReferencePromptName(item, `图片${index + 1}`)} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center" style={{ color: "var(--color-text-muted)" }}>
                      <ImageIcon size={15} />
                    </div>
                  )}
                  {!usesOfficialLibrary && <ReferenceAssetStatusBadge item={item} provider={videoProvider} />}
                </div>
              </HoverImagePreview>
            )}
            <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => (selectionMode ? onRemove(index) : onReplace(index))}
              disabled={!selectionMode && item?.referencePurpose === "blocking_reference"}
              className="w-full min-w-0 text-left disabled:cursor-default"
              title={item?.referencePurpose === "blocking_reference" ? "站位参考图由站位方案管理，点击右侧「站位关系」编辑" : (selectionMode ? `取消选择 ${referenceMarker(item, index)}` : `替换 ${referenceMarker(item, index)}`)}
            >
              <div className="truncate text-xs font-medium" style={{ color: "var(--color-text-primary)" }}>
	                {referenceMarker(item, index)} · {item.type || "参考"}
              </div>
              <div className="truncate text-[11px]" style={{ color: "var(--color-text-muted)" }}>
                {getReferencePromptName(item, `图片${index + 1}`)}
                {item?.isVariant && firstText(item?.variantTitle) ? ` · 变体图 ${firstText(item.variantTitle)}` : ""}
              </div>
            </button>
            {usesOfficialLibrary && (
              <ModelArkReferenceStatus
                registry={registry}
                item={item}
                label={referenceMarker(item, index)}
                disabled={saving || uploading || selectionMode}
              />
            )}
            </div>
            {!usesOfficialLibrary && !selectionMode && item?.referencePurpose !== "blocking_reference" && (
              <ReferenceAssetIngestButton
                item={item}
                provider={videoProvider}
                loading={ingestingUrls.has(normalizeImageUrl(item?.url))}
                onIngest={onIngest}
              />
            )}
            {item?.referencePurpose === "blocking_reference" ? (
              <span
                className={`mini-workbench__reference-trailing-action inline-flex h-7 shrink-0 items-center whitespace-nowrap rounded-full px-2 text-[11px] ${usesOfficialLibrary ? "self-end" : ""}`}
                style={ghostButtonStyle}
                title="站位参考图由站位方案管理"
              >
                <Move3d size={11} className="mr-1" />
                站位
              </span>
            ) : (
            <button
              type="button"
              onClick={() => onRemove(index)}
              className={`mini-workbench__reference-trailing-action inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${usesOfficialLibrary ? "self-end" : ""}`}
              style={ghostButtonStyle}
              aria-label={selectionMode ? `取消选择 ${referenceMarker(item, index)}` : `移除 ${referenceMarker(item, index)}`}
              title={selectionMode ? `取消选择 ${referenceMarker(item, index)}` : `移除 ${referenceMarker(item, index)}`}
            >
              <X size={12} />
            </button>
            )}
          </div>
        )) : (
          <button
            type="button"
            onClick={onAdd}
            className="flex h-16 w-full items-center justify-center rounded-lg border border-dashed text-xs"
            style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border-secondary)" }}
          >
            <Plus size={14} className="mr-1" />
            {selectionMode ? "从上方资产库选择参考图" : "添加当前片段参考图"}
          </button>
        )}
      </div>
    </div>
  </div>
  );
};

const EpisodeIssueDrawer = ({
  issues,
  draft,
  loading,
  referenceUploading,
  selectedIssueId,
  onClose,
  onNew,
  onSelect,
  onChange,
  onSave,
  onDelete,
  onUploadReferenceImages,
}) => {
  const referenceInputRef = useRef(null);
  const [filters, setFilters] = useState({ priority: "all", type: "all", status: "all" });
  const selectedIssue = issues.find((item) => Number(item.id) === Number(selectedIssueId));
  const activeDraft = draft || selectedIssue || {};
  const priorityMeta = issuePriorityMeta(activeDraft.priority);
  const referenceImages = splitReferenceUrls(activeDraft.referenceImages);
  const filteredIssues = useMemo(() => (
    issues.filter((issue) => {
      const priority = firstText(issue.priority, "P2").toUpperCase();
      const type = firstText(issue.type, "story");
      const status = firstText(issue.status, "open");
      return (filters.priority === "all" || priority === filters.priority)
        && (filters.type === "all" || type === filters.type)
        && (filters.status === "all" || status === filters.status);
    })
  ), [filters.priority, filters.status, filters.type, issues]);
  const openCount = issues.filter((issue) => !["resolved", "ignored"].includes(firstText(issue.status, "open").toLowerCase())).length;
  const p0p1Count = issues.filter((issue) => ["P0", "P1"].includes(firstText(issue.priority, "P2").toUpperCase())).length;
  const setFilter = useCallback((key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  }, []);
  const filterActive = filters.priority !== "all" || filters.type !== "all" || filters.status !== "all";

  return (
    <aside
      className="absolute inset-0 z-50 grid grid-cols-[340px_minmax(0,1fr)] overflow-hidden rounded-2xl shadow-2xl animate-in fade-in zoom-in-95 duration-200"
      style={{ ...panelStyle, backgroundColor: "var(--color-bg-secondary)" }}
    >
      <div className="flex min-h-0 flex-col" style={{ borderRight: "1px solid var(--color-border-primary)" }}>
        <div className="shrink-0 px-4 py-4" style={{ borderBottom: "1px solid var(--color-border-primary)" }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-base font-semibold">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: "var(--color-accent-light)", color: "var(--color-accent-primary)" }}>
                  <MessageSquareWarning size={17} />
                </span>
                问题反馈台
              </div>
              <div className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>
                按优先级、类型和状态定位修改单
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
              style={ghostButtonStyle}
              aria-label="关闭问题反馈"
            >
              <X size={15} />
            </button>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2">
            <div className="rounded-xl px-3 py-2" style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-secondary)" }}>
              <div className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>总数</div>
              <div className="mt-1 text-lg font-semibold">{issues.length}</div>
            </div>
            <div className="rounded-xl px-3 py-2" style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-secondary)" }}>
              <div className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>待处理</div>
              <div className="mt-1 text-lg font-semibold" style={{ color: "var(--color-accent-primary)" }}>{openCount}</div>
            </div>
            <div className="rounded-xl px-3 py-2" style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-secondary)" }}>
              <div className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>高优先</div>
              <div className="mt-1 text-lg font-semibold" style={{ color: ISSUE_PRIORITIES[0].color }}>{p0p1Count}</div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2">
            <label className="min-w-0 text-[10px]" style={{ color: "var(--color-text-muted)" }}>
              优先级
              <AppSelect
                value={filters.priority}
                onChange={(event) => setFilter("priority", event.target.value)}
                className="mt-1 h-8 w-full rounded-lg px-2 text-xs outline-none"
                style={inputSurfaceStyle}
              >
                <option value="all">全部</option>
                {ISSUE_PRIORITIES.map((item) => <option key={item.id} value={item.id}>{item.id}</option>)}
              </AppSelect>
            </label>
            <label className="min-w-0 text-[10px]" style={{ color: "var(--color-text-muted)" }}>
              类型
              <AppSelect
                value={filters.type}
                onChange={(event) => setFilter("type", event.target.value)}
                className="mt-1 h-8 w-full rounded-lg px-2 text-xs outline-none"
                style={inputSurfaceStyle}
              >
                <option value="all">全部</option>
                {ISSUE_TYPES.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </AppSelect>
            </label>
            <label className="min-w-0 text-[10px]" style={{ color: "var(--color-text-muted)" }}>
              状态
              <AppSelect
                value={filters.status}
                onChange={(event) => setFilter("status", event.target.value)}
                className="mt-1 h-8 w-full rounded-lg px-2 text-xs outline-none"
                style={inputSurfaceStyle}
              >
                <option value="all">全部</option>
                {ISSUE_STATUSES.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </AppSelect>
            </label>
          </div>

          <div className="mt-3 flex items-center gap-2">
            {filterActive && (
              <button
                type="button"
                onClick={() => setFilters({ priority: "all", type: "all", status: "all" })}
                className="inline-flex h-9 items-center justify-center rounded-lg px-3 text-xs"
                style={ghostButtonStyle}
              >
                清除筛选
              </button>
            )}
            <button
              type="button"
              onClick={onNew}
              className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-lg text-xs font-semibold"
              style={primaryButtonStyle}
            >
              <Plus size={15} />
              新建反馈
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-3 custom-scrollbar">
          {loading ? (
            <div className="flex h-40 items-center justify-center text-sm" style={{ color: "var(--color-text-muted)" }}>
              <Loader2 size={17} className="mr-2 animate-spin" />
              正在加载
            </div>
          ) : !issues.length ? (
            <button
              type="button"
              onClick={onNew}
              className="flex h-44 w-full flex-col items-center justify-center rounded-xl border border-dashed text-center text-xs leading-5"
              style={{ ...subtleSurfaceStyle, color: "var(--color-text-muted)" }}
            >
              <AlertTriangle size={24} />
              <span className="mt-2">当前集还没有修改意见</span>
              <span>点击新建反馈，标记到粗剪时间线</span>
            </button>
          ) : !filteredIssues.length ? (
            <div className="flex h-44 flex-col items-center justify-center rounded-xl border border-dashed text-center text-xs leading-5" style={{ ...subtleSurfaceStyle, color: "var(--color-text-muted)" }}>
              <Search size={22} />
              <span className="mt-2">没有匹配的反馈</span>
              <button
                type="button"
                onClick={() => setFilters({ priority: "all", type: "all", status: "all" })}
                className="mt-3 inline-flex h-8 items-center rounded-lg px-3"
                style={ghostButtonStyle}
              >
                清除筛选
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredIssues.map((issue) => {
                const meta = issuePriorityMeta(issue.priority);
                const active = Number(issue.id) === Number(selectedIssueId);
                return (
                  <button
                    key={issue.id}
                    type="button"
                    onClick={() => onSelect(issue)}
                    className="w-full rounded-xl border p-3 text-left transition hover:opacity-90"
                    style={{
                      borderColor: active ? meta.color : "var(--color-border-secondary)",
                      backgroundColor: active ? meta.background : "var(--color-bg-overlay)",
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                        style={{ backgroundColor: meta.background, color: meta.color, border: `1px solid ${meta.color}` }}
                      >
                        {meta.name}
                      </span>
                      <span className="font-mono text-[11px]" style={{ color: "var(--color-text-muted)" }}>
                        {formatSeconds(issue.timecodeSeconds)}
                      </span>
                    </div>
                    <div className="mt-2 line-clamp-2 text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
                      {firstText(issue.title, "未命名问题")}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-[11px]" style={{ color: "var(--color-text-muted)" }}>
                      <span>{issueTypeName(issue.type)}</span>
                      <span>·</span>
                      <span>{issueStatusName(issue.status)}</span>
                    </div>
                    {firstText(issue.problem) && (
                      <div className="mt-2 line-clamp-2 text-xs leading-5" style={{ color: "var(--color-text-secondary)" }}>
                        {issue.problem}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex min-h-0 flex-col">
        <div className="flex shrink-0 items-center justify-between gap-3 px-5 py-4" style={{ borderBottom: "1px solid var(--color-border-primary)" }}>
          <div>
            <div className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>
              {activeDraft.id ? "编辑修改单" : "新建修改单"}
            </div>
            <div className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>
              保存后会在小故事板时间线上显示优先级标记
            </div>
          </div>
          <span
            className="rounded-full px-3 py-1 text-xs font-semibold"
            style={{ backgroundColor: priorityMeta.background, color: priorityMeta.color, border: `1px solid ${priorityMeta.color}` }}
          >
            {priorityMeta.name}
          </span>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-5 custom-scrollbar">
          <div className="grid grid-cols-3 gap-3">
            <label className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              优先级
              <AppSelect
                value={firstText(activeDraft.priority, "P2")}
                onChange={(event) => onChange({ priority: event.target.value })}
                className="mt-1 h-10 w-full rounded-lg px-3 outline-none"
                style={inputSurfaceStyle}
              >
                {ISSUE_PRIORITIES.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </AppSelect>
            </label>
            <label className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              类型
              <AppSelect
                value={firstText(activeDraft.type, "story")}
                onChange={(event) => onChange({ type: event.target.value })}
                className="mt-1 h-10 w-full rounded-lg px-3 outline-none"
                style={inputSurfaceStyle}
              >
                {ISSUE_TYPES.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </AppSelect>
            </label>
            <label className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              状态
              <AppSelect
                value={firstText(activeDraft.status, "open")}
                onChange={(event) => onChange({ status: event.target.value })}
                className="mt-1 h-10 w-full rounded-lg px-3 outline-none"
                style={inputSurfaceStyle}
              >
                {ISSUE_STATUSES.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </AppSelect>
            </label>
          </div>

          <div className="mt-4 grid grid-cols-[minmax(0,1fr)_140px] gap-3">
            <label className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              标题
              <input
                value={firstText(activeDraft.title)}
                onChange={(event) => onChange({ title: event.target.value })}
                className="mt-1 h-10 w-full rounded-lg px-3 outline-none"
                style={inputSurfaceStyle}
                placeholder="比如：00:45 雷电特效缺动作承接"
              />
            </label>
            <label className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              时间点（秒）
              <input
                type="number"
                min="0"
                value={Math.round(Number(activeDraft.timecodeSeconds) || 0)}
                onChange={(event) => onChange({ timecodeSeconds: Math.max(0, Number(event.target.value) || 0) })}
                className="mt-1 h-10 w-full rounded-lg px-3 outline-none"
                style={inputSurfaceStyle}
                placeholder="45"
              />
            </label>
          </div>

          <label className="mt-4 block text-xs" style={{ color: "var(--color-text-muted)" }}>
            问题描述
            <textarea
              value={firstText(activeDraft.problem)}
              onChange={(event) => onChange({ problem: event.target.value })}
              className="mt-1 min-h-28 w-full rounded-lg p-3 text-sm leading-6 outline-none"
              style={inputSurfaceStyle}
              placeholder="精确写出哪里不对，例如：徒弟挥剑动作缺失，导致雷电特效直接出现，动作承接跳。"
            />
          </label>

          <label className="mt-4 block text-xs" style={{ color: "var(--color-text-muted)" }}>
            修改建议
            <textarea
              value={firstText(activeDraft.suggestion)}
              onChange={(event) => onChange({ suggestion: event.target.value })}
              className="mt-1 min-h-32 w-full rounded-lg p-3 text-sm leading-6 outline-none"
              style={inputSurfaceStyle}
              placeholder="比如：徒弟双手举剑，巨剑上电光跃动，随后挥剑引雷劈向对手。"
            />
          </label>

          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>参考图</span>
              <button
                type="button"
                onClick={() => referenceInputRef.current?.click()}
                disabled={referenceUploading}
                className="inline-flex h-9 items-center gap-2 rounded-lg px-3 text-xs disabled:opacity-50"
                style={ghostButtonStyle}
              >
                {referenceUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                上传参考图
              </button>
              <input
                ref={referenceInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={async (event) => {
                  await onUploadReferenceImages?.(event.target.files);
                  event.target.value = "";
                }}
              />
            </div>
            {referenceImages.length ? (
              <div className="flex flex-wrap gap-2">
                {referenceImages.map((url, index) => (
                  <div key={`${url}-${index}`} className="group relative">
                    <HoverImagePreview src={url} title={`参考图 ${index + 1}`} previewWidth={360} previewHeight={420}>
                      <img src={url} alt={`参考图 ${index + 1}`} className="h-20 w-20 rounded-lg object-cover" style={{ border: "1px solid var(--color-border-primary)" }} />
                    </HoverImagePreview>
                    <button
                      type="button"
                      onClick={() => onChange({ referenceImages: referenceImages.filter((_, i) => i !== index) })}
                      className="absolute -right-2 -top-2 inline-flex h-6 w-6 items-center justify-center rounded-full opacity-0 transition group-hover:opacity-100"
                      style={{ ...ghostButtonStyle, backgroundColor: "var(--color-bg-overlay)" }}
                      aria-label={`移除参考图 ${index + 1}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => referenceInputRef.current?.click()}
                disabled={referenceUploading}
                className="flex h-24 w-full items-center justify-center rounded-xl border border-dashed text-xs disabled:opacity-50"
                style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border-secondary)" }}
              >
                {referenceUploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
                本地上传动作/特效/构图参考图
              </button>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center justify-between gap-3 px-5 py-4" style={{ borderTop: "1px solid var(--color-border-primary)" }}>
          <div className="flex items-center gap-2">
            {activeDraft.id && (
              <button
                type="button"
                onClick={() => onDelete(activeDraft)}
                className="inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm"
                style={{ ...ghostButtonStyle, color: "var(--color-error)", borderColor: "var(--color-error)" }}
              >
                <Trash2 size={15} />
                删除
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onSave}
            className="inline-flex h-10 items-center gap-2 rounded-lg px-5 text-sm font-semibold"
            style={primaryButtonStyle}
          >
            <Save size={15} />
            保存修改单
          </button>
        </div>
      </div>
    </aside>
  );
};

const MiniSegmentInsertDialog = ({
  open,
  insertIndex,
  generatedSegments = [],
  defaultSourceIndex,
  defaultFrameSecond = 0,
  loading,
  onClose,
  onConfirm,
}) => {
  const [mode, setMode] = useState(() => (generatedSegments.length ? "capture" : "blank"));
  const [sourceIndex, setSourceIndex] = useState(defaultSourceIndex || generatedSegments[0]?.segmentIndex || "");
  const [frameSecond, setFrameSecond] = useState(defaultFrameSecond || 0);
  const [framePreviews, setFramePreviews] = useState([]);
  const [framePreviewLoading, setFramePreviewLoading] = useState(() => {
    const initialSourceIndex = defaultSourceIndex || generatedSegments[0]?.segmentIndex || "";
    const initialSource = generatedSegments.find(
      (segment) => Number(segment.segmentIndex) === Number(initialSourceIndex),
    );
    return Boolean(open && generatedSegments.length && initialSource?.videoUrl);
  });
  const [framePreviewError, setFramePreviewError] = useState("");
  const [currentFramePreview, setCurrentFramePreview] = useState("");
  const [currentFrameLoading, setCurrentFrameLoading] = useState(false);
  const [mosaicFrameUrl, setMosaicFrameUrl] = useState("");
  const [mosaicEditorImage, setMosaicEditorImage] = useState("");
  const visibility = useDelayedUnmount(Boolean(open), 160);

  const resetFrameSelection = () => {
    setFramePreviews([]);
    setFramePreviewError("");
    setCurrentFramePreview("");
    setCurrentFrameLoading(false);
    setMosaicFrameUrl("");
    setMosaicEditorImage("");
  };

  const selectMode = (nextMode) => {
    setMode(nextMode);
    resetFrameSelection();
    setFramePreviewLoading(nextMode === "capture" && Boolean(sourceSegment?.videoUrl));
  };

  const selectSource = (nextSourceIndex) => {
    const nextSource = generatedSegments.find(
      (segment) => Number(segment.segmentIndex) === Number(nextSourceIndex),
    );
    setSourceIndex(nextSourceIndex);
    setFrameSecond(0);
    resetFrameSelection();
    setFramePreviewLoading(mode === "capture" && Boolean(nextSource?.videoUrl));
  };

  const selectFrame = (nextSecond) => {
    setFrameSecond(nextSecond);
    setCurrentFramePreview("");
    setCurrentFrameLoading(false);
    setMosaicFrameUrl("");
    setMosaicEditorImage("");
  };

  const sourceSegment = generatedSegments.find((segment) => Number(segment.segmentIndex) === Number(sourceIndex));
  const needsSource = mode === "capture" || mode === "extend" || mode === "extend_auto";
  const disabled = loading || (needsSource && !sourceSegment?.videoUrl);
  const sourceDuration = Math.max(0, Number(sourceSegment?.durationSeconds) || Number(sourceSegment?.duration) || 15);
  const selectedFramePreview = framePreviews.reduce((best, item) => (
    !best || Math.abs(Number(item.second) - Number(frameSecond)) < Math.abs(Number(best.second) - Number(frameSecond)) ? item : best
  ), null);

  useEffect(() => {
    if (!open || mode !== "capture" || !sourceSegment?.videoUrl) return undefined;
    let cancelled = false;
    buildVideoFramePreviewStrip(sourceSegment.videoUrl, sourceDuration, 10)
      .then((previews) => {
        if (cancelled) return;
        setFramePreviews(previews);
      })
      .catch((error) => {
        if (cancelled) return;
        setFramePreviews([]);
        setFramePreviewError(error.message || "视频帧预览生成失败");
      })
      .finally(() => {
        if (!cancelled) setFramePreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, open, sourceDuration, sourceSegment?.videoUrl]);

  useEffect(() => {
    if (!open || mode !== "capture" || !sourceSegment?.videoUrl) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setCurrentFrameLoading(true);
      captureVideoFrameToDataUrl(sourceSegment.videoUrl, Number(frameSecond) || 0, 720)
        .then((imageUrl) => {
          if (!cancelled) setCurrentFramePreview(imageUrl);
        })
        .catch(() => {
          if (!cancelled) setCurrentFramePreview("");
        })
        .finally(() => {
          if (!cancelled) setCurrentFrameLoading(false);
        });
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [frameSecond, mode, open, sourceSegment?.videoUrl]);

  if (!visibility.mounted) return null;

  return createPortal(
    <div className={`fixed inset-0 z-[9999] flex items-center justify-center bg-[var(--overlay-medium)] px-4 backdrop-blur-sm ${visibility.exiting ? "ui-backdrop-exit" : "ui-backdrop-enter"}`}>
      <div className={`w-full max-w-4xl overflow-hidden rounded-2xl border shadow-2xl ${visibility.exiting ? "ui-modal-exit" : "ui-modal-enter"}`} style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
        <div className="flex items-start justify-between gap-4 border-b p-5" style={{ borderColor: "var(--color-border-primary)" }}>
          <div>
            <div className="text-lg font-semibold" style={{ color: "var(--color-text-primary)" }}>
              增加分镜组
            </div>
            <div className="mt-1 text-sm" style={{ color: "var(--color-text-muted)" }}>
              将插入到第 {insertIndex} 个位置，后续分镜组会自动后移。
            </div>
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-full" style={ghostButtonStyle}>
            <X size={16} />
          </button>
        </div>

        <div className="grid gap-3 p-5 md:grid-cols-4">
          {[
            { id: "capture", icon: Camera, title: "截取帧", desc: "从已有视频截一帧，作为新段落第一个参考图。" },
            { id: "extend", icon: Video, title: "延长视频", desc: "把已有视频作为第一个参考视频，提示词默认延长 @视频1。" },
            { id: "extend_auto", icon: Sparkles, title: "延长视频+自动提示词", desc: "继承来源段落信息，自动生成更完整的延长提示词。" },
            { id: "blank", icon: Plus, title: "增加空段落", desc: "只创建空白段落，手动填写提示词和参考图。" },
          ].map((item) => {
            const Icon = item.icon;
            const active = mode === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => selectMode(item.id)}
                className="rounded-xl border p-4 text-left transition"
                style={{
                  backgroundColor: active ? "var(--color-accent-light)" : "var(--color-bg-overlay)",
                  borderColor: active ? "var(--color-accent-primary)" : "var(--color-border-secondary)",
                  color: "var(--color-text-primary)",
                }}
              >
                <Icon size={18} style={{ color: active ? "var(--color-accent-primary)" : "var(--color-text-muted)" }} />
                <div className="mt-3 text-sm font-semibold">{item.title}</div>
                <div className="mt-1 text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>{item.desc}</div>
              </button>
            );
          })}
        </div>

        {needsSource && (
          <div className="space-y-4 px-5 pb-5">
            <label className="block text-xs font-medium" style={{ color: "var(--color-text-secondary)" }}>
              来源视频段落
              <AppSelect
                value={sourceIndex}
                onChange={(event) => selectSource(event.target.value)}
                className="mt-2 h-10 w-full rounded-xl px-3 text-sm outline-none"
                style={inputSurfaceStyle}
              >
                {generatedSegments.map((segment) => (
                  <option key={segment.segmentIndex} value={segment.segmentIndex}>
                    分镜组 {String(segment.segmentIndex).padStart(2, "0")} · {firstText(segment.title, "已生成视频")}
                  </option>
                ))}
              </AppSelect>
            </label>
            {mode === "capture" && (
              <div className="rounded-xl p-3" style={subtleSurfaceStyle}>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="text-xs font-medium" style={{ color: "var(--color-text-secondary)" }}>截取视频帧</div>
                  <div className="font-mono text-xs" style={{ color: "var(--color-accent-primary)" }}>{Number(frameSecond || 0).toFixed(1)}s</div>
                </div>
                {(mosaicFrameUrl || currentFramePreview || selectedFramePreview?.imageUrl || currentFrameLoading) && (
                  <div className="mb-3 overflow-hidden rounded-lg" style={{ border: "1px solid var(--color-border-secondary)", backgroundColor: "var(--color-bg-primary)" }}>
                    {currentFrameLoading && !currentFramePreview ? (
                      <div className="flex h-48 items-center justify-center text-xs" style={{ color: "var(--color-text-muted)" }}>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        正在定位当前帧
                      </div>
                    ) : (
                      <img src={mosaicFrameUrl || currentFramePreview || selectedFramePreview.imageUrl} alt={mosaicFrameUrl ? "打码后的截帧预览" : "当前截帧预览"} className="h-48 w-full object-contain" />
                    )}
                  </div>
                )}
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setMosaicEditorImage(currentFramePreview || selectedFramePreview?.imageUrl || "")}
                    disabled={currentFrameLoading || !(currentFramePreview || selectedFramePreview?.imageUrl)}
                    className="inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-xs disabled:opacity-40"
                    style={ghostButtonStyle}
                  >
                    <SquareDashed size={13} />
                    打码当前帧
                  </button>
                  {mosaicFrameUrl && (
                    <>
                      <span className="rounded-full px-2 py-1 text-[11px]" style={{ backgroundColor: "color-mix(in srgb, var(--color-success) 16%, transparent)", color: "var(--color-success)", border: "1px solid color-mix(in srgb, var(--color-success) 35%, transparent)" }}>
                        已使用打码截帧
                      </span>
                      <button
                        type="button"
                        onClick={() => setMosaicFrameUrl("")}
                        className="inline-flex h-8 items-center rounded-full px-3 text-xs"
                        style={ghostButtonStyle}
                      >
                        还原原帧
                      </button>
                    </>
                  )}
                </div>
                <input
                  type="range"
                  min={0}
                  max={sourceDuration || 15}
                  step={0.1}
                  value={frameSecond}
                  onChange={(event) => selectFrame(event.target.value)}
                  className="w-full accent-[var(--color-accent-primary)]"
                />
                <div className="mt-3 flex gap-2 overflow-x-auto pb-1 custom-scrollbar">
                  {framePreviewLoading && Array.from({ length: 8 }).map((_, index) => (
                    <div key={index} className="h-20 w-14 shrink-0 animate-pulse rounded-lg" style={{ backgroundColor: "var(--color-bg-tertiary)" }} />
                  ))}
                  {!framePreviewLoading && framePreviews.map((item) => {
                    const active = Math.abs(Number(item.second) - Number(frameSecond)) < 0.25;
                    return (
                      <button
                        key={`${item.second}-${item.imageUrl}`}
                        type="button"
                        onClick={() => selectFrame(item.second)}
                        className="shrink-0 overflow-hidden rounded-lg border text-left transition hover:opacity-90"
                        style={{
                          width: 58,
                          borderColor: active ? "var(--color-accent-primary)" : "var(--color-border-secondary)",
                          backgroundColor: active ? "var(--color-accent-light)" : "var(--color-bg-overlay)",
                        }}
                        title={`${Number(item.second).toFixed(1)}s`}
                      >
                        <img src={item.imageUrl} alt={`${Number(item.second).toFixed(1)} 秒画面`} className="h-20 w-full object-cover" />
                        <div className="px-1 py-0.5 text-center font-mono text-[10px]" style={{ color: active ? "var(--color-accent-primary)" : "var(--color-text-muted)" }}>
                          {Number(item.second).toFixed(1)}s
                        </div>
                      </button>
                    );
                  })}
                  {!framePreviewLoading && framePreviewError && (
                    <div className="flex h-20 min-w-48 items-center rounded-lg px-3 text-xs leading-5" style={{ color: "var(--color-text-muted)", border: "1px dashed var(--color-border-secondary)" }}>
                      {framePreviewError}，可拖动上方时间轴手动选择。
                    </div>
                  )}
                </div>
              </div>
            )}
            {!generatedSegments.length && (
              <div className="rounded-xl border border-dashed p-4 text-sm" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border-secondary)" }}>
                当前还没有已生成视频，只能增加空段落。
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t p-5" style={{ borderColor: "var(--color-border-primary)" }}>
          <button type="button" onClick={onClose} className="h-9 rounded-full px-4 text-sm" style={ghostButtonStyle}>
            取消
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onConfirm({ mode, sourceSegment, frameSecond: Number(frameSecond) || 0, mosaicFrameUrl })}
            className="inline-flex h-9 items-center gap-2 rounded-full px-5 text-sm font-medium disabled:opacity-40"
            style={primaryButtonStyle}
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
            创建段落
          </button>
        </div>
      </div>
      {mosaicEditorImage && (
        <MosaicEditorModal
          imageUrl={mosaicEditorImage}
          zIndex={10000}
          onConfirm={(url) => {
            setMosaicFrameUrl(url);
            setMosaicEditorImage("");
          }}
          onClose={() => setMosaicEditorImage("")}
        />
      )}
    </div>,
    document.body,
  );
};

const MiniStoryboardWorkbench = ({ projectId, episodeId }) => {
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [episode, setEpisode] = useState(null);
  const [segments, setSegments] = useState([]);
  const segmentsRef = useRef([]);
  const [segmentsLoadReady, setSegmentsLoadReady] = useState(false);
  const [segmentsLoadError, setSegmentsLoadError] = useState("");
  const [episodeLoadError, setEpisodeLoadError] = useState("");
  const segmentsLoadedRef = useRef(false);
  const [selectedIndex, setSelectedIndex] = useState(1);
  const appliedSegmentLinkRef = useRef('');
  const [assets, setAssets] = useState({ characters: [], scenes: [], props: [] });
  const [fallbackScript, setFallbackScript] = useState("");
  const [assetType, setAssetType] = useState("all");
  const [assetQuery, setAssetQuery] = useState("");
  const [assetPage, setAssetPage] = useState(1);
  const [storyMode, setStoryMode] = useState("drama");
  const [storyboardEnhancementModes, setStoryboardEnhancementModes] = useState(() => {
    const savedList = localStorage.getItem("mini_storyboard_enhancement_modes");
    const parsedList = normalizeStoryboardEnhancementModes(savedList);
    if (parsedList.length) return parsedList;
    const saved = localStorage.getItem("mini_storyboard_enhancement_mode");
    const parsed = normalizeStoryboardEnhancementModes(saved);
    if (parsed.length) return parsed;
    return localStorage.getItem("mini_storyboard_director_deep") === "true" ? ["director_depth"] : [];
  });
  const stylePrompt = DEFAULT_STYLE;
  const userRequest = "";
  const [projectRuntimeTick, setProjectRuntimeTick] = useState(0);
  const defaultVideoProvider = useMemo(() => {
    void projectRuntimeTick;
    return getProjectVideoSubmissionProvider(projectId);
  }, [projectId, projectRuntimeTick]);
  const sd25Project = useMemo(() => {
    void projectRuntimeTick;
    return isSd25Project(projectId);
  }, [projectId, projectRuntimeTick]);
  const wan3Project = useMemo(() => {
    void projectRuntimeTick;
    return isWan3Project(projectId)
      || normalizeVideoProvider(defaultVideoProvider) === WANX_VIDEO_PROVIDER;
  }, [defaultVideoProvider, projectId, projectRuntimeTick]);
  const overseasProject = isOverseasVideoProvider(defaultVideoProvider);
  const videoModelOptions = useMemo(() => {
    if (wan3Project) return WAN3_VIDEO_MODELS;
    if (sd25Project) return [SEEDANCE25_VIDEO_MODEL, ...WAND_VEGA_MODELS];
    const projectModels = !overseasProject
      ? DOMESTIC_SEEDANCE_MODELS
      : defaultVideoProvider === VIDEO_PROVIDER_OMINI_SEEDANCE
      ? OMINI_VIDEO_MODELS
      : overseasProject ? OVERSEAS_VIDEO_MODELS : DOMESTIC_VIDEO_MODELS;
    return [...projectModels, SEEDANCE25_VIDEO_MODEL, ...WAND_VEGA_MODELS];
  }, [defaultVideoProvider, overseasProject, sd25Project, wan3Project]);
  const videoSettingsScopeKey = useMemo(
    () => getMiniVideoSettingsStorageKey(projectId, episodeId),
    [episodeId, projectId],
  );
  const videoSettingsReadinessKey = `${videoSettingsScopeKey}:${wan3Project}:${sd25Project}:${defaultVideoProvider}`;
  const [initialVideoSettings] = useState(() => (
    resolveMiniVideoSettings(projectId, episodeId, videoModelOptions)
  ));
  const [videoModel, setVideoModel] = useState(initialVideoSettings.videoModel);
  const [videoProvider, setVideoProvider] = useState(initialVideoSettings.videoProvider);
  const [videoResolution, setVideoResolution] = useState(initialVideoSettings.videoResolution);
  const [videoRatio, setVideoRatio] = useState(initialVideoSettings.videoRatio);
  const [videoSettingsReadyScope, setVideoSettingsReadyScope] = useState(videoSettingsReadinessKey);
  // 项目类型本身就是权威线路；即使本地旧设置还没被 effect 清理，也不能在首帧
  // 短暂按 Seedance 展示价格或提交语义。
  const wan3Selected = wan3Project
    || (normalizeVideoProvider(videoProvider) === WANX_VIDEO_PROVIDER && isWanxVideoModel(videoModel));
  const vegaSelected = videoProvider === WAND_VEGA_PROVIDER
    && isWandVegaModel(videoModel);
  const seedance25Selected = !wan3Selected && !vegaSelected
    && isSeedance25Provider(videoProvider)
    && videoModel === SEEDANCE25_MODEL;
  const seedance25Capabilities = getSeedance25Capabilities(videoProvider);
  const referenceImageLimit = vegaSelected
    ? WAND_VEGA_STORYBOARD_REFERENCE_LIMIT
    : wan3Selected
    ? WAN3_MAX_REFERENCE_IMAGES
    : seedance25Selected
    ? seedance25Capabilities.maxReferenceImages
    : 9;
  const activeVideoProvider = vegaSelected
    ? WAND_VEGA_PROVIDER
    : wan3Selected
    ? WANX_VIDEO_PROVIDER
    : seedance25Selected || isDomesticOfficialSeedance(videoProvider) ? videoProvider : defaultVideoProvider;
  const videoProviderLabel = getVideoProviderLabel(activeVideoProvider);
  const durationProfile = wan3Selected
    ? MINI_STORYBOARD_DURATION_PROFILE_WAN3
    : seedance25Selected || (vegaSelected && sd25Project)
    ? MINI_STORYBOARD_DURATION_PROFILE_SEEDANCE25
    : MINI_STORYBOARD_DURATION_PROFILE_STANDARD;
  const durationLimits = vegaSelected
    ? getWandVegaDurationLimits(videoModel)
    : wan3Selected
    ? { min: 2, max: WAN3_PLANNING_MAX_DURATION }
    : getMiniStoryboardDurationLimits(durationProfile);
  // 项目类型只决定 AI 自动拆分节奏；当前模型的范围只作为输入提示和
  // 新片段默认值，已有片段时长不会随模型切换而改变。
  const splitDurationSeconds = wan3Project
    ? WAN3_PLANNING_MAX_DURATION
    : sd25Project
    ? SEEDANCE25_PLANNING_MAX_DURATION
    : STANDARD_MINI_SEGMENT_MAX_SECONDS;
  // 当前模型的新建/缺省片段时长。厂商实际接收的秒数只在提交、
  // 计费和后处理边界转换，不参与编辑字段或 Prompt。
  const durationSeconds = wan3Selected
    ? WAN3_PLANNING_MAX_DURATION
    : seedance25Selected
    ? seedance25Capabilities.planningMaxDuration
    : vegaSelected && sd25Project
    ? SEEDANCE25_PLANNING_MAX_DURATION
    : STANDARD_MINI_SEGMENT_MAX_SECONDS;
  const [globalLightingPresetId, setGlobalLightingPresetId] = useState("");
  const [splitSettingsOpen, setSplitSettingsOpen] = useState(false);
  const [videoSettingsOpen, setVideoSettingsOpen] = useState(false);
  const [actingDirectionOpen, setActingDirectionOpen] = useState(false);
  const [actingDirectionSaving, setActingDirectionSaving] = useState(false);
  const aspectRatio = videoRatio;
  const [loading, setLoading] = useState(projectId && episodeId ? "load" : "");
  const [progressText, setProgressText] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const draftBaselineRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [playheadSeconds, setPlayheadSeconds] = useState(0);
  const [seekNonce, setSeekNonce] = useState(0);
  const [sidePanelSection, setSidePanelSection] = useState("assets");
  const sourceDrawerOpen = sidePanelSection === "source";
  const assetSectionOpen = sidePanelSection === "assets";
  const [sourceViewKey, setSourceViewKey] = useState("");
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [videoHistory, setVideoHistory] = useState([]);
  const [videoHistoryStats, setVideoHistoryStats] = useState(null);
  const [videoHistoryLoading, setVideoHistoryLoading] = useState(false);
  const [videoHistoryError, setVideoHistoryError] = useState("");
  const [historyLoadedSegmentKey, setHistoryLoadedSegmentKey] = useState("");
  const historyLoadRequestRef = useRef(0);
  const selectedHistorySegmentKeyRef = useRef("");
  const historyDrawerOpenRef = useRef(false);
  historyDrawerOpenRef.current = historyDrawerOpen;
  const [referenceReplaceIndex, setReferenceReplaceIndex] = useState(null);
  const [referenceTargetSegmentIndex, setReferenceTargetSegmentIndex] = useState(null);
  const [referenceSelectionDraft, setReferenceSelectionDraft] = useState(null);
  const [referenceUploading, setReferenceUploading] = useState(false);
  const [referenceAssetIngestingUrls, setReferenceAssetIngestingUrls] = useState(() => new Set());
  const [referenceDrawerOpen, setReferenceDrawerOpen] = useState(true);
  const [downloadingAllVideos, setDownloadingAllVideos] = useState(false);
  const [downloadingSegmentIndex, setDownloadingSegmentIndex] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [operationConfirm, setOperationConfirm] = useState(null);
  const [insertSegmentDialog, setInsertSegmentDialog] = useState(null);
  const [insertSegmentLoading, setInsertSegmentLoading] = useState(false);
  const [erasingSubtitleIndexes, setErasingSubtitleIndexes] = useState(() => new Set());
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [musicCues, setMusicCues] = useState([]);
  const [musicGenerating, setMusicGenerating] = useState(false);
  const [musicProgressText, setMusicProgressText] = useState("");
  const [musicEnabled, setMusicEnabled] = useState(true);
  const [musicVolume] = useState(0.22);
  const [issueDrawerOpen, setIssueDrawerOpen] = useState(false);
  const [revisionIssues, setRevisionIssues] = useState([]);
  const [revisionIssuesLoading, setRevisionIssuesLoading] = useState(false);
  const [issueReferenceUploading, setIssueReferenceUploading] = useState(false);
  const [selectedIssueId, setSelectedIssueId] = useState(null);
  const [issueDraft, setIssueDraft] = useState(null);
  const [assetDrawerOpen, setAssetDrawerOpen] = useState(false);
  const [blockingPlans, setBlockingPlans] = useState([]);
  const [blockingAssignments, setBlockingAssignments] = useState([]);
  const [blockingEditorOpen, setBlockingEditorOpen] = useState(false);
  const [blockingEditorPlan, setBlockingEditorPlan] = useState(null);
  const [workspaceSize, setWorkspaceSize] = useState({ width: 0, height: 0 });
  const [miniColumnPercents, setMiniColumnPercents] = useState(() => loadMiniWorkspaceColumnPercents(projectId));
  const workspaceRef = useRef(null);
  const miniWorkspaceGridRef = useRef(null);
  const miniPromptGridRef = useRef(null);
  const previewStageRef = useRef(null);
  const previewFrameRef = useRef(null);
  const referenceUploadInputRef = useRef(null);
  const previewVideoRef = useRef(null);
  const nextVideoPreloadRef = useRef(null);
  const musicAudioRef = useRef(null);
  const timelineScrollRef = useRef(null);
  const timelineDragbarRef = useRef(null);
  const pendingSeekRef = useRef(null);
  const activePollsRef = useRef(new Set());
  const stoppedVideoTaskIdsRef = useRef(new Set());
  const subtitlePollsRef = useRef(new Map());
  const assetLoadRequestRef = useRef(0);
  const assetLoadPromiseRef = useRef(null);
  const assetRefreshQueuedRef = useRef(false);
  const dataLoadRequestRef = useRef(0);
  const segmentMutationChainsRef = useRef(new Map());
  const mountedRef = useRef(true);
  const [timelineScrollMetrics, setTimelineScrollMetrics] = useState({ scrollLeft: 0, clientWidth: 0, scrollWidth: 0 });
  const { assetMetaMap, refreshStatusMap } = useAssetStatusMap(projectId, { provider: activeVideoProvider });
  const scopeKey = useMemo(() => `${projectId || ""}:${episodeId || ""}`, [episodeId, projectId]);
  const scopeKeyRef = useRef(scopeKey);
  scopeKeyRef.current = scopeKey;
  const isCurrentScope = useCallback((targetScopeKey) => mountedRef.current && scopeKeyRef.current === targetScopeKey, []);

  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);
  const segmentBelongsToCurrentScope = useCallback((segment) => {
    if (!segment) return false;
    const segmentProjectId = firstText(segment.projectId);
    const segmentEpisodeId = firstText(segment.episodeId);
    if (segmentProjectId && projectId && segmentProjectId !== String(projectId)) return false;
    if (segmentEpisodeId && episodeId && segmentEpisodeId !== String(episodeId)) return false;
    return true;
  }, [episodeId, projectId]);

  useEffect(() => {
    const activePolls = activePollsRef.current;
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      activePolls.clear();
    };
  }, []);

  useEffect(() => {
    const node = workspaceRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      const width = Math.round(rect.width);
      const height = Math.round(rect.height);
      setWorkspaceSize((current) => (
        current.width === width && current.height === height ? current : { width, height }
      ));
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setMiniColumnPercents(loadMiniWorkspaceColumnPercents(projectId));
  }, [projectId]);

  useEffect(() => {
    scopeKeyRef.current = scopeKey;
    assetLoadRequestRef.current += 1;
    assetLoadPromiseRef.current = null;
    assetRefreshQueuedRef.current = false;
    dataLoadRequestRef.current += 1;
    historyLoadRequestRef.current += 1;
    segmentsLoadedRef.current = false;
    activePollsRef.current.clear();
    subtitlePollsRef.current.forEach((stopPolling) => stopPolling?.());
    subtitlePollsRef.current.clear();
    setEpisode(null);
    segmentsRef.current = [];
    setSegments([]);
    setSegmentsLoadReady(false);
    setSegmentsLoadError("");
    setEpisodeLoadError("");
    setAssets({ characters: [], scenes: [], props: [] });
    setFallbackScript("");
    setMusicCues([]);
    setRevisionIssues([]);
    setBlockingPlans([]);
    setBlockingAssignments([]);
    setBlockingEditorOpen(false);
    setBlockingEditorPlan(null);
    setSelectedIssueId(null);
    setIssueDraft(null);
    setSelectedIndex(1);
    setSidePanelSection("assets");
    setSourceViewKey("");
    setPlayheadSeconds(0);
    setPlaying(false);
    setEditing(false);
    setDraft(null);
    draftBaselineRef.current = null;
    setHistoryDrawerOpen(false);
    setVideoHistory([]);
    setVideoHistoryStats(null);
    setVideoHistoryError("");
    setHistoryLoadedSegmentKey("");
    setReferenceReplaceIndex(null);
    setReferenceTargetSegmentIndex(null);
    setReferenceSelectionDraft(null);
    setReferenceUploading(false);
    setReferenceAssetIngestingUrls(new Set());
    setAssetsLoading(false);
    setVideoHistoryLoading(false);
    setDownloadingAllVideos(false);
    setDownloadingSegmentIndex(null);
    setMusicGenerating(false);
    setMusicProgressText("");
    setRevisionIssuesLoading(false);
    setIssueReferenceUploading(false);
    setInsertSegmentDialog(null);
    setInsertSegmentLoading(false);
    setErasingSubtitleIndexes(new Set());
    setDeleteTarget(null);
    setOperationConfirm(null);
    setProgressText("");
    setLoading(projectId && episodeId ? "load" : "");
  }, [episodeId, projectId, scopeKey]);

  useEffect(() => {
    const onProfileChange = (event) => {
      if (!projectId || String(event?.detail?.projectId) === String(projectId)) {
        setProjectRuntimeTick((value) => value + 1);
      }
    };
    window.addEventListener("project-runtime-profile-change", onProfileChange);
    return () => window.removeEventListener("project-runtime-profile-change", onProfileChange);
  }, [projectId]);

  useEffect(() => {
    if (videoSettingsReadyScope === videoSettingsReadinessKey) return;
    const nextSettings = resolveMiniVideoSettings(projectId, episodeId, videoModelOptions);
    setVideoModel(nextSettings.videoModel);
    setVideoProvider(nextSettings.videoProvider);
    setVideoResolution(nextSettings.videoResolution);
    setVideoRatio(nextSettings.videoRatio);
    setVideoSettingsReadyScope(videoSettingsReadinessKey);
  }, [episodeId, projectId, videoModelOptions, videoSettingsReadyScope, videoSettingsReadinessKey]);

  useEffect(() => {
    if (videoSettingsReadyScope !== videoSettingsReadinessKey) return;
    const currentIds = new Set(videoModelOptions.map((item) => item.id));
    if (!currentIds.has(videoModel)) {
      const nextModel = wan3Project
        ? normalizeWanxVideoModel(getProjectDefaultVideoModel(projectId))
        : getProjectDefaultVideoModel(projectId);
      setVideoModel(nextModel);
      setVideoProvider(wan3Project ? WANX_VIDEO_PROVIDER : defaultVideoProvider);
      setVideoResolution((current) => (
        wan3Project
          ? normalizeWanxVideoResolution(current, "1080P")
          : normalizeMiniStoryboardVideoResolution(current, nextModel)
      ));
    }
  }, [defaultVideoProvider, projectId, videoModel, videoModelOptions, videoSettingsReadyScope, videoSettingsReadinessKey, wan3Project]);

  useEffect(() => {
    if (videoSettingsReadyScope !== videoSettingsReadinessKey) return;
    if (isWandVegaModel(videoModel) || wan3Selected) {
      const selectedProvider = isWandVegaModel(videoModel)
        ? WAND_VEGA_PROVIDER : WANX_VIDEO_PROVIDER;
      setVideoProvider((current) => current === selectedProvider ? current : selectedProvider);
      setVideoResolution((current) => selectedProvider === WAND_VEGA_PROVIDER
        ? normalizeWandVegaResolution(current)
        : normalizeWanxVideoResolution(current, "1080P"));
      setVideoRatio((current) => selectedProvider === WAND_VEGA_PROVIDER
        ? normalizeWandVegaRatio(current)
        : normalizeWanxVideoRatio(current, normalizeWanxVideoRatio(getProjectDefaultVideoRatio(projectId), "adaptive")));
      return;
    }
    if (seedance25Selected) {
      setVideoResolution((current) => normalizeSeedance25Resolution(videoProvider, current));
      if (!seedance25Capabilities.ratios.includes(videoRatio)) {
        setVideoRatio("9:16");
      }
      return;
    }
    if (isDomesticOfficialSeedance(activeVideoProvider)) {
      setVideoResolution((current) => normalizeDomesticSeedanceResolution(videoModel, current));
      return;
    }
    if (!VIDEO_RATIOS.includes(videoRatio)) {
      setVideoRatio(getProjectDefaultVideoRatio(projectId));
    }
  }, [projectId, seedance25Capabilities, seedance25Selected, videoProvider, activeVideoProvider, videoModel, videoRatio, videoSettingsReadyScope, videoSettingsReadinessKey, wan3Project, wan3Selected]);

  useEffect(() => {
    if (videoSettingsReadyScope !== videoSettingsReadinessKey) return;
    if (projectId && !defaultVideoProvider) return;
    saveMiniVideoSettings(projectId, episodeId, {
      version: MINI_VIDEO_SETTINGS_VERSION,
      videoProvider: activeVideoProvider,
      videoModel: wan3Selected && !vegaSelected ? normalizeWanxVideoModel(videoModel) : videoModel,
      videoResolution: vegaSelected
        ? normalizeWandVegaResolution(videoResolution)
        : wan3Selected
        ? normalizeWanxVideoResolution(videoResolution, "1080P")
        : seedance25Selected
        ? normalizeSeedance25Resolution(activeVideoProvider, videoResolution)
        : videoResolution,
      videoRatio: vegaSelected
        ? normalizeWandVegaRatio(videoRatio)
        : wan3Selected
        ? normalizeWanxVideoRatio(videoRatio, "adaptive")
        : seedance25Selected
        ? normalizeSeedance25Ratio(activeVideoProvider, videoRatio, getProjectDefaultVideoRatio(projectId))
        : videoRatio,
    });
  }, [activeVideoProvider, defaultVideoProvider, episodeId, projectId, seedance25Capabilities, seedance25Selected, vegaSelected, videoModel, videoRatio, videoResolution, videoSettingsReadyScope, videoSettingsReadinessKey, wan3Selected]);

  const globalLightingPreset = useMemo(() => (
    LIGHTING_PRESETS.find((preset) => preset.id === globalLightingPresetId) || null
  ), [globalLightingPresetId]);
  const oneClickFlowLightingChoice = globalLightingPresetId === "none"
    ? LIGHTING_PICKER_OPTIONS[0]
    : globalLightingPreset;
  const globalLightingChoice = wan3Selected ? null : oneClickFlowLightingChoice;

  // Changing the video line can change which cards are eligible for global
  // lighting, but must not reload the board or reset the selected card/playhead.
  useEffect(() => {
    if (!segmentsLoadReady) return;
    setGlobalLightingPresetId(inferGlobalLightingPresetId(segmentsRef.current, wan3Selected));
  }, [segmentsLoadReady, wan3Selected]);

  const activeStoryMode = useMemo(() => (
    STORY_MODES.find((item) => item.id === storyMode) || STORY_MODES[0]
  ), [storyMode]);
  const storyboardEnhancementMode = storyboardEnhancementModes.length ? storyboardEnhancementModes.join(",") : "none";
  const directorDeepStoryboard = storyboardEnhancementModes.includes("director_depth");

  useEffect(() => {
    localStorage.setItem("mini_storyboard_enhancement_modes", JSON.stringify(storyboardEnhancementModes));
    localStorage.setItem("mini_storyboard_enhancement_mode", storyboardEnhancementMode);
    localStorage.setItem("mini_storyboard_director_deep", directorDeepStoryboard ? "true" : "false");
  }, [directorDeepStoryboard, storyboardEnhancementMode, storyboardEnhancementModes]);

  const toggleStoryboardEnhancementMode = useCallback((modeId) => {
    if (modeId === "none") {
      setStoryboardEnhancementModes([]);
      return;
    }
    setStoryboardEnhancementModes((prev) => {
      const current = normalizeStoryboardEnhancementModes(prev);
      if (current.includes(modeId)) return current.filter((id) => id !== modeId);
      return STORYBOARD_ENHANCEMENT_IDS.filter((id) => [...current, modeId].includes(id));
    });
  }, []);

  useEffect(() => {
    if (referenceReplaceIndex != null) {
      setReferenceDrawerOpen(true);
    }
  }, [referenceReplaceIndex]);

  const startMiniColumnResize = useCallback((mode, event) => {
    if (event.button != null && event.button !== 0) return;
    const container = mode === "inner" ? miniPromptGridRef.current : miniWorkspaceGridRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    if (!rect.width) return;

    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startPercents = normalizeMiniWorkspaceColumnPercents(miniColumnPercents);
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    const commit = (nextPercents) => {
      const normalized = normalizeMiniWorkspaceColumnPercents(nextPercents);
      setMiniColumnPercents(normalized);
      saveMiniWorkspaceColumnPercents(projectId, normalized);
    };

    const handleMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const next = [...startPercents];

      if (mode === "inner") {
        const pairTotal = startPercents[1] + startPercents[2];
        let minPrompt = (MINI_WORKSPACE_COLUMN_MIN_WIDTHS[1] / rect.width) * pairTotal;
        let minAsset = (MINI_WORKSPACE_COLUMN_MIN_WIDTHS[2] / rect.width) * pairTotal;
        const minTotal = minPrompt + minAsset;
        if (minTotal > pairTotal) {
          const scale = pairTotal / minTotal;
          minPrompt *= scale;
          minAsset *= scale;
        }
        const deltaPercent = (deltaX / rect.width) * pairTotal;
        const nextPrompt = clamp(startPercents[1] + deltaPercent, minPrompt, pairTotal - minAsset);
        next[1] = nextPrompt;
        next[2] = pairTotal - nextPrompt;
      } else {
        const minPreview = Math.min(55, (MINI_WORKSPACE_COLUMN_MIN_WIDTHS[0] / rect.width) * 100);
        const minRight = Math.min(80, ((MINI_WORKSPACE_COLUMN_MIN_WIDTHS[1] + MINI_WORKSPACE_COLUMN_MIN_WIDTHS[2] + 8) / rect.width) * 100);
        const deltaPercent = (deltaX / rect.width) * 100;
        const maxPreview = Math.max(minPreview, 100 - minRight);
        const nextPreview = clamp(startPercents[0] + deltaPercent, minPreview, maxPreview);
        const rightTotal = 100 - nextPreview;
        const startRightTotal = Math.max(1, startPercents[1] + startPercents[2]);
        const promptRatio = startPercents[1] / startRightTotal;
        next[0] = nextPreview;
        next[1] = rightTotal * promptRatio;
        next[2] = rightTotal - next[1];
      }

      commit(next);
    };

    const handleUp = () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerup", handleUp);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("pointermove", handleMove);
    document.addEventListener("pointerup", handleUp);
  }, [miniColumnPercents, projectId]);

  const timelineSegments = useMemo(() => {
    let cursor = 0;
    return segments.map((segment) => {
      const segmentDuration = Math.max(1, Number(segment.durationSeconds) || durationSeconds);
      const next = {
        ...segment,
        durationSeconds: segmentDuration,
        startSeconds: cursor,
        endSeconds: cursor + segmentDuration,
      };
      cursor += segmentDuration;
      return next;
    });
  }, [durationSeconds, segments]);

  const totalDuration = useMemo(() => (
    timelineSegments.length ? timelineSegments[timelineSegments.length - 1].endSeconds : 0
  ), [timelineSegments]);

  const timelineLayout = useMemo(() => {
    const byIndex = new Map();
    let cursor = TIMELINE_INSERT_WIDTH + TIMELINE_GAP;
    timelineSegments.forEach((segment) => {
      const duration = Math.max(1, Number(segment.durationSeconds) || durationSeconds);
      const width = getTimelineSegmentWidth(duration);
      byIndex.set(Number(segment.segmentIndex), {
        left: cursor,
        width,
        right: cursor + width,
      });
      cursor += width + TIMELINE_GAP + TIMELINE_INSERT_WIDTH + TIMELINE_GAP;
    });
    return {
      byIndex,
      width: timelineSegments.length ? Math.max(0, cursor - TIMELINE_GAP) : 0,
    };
  }, [durationSeconds, timelineSegments]);

  const updateTimelineScrollMetrics = useCallback(() => {
    const el = timelineScrollRef.current;
    if (!el) return;
    setTimelineScrollMetrics({
      scrollLeft: el.scrollLeft,
      clientWidth: el.clientWidth,
      scrollWidth: el.scrollWidth,
    });
  }, []);

  useLayoutEffect(() => {
    updateTimelineScrollMetrics();
    if (typeof window === "undefined") return undefined;
    window.addEventListener("resize", updateTimelineScrollMetrics);
    return () => window.removeEventListener("resize", updateTimelineScrollMetrics);
  }, [timelineLayout.width, timelineSegments.length, referenceDrawerOpen, updateTimelineScrollMetrics]);

  const timelineDragThumb = useMemo(() => {
    const { clientWidth, scrollWidth, scrollLeft } = timelineScrollMetrics;
    if (clientWidth <= 0 || scrollWidth <= clientWidth + 1) {
      return { visible: false, width: 0, left: 0 };
    }
    const width = Math.max(44, (clientWidth / scrollWidth) * clientWidth);
    const maxLeft = Math.max(0, clientWidth - width);
    const maxScrollLeft = Math.max(1, scrollWidth - clientWidth);
    return {
      visible: true,
      width,
      left: (scrollLeft / maxScrollLeft) * maxLeft,
    };
  }, [timelineScrollMetrics]);

  const scrollTimelineByDragbar = useCallback((clientX) => {
    const scrollEl = timelineScrollRef.current;
    const barEl = timelineDragbarRef.current;
    if (!scrollEl || !barEl || !timelineDragThumb.visible) return;

    const rect = barEl.getBoundingClientRect();
    const maxThumbLeft = Math.max(1, rect.width - timelineDragThumb.width);
    const nextThumbLeft = clamp(clientX - rect.left - timelineDragThumb.width / 2, 0, maxThumbLeft);
    scrollEl.scrollLeft = (nextThumbLeft / maxThumbLeft) * Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth);
    updateTimelineScrollMetrics();
  }, [timelineDragThumb.visible, timelineDragThumb.width, updateTimelineScrollMetrics]);

  const handleTimelineDragbarPointerDown = useCallback((event) => {
    if (!timelineDragThumb.visible) return;
    event.preventDefault();
    scrollTimelineByDragbar(event.clientX);

    const handleMove = (moveEvent) => scrollTimelineByDragbar(moveEvent.clientX);
    const handleUp = () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerup", handleUp);
    };
    document.addEventListener("pointermove", handleMove);
    document.addEventListener("pointerup", handleUp);
  }, [scrollTimelineByDragbar, timelineDragThumb.visible]);

  const downloadableSegments = useMemo(() => (
    timelineSegments
      .filter((segment) => isVideoSuccess(segment))
      .sort((a, b) => Number(a.segmentIndex || 0) - Number(b.segmentIndex || 0))
  ), [timelineSegments]);

  const generatedVideoSegments = useMemo(() => (
    downloadableSegments.filter((segment) => firstText(segment.videoUrl))
  ), [downloadableSegments]);

  const hasProcessingVideoSegments = useMemo(() => (
    timelineSegments.some((segment) => isVideoProcessing(segment))
    || erasingSubtitleIndexes.size > 0
  ), [erasingSubtitleIndexes, timelineSegments]);

  const findTimelineSegmentAt = useCallback((seconds) => {
    if (!timelineSegments.length) return null;
    const bounded = clamp(Number(seconds) || 0, 0, Math.max(0, totalDuration - 0.001));
    return timelineSegments.find((item) => bounded >= item.startSeconds && bounded < item.endSeconds)
      || timelineSegments[timelineSegments.length - 1];
  }, [timelineSegments, totalDuration]);

  const currentTimelineSegment = useMemo(() => (
    findTimelineSegmentAt(playheadSeconds)
  ), [findTimelineSegmentAt, playheadSeconds]);

  const selectedSegment = useMemo(() => (
    timelineSegments.find((item) => Number(item.segmentIndex) === Number(selectedIndex))
      || currentTimelineSegment
      || timelineSegments[0]
      || buildEmptySegment(1)
  ), [currentTimelineSegment, selectedIndex, timelineSegments]);
  const selectedHistorySegmentKey = getMiniStoryboardHistoryKey(projectId, episodeId, selectedSegment);
  selectedHistorySegmentKeyRef.current = selectedHistorySegmentKey;

  const blockingPlanById = useMemo(() => new Map(
    blockingPlans.map((plan) => [String(plan.id), plan]),
  ), [blockingPlans]);

  const blockingAssignmentBySegmentId = useMemo(() => new Map(
    blockingAssignments.map((assignment) => [String(assignment.segmentId), assignment]),
  ), [blockingAssignments]);

  const selectedBlockingPlan = useMemo(() => {
    const assignment = blockingAssignmentBySegmentId.get(String(selectedSegment?.id));
    return assignment ? blockingPlanById.get(String(assignment.planId)) || null : null;
  }, [blockingAssignmentBySegmentId, blockingPlanById, selectedSegment?.id]);

  const blockingTrackBlocks = useMemo(() => {
    const blocks = [];
    timelineSegments.forEach((segment) => {
      const assignment = blockingAssignmentBySegmentId.get(String(segment.id));
      const plan = assignment ? blockingPlanById.get(String(assignment.planId)) : null;
      if (!assignment || !plan) return;
      const previous = blocks[blocks.length - 1];
      if (previous && String(previous.planId) === String(plan.id)
        && Number(previous.endSeconds) === Number(segment.startSeconds)) {
        previous.endSeconds = segment.endSeconds;
        previous.segmentIds.push(segment.id);
        return;
      }
      blocks.push({
        planId: plan.id,
        plan,
        name: firstText(plan.name, "站位方案"),
        mappings: (Array.isArray(plan.actors) ? plan.actors : [])
          .map((actor) => `${firstText(actor?.colorName, "标记")}=${firstText(actor?.characterName, actor?.name, "人物")}`)
          .join("；"),
        referenceUrl: firstText(plan.modelReferenceUrl, plan.directorPreviewUrl),
        startSeconds: segment.startSeconds,
        endSeconds: segment.endSeconds,
        segmentIds: [segment.id],
      });
    });
    return blocks;
  }, [blockingAssignmentBySegmentId, blockingPlanById, timelineSegments]);

  const playheadPixel = useMemo(() => {
    if (!timelineSegments.length) return 0;
    if (playheadSeconds >= totalDuration) {
      const last = timelineSegments[timelineSegments.length - 1];
      return timelineLayout.byIndex.get(Number(last.segmentIndex))?.right || timelineLayout.width;
    }
    const segment = findTimelineSegmentAt(playheadSeconds);
    const layout = timelineLayout.byIndex.get(Number(segment?.segmentIndex));
    if (!segment || !layout) return 0;
    const duration = Math.max(1, Number(segment.durationSeconds) || durationSeconds);
    const ratio = clamp((playheadSeconds - segment.startSeconds) / duration, 0, 1);
    return layout.left + ratio * layout.width;
  }, [durationSeconds, findTimelineSegmentAt, playheadSeconds, timelineLayout, timelineSegments, totalDuration]);

  const nextTimelineSegment = useMemo(() => {
    if (!selectedSegment || !timelineSegments.length) return null;
    const index = timelineSegments.findIndex((item) => Number(item.segmentIndex) === Number(selectedSegment.segmentIndex));
    return index >= 0 ? timelineSegments[index + 1] || null : null;
  }, [selectedSegment, timelineSegments]);

  const nextPreloadUrl = firstText(nextTimelineSegment?.videoUrl);

  const activeMusicCue = useMemo(() => {
    if (!musicCues.length || !totalDuration) return null;
    const bounded = clamp(playheadSeconds, 0, Math.max(0, totalDuration - 0.001));
    return musicCues.find((cue) => (
      firstText(cue?.audioUrl)
      && bounded >= (Number(cue.startSeconds) || 0)
      && bounded < (Number(cue.endSeconds) || Number(cue.startSeconds) || 0)
    )) || null;
  }, [musicCues, playheadSeconds, totalDuration]);

  const musicTrackBlocks = useMemo(() => (
    musicCues
      .map((cue) => ({
        ...cue,
        startSeconds: Math.max(0, Number(cue.startSeconds) || 0),
        endSeconds: Math.max(Number(cue.startSeconds) || 0, Number(cue.endSeconds) || Number(cue.startSeconds) || 0),
      }))
      .filter((cue) => cue.endSeconds > cue.startSeconds)
  ), [musicCues]);

  const getTimelinePixelAtSeconds = useCallback((seconds) => {
    if (!timelineSegments.length) return 0;
    if (seconds >= totalDuration) {
      const last = timelineSegments[timelineSegments.length - 1];
      return timelineLayout.byIndex.get(Number(last.segmentIndex))?.right || timelineLayout.width;
    }
    const segment = findTimelineSegmentAt(seconds);
    const layout = timelineLayout.byIndex.get(Number(segment?.segmentIndex));
    if (!segment || !layout) return TIMELINE_INSERT_WIDTH;
    const duration = Math.max(1, Number(segment.durationSeconds) || durationSeconds);
    const ratio = clamp((seconds - segment.startSeconds) / duration, 0, 1);
    return layout.left + ratio * layout.width;
  }, [durationSeconds, findTimelineSegmentAt, timelineLayout, timelineSegments, totalDuration]);

  const activeReferenceSegment = useMemo(() => (
    editing && draft && Number(draft.segmentIndex) === Number(selectedSegment?.segmentIndex)
      ? draft
      : selectedSegment
  ), [draft, editing, selectedSegment]);

  const selectedReferenceItems = useMemo(() => (
    buildSegmentPromptWithReferences(activeReferenceSegment, assets, assetMetaMap, referenceImageLimit).references
  ), [activeReferenceSegment, assetMetaMap, assets, referenceImageLimit]);

  const selectedSubmissionPrompt = useMemo(() => {
    const prompt = buildSegmentPromptWithReferences(activeReferenceSegment, assets, assetMetaMap, referenceImageLimit).prompt;
    return isOneClickFlowSegment(activeReferenceSegment) ? prompt : stripStandaloneLightingBlocks(prompt);
  }, [activeReferenceSegment, assetMetaMap, assets, referenceImageLimit]);

  // 站位参考：显示层注入（参考图列表首位 + 提示词前加约束块，标记顺延一位；不影响持久化数据）
  const blockingDisplayItem = useMemo(() => (
    buildBlockingReferenceItem(selectedBlockingPlan, assetMetaMap)
  ), [assetMetaMap, selectedBlockingPlan]);

  const displayReferenceItems = useMemo(() => (
    blockingDisplayItem ? [blockingDisplayItem, ...selectedReferenceItems] : selectedReferenceItems
  ), [blockingDisplayItem, selectedReferenceItems]);

  const displaySubmissionPrompt = useMemo(() => {
    if (!blockingDisplayItem) return selectedSubmissionPrompt;
    const constraint = buildBlockingConstraintText(selectedBlockingPlan);
    const shifted = isOneClickFlowSegment(activeReferenceSegment)
      ? transformOneClickFlowPrompt(selectedSubmissionPrompt, (prompt) => shiftPromptReferenceMarkers(prompt, 1))
      : shiftPromptReferenceMarkers(selectedSubmissionPrompt, 1);
    return formatSeedancePrompt(`${constraint}\n\n${shifted}`);
  }, [activeReferenceSegment, blockingDisplayItem, selectedBlockingPlan, selectedSubmissionPrompt]);

  const actingCharacterOptions = useMemo(() => {
    const characterMap = new Map();
    selectedReferenceItems
      .filter((item) => groupKeyForReferenceType(item?.type) === "characters")
      .forEach((item) => {
        const name = getReferencePromptName(item, firstText(item?.displayName, item?.name, "人物"));
        const identity = firstText(
          item?.sourceAssetId != null ? String(item.sourceAssetId) : "",
          item?.id != null ? String(item.id) : "",
          item?.url,
          name,
        );
        const key = `${identity}:${normalizeReferenceText(name)}`;
        if (!characterMap.has(key)) {
          characterMap.set(key, {
            id: identity,
            sourceAssetId: item?.sourceAssetId,
            name,
            displayName: firstText(item?.assetDisplayName, item?.displayName, name),
            imageUrl: item?.url,
          });
        }
      });
    if (!characterMap.size) {
      toReferenceNameList(activeReferenceSegment?.assetRefs?.characters).forEach((name) => {
        const normalized = normalizeReferenceText(name);
        if (!normalized || characterMap.has(normalized)) return;
        characterMap.set(normalized, { id: normalized, name, displayName: name, imageUrl: "" });
      });
    }
    return [...characterMap.values()];
  }, [activeReferenceSegment?.assetRefs?.characters, selectedReferenceItems]);

  const referenceSelectionActive = Boolean(referenceSelectionDraft);
  const referencePanelItems = referenceSelectionActive ? (referenceSelectionDraft?.references || []) : displayReferenceItems;

  const beginReferenceReplace = useCallback((index) => {
    if (!activeReferenceSegment?.segmentIndex) return;
    setPlaying(false);
    setReferenceTargetSegmentIndex(Number(activeReferenceSegment.segmentIndex));
    setReferenceReplaceIndex(index);
    setReferenceSelectionDraft(null);
  }, [activeReferenceSegment]);

  const beginReferenceSelection = useCallback(() => {
    if (!activeReferenceSegment?.segmentIndex) return;
    const references = buildSegmentPromptWithReferences(activeReferenceSegment, assets, assetMetaMap, referenceImageLimit).references;
    setPlaying(false);
    setReferenceTargetSegmentIndex(Number(activeReferenceSegment.segmentIndex));
    setReferenceReplaceIndex(null);
    setReferenceSelectionDraft({
      segmentIndex: Number(activeReferenceSegment.segmentIndex),
      references,
    });
    setReferenceDrawerOpen(true);
    setSidePanelSection("assets");
    setAssetDrawerOpen(true);
  }, [activeReferenceSegment, assetMetaMap, assets, referenceImageLimit]);

  const resolveReferenceTargetSegment = useCallback(() => {
    const targetIndex = referenceTargetSegmentIndex || activeReferenceSegment?.segmentIndex;
    if (!targetIndex) return activeReferenceSegment;
    if (editing && draft && Number(draft.segmentIndex) === Number(targetIndex)) return draft;
    return segments.find((item) => Number(item.segmentIndex) === Number(targetIndex)) || activeReferenceSegment;
  }, [activeReferenceSegment, draft, editing, referenceTargetSegmentIndex, segments]);

  const selectedLightingPreset = useMemo(() => (
    findLightingPresetByPrompt(inferLightingPrompt(activeReferenceSegment))
  ), [activeReferenceSegment]);

  const selectedTimelineRange = formatSegmentTimelineRange(selectedSegment);
  const segmentToGenerate = editing && draft ? draft : selectedSegment;
  const currentGenerationPrompt = firstText(segmentToGenerate?.seedancePrompt);

  useEffect(() => {
    if (referenceReplaceIndex == null && !referenceSelectionDraft) {
      setReferenceTargetSegmentIndex(null);
    }
  }, [referenceReplaceIndex, referenceSelectionDraft, selectedSegment?.segmentIndex]);

  useEffect(() => {
    if (
      referenceSelectionDraft
      && selectedSegment?.segmentIndex
      && Number(referenceSelectionDraft.segmentIndex) !== Number(selectedSegment.segmentIndex)
    ) {
      setReferenceSelectionDraft(null);
      setReferenceTargetSegmentIndex(null);
    }
  }, [referenceSelectionDraft, selectedSegment?.segmentIndex]);

  const loadSegmentHistory = useCallback(async (segmentIndex, segmentId) => {
    if (!projectId || !episodeId || !segmentIndex) return;
    const requestScopeKey = scopeKey;
    const requestedSegment = { id: segmentId, segmentIndex };
    const requestedKey = getMiniStoryboardHistoryKey(projectId, episodeId, requestedSegment);
    const requestId = ++historyLoadRequestRef.current;
    const isCurrentHistoryRequest = () => (
      isCurrentScope(requestScopeKey)
      && requestId === historyLoadRequestRef.current
      && selectedHistorySegmentKeyRef.current === requestedKey
      && historyDrawerOpenRef.current
    );
    setVideoHistoryLoading(true);
    setVideoHistory([]);
    setVideoHistoryStats(null);
    setVideoHistoryError("");
    setHistoryLoadedSegmentKey("");
    try {
      const params = new URLSearchParams({
        projectId: String(projectId),
        episodeId: String(episodeId),
        segmentIndex: String(segmentIndex),
        limit: "20",
      });
      if (segmentId != null && String(segmentId).trim()) {
        params.set("segmentId", String(segmentId));
      }
      const result = await authenticatedFetch(`${API_HOST}/api/episode-mini-storyboard/video/history?${params.toString()}`);
      if (!isCurrentHistoryRequest()) return;
      if (result?.segmentId != null && String(result.segmentId) !== String(segmentId)) {
        throw new Error("历史记录与当前分镜不一致，请重试");
      }
      if (result?.segmentIndex != null && String(result.segmentIndex) !== String(segmentIndex)) {
        throw new Error("历史记录与当前分镜不一致，请重试");
      }
      const items = Array.isArray(result?.items) ? result.items : [];
      setVideoHistory(items.filter((item) => matchesMiniStoryboardHistoryItem(item, projectId, episodeId, requestedSegment)));
      setVideoHistoryStats({
        attemptCount: Number(result?.attemptCount ?? result?.count) || 0,
        successCount: Number(result?.successCount) || 0,
        failedCount: Number(result?.failedCount) || 0,
        inProgressCount: Number(result?.inProgressCount) || 0,
      });
      setHistoryLoadedSegmentKey(requestedKey);
    } catch (error) {
      if (!isCurrentHistoryRequest()) return;
      toast.error(error.message || "加载生成历史失败", 0);
      setVideoHistory([]);
      setVideoHistoryStats(null);
      setVideoHistoryError(error.message || "加载生成历史失败");
      setHistoryLoadedSegmentKey(requestedKey);
    } finally {
      if (isCurrentHistoryRequest()) setVideoHistoryLoading(false);
    }
  }, [episodeId, isCurrentScope, projectId, scopeKey, toast]);

  const openVideoHistory = useCallback(() => {
    setHistoryLoadedSegmentKey("");
    setHistoryDrawerOpen(true);
  }, []);

  useEffect(() => {
    if (historyDrawerOpen && selectedSegment?.segmentIndex) {
      loadSegmentHistory(selectedSegment.segmentIndex, selectedSegment.id);
    }
  }, [historyDrawerOpen, loadSegmentHistory, selectedSegment?.id, selectedSegment?.segmentIndex]);

  const allAssets = useMemo(() => {
    const groups = [
      ["characters", "角色", assets.characters || []],
      ["scenes", "场景", assets.scenes || []],
      ["props", "道具", assets.props || []],
    ];
    return groups
      .filter(([type]) => assetType === "all" || assetType === type)
      .flatMap(([type, label, list]) => list.map((asset) => ({ asset, type, label })))
      .filter(({ asset }) => assetMatches(asset, assetQuery));
  }, [assetQuery, assetType, assets]);

  const assetTotalPages = useMemo(() => (
    Math.max(1, Math.ceil(allAssets.length / ASSET_PAGE_SIZE))
  ), [allAssets.length]);

  const pagedAssets = useMemo(() => {
    const safePage = clamp(assetPage, 1, assetTotalPages);
    const start = (safePage - 1) * ASSET_PAGE_SIZE;
    return allAssets.slice(start, start + ASSET_PAGE_SIZE);
  }, [allAssets, assetPage, assetTotalPages]);

  useEffect(() => {
    setAssetPage((page) => clamp(page, 1, assetTotalPages));
  }, [assetTotalPages]);

  const loadAssets = useCallback(() => {
    if (!projectId) return Promise.resolve({ characters: [], scenes: [], props: [] });
    const requestScopeKey = scopeKey;
    const requestProjectId = projectId;
    const activeLoad = assetLoadPromiseRef.current;
    if (activeLoad?.scopeKey === requestScopeKey) return activeLoad.promise;

    const requestId = ++assetLoadRequestRef.current;
    setAssetsLoading(true);
    const loadPromise = (async () => {
      try {
        const candidates = await authenticatedFetch(`${API_HOST}/api/assets/candidates?projectId=${requestProjectId}`).catch(() => null);
        if (candidates && [candidates.characters, candidates.scenes, candidates.props].every(Array.isArray)) {
          const nextAssets = {
            characters: Array.isArray(candidates.characters) ? candidates.characters : [],
            scenes: Array.isArray(candidates.scenes) ? candidates.scenes : [],
            props: Array.isArray(candidates.props) ? candidates.props : [],
          };
          if (!isCurrentScope(requestScopeKey) || requestId !== assetLoadRequestRef.current) {
            return { characters: [], scenes: [], props: [] };
          }
          setAssets(nextAssets);
          return nextAssets;
        }

        let legacyGroups;
        try {
          legacyGroups = await Promise.all([
            authenticatedFetch(`${API_HOST}/api/characters?projectId=${requestProjectId}&summary=true`),
            authenticatedFetch(`${API_HOST}/api/scenes?projectId=${requestProjectId}&summary=true`),
            authenticatedFetch(`${API_HOST}/api/props?projectId=${requestProjectId}&summary=true`),
          ]);
        } catch (error) {
          throw new Error(`角色、场景或道具资源加载不完整，请重试后再拆分：${error?.message || "资源接口异常"}`);
        }
        const [characters, scenes, props] = legacyGroups;
        const nextAssets = {
          characters: Array.isArray(characters) ? characters : [],
          scenes: Array.isArray(scenes) ? scenes : [],
          props: Array.isArray(props) ? props : [],
        };
        if (!isCurrentScope(requestScopeKey) || requestId !== assetLoadRequestRef.current) {
          return { characters: [], scenes: [], props: [] };
        }
        setAssets(nextAssets);
        return nextAssets;
      } finally {
        if (isCurrentScope(requestScopeKey) && requestId === assetLoadRequestRef.current) setAssetsLoading(false);
      }
    })();
    const trackedPromise = loadPromise.finally(() => {
      if (assetLoadPromiseRef.current?.requestId === requestId) {
        assetLoadPromiseRef.current = null;
      }
    });
    assetLoadPromiseRef.current = { scopeKey: requestScopeKey, requestId, promise: trackedPromise };
    return trackedPromise;
  }, [isCurrentScope, projectId, scopeKey]);

  const getAssetsForUse = useCallback(async () => {
    if (!assets.characters.length && !assets.scenes.length && !assets.props.length) {
      return loadAssets();
    }
    return assets;
  }, [assets, loadAssets]);

  useEffect(() => {
    if (!projectId || !location.pathname.includes("mini-storyboard")) return;
    void loadAssets().catch(() => undefined);
  }, [loadAssets, location.pathname, projectId]);

  useEffect(() => {
    const handleAssetsChanged = (event) => {
      const detail = event?.detail || {};
      if (detail.projectId && String(detail.projectId) !== String(projectId)) return;
      if (assetLoadPromiseRef.current?.scopeKey === scopeKey) {
        assetRefreshQueuedRef.current = true;
        return;
      }
      void loadAssets().catch(() => undefined);
    };
    window.addEventListener("project-assets-change", handleAssetsChanged);
    window.addEventListener("asset-library-change", handleAssetsChanged);
    return () => {
      window.removeEventListener("project-assets-change", handleAssetsChanged);
      window.removeEventListener("asset-library-change", handleAssetsChanged);
    };
  }, [loadAssets, projectId, scopeKey]);

  useEffect(() => {
    if (assetsLoading || !assetRefreshQueuedRef.current) return;
    assetRefreshQueuedRef.current = false;
    void loadAssets().catch(() => undefined);
  }, [assetsLoading, loadAssets]);

  const loadMusicCues = useCallback(async () => {
    if (!projectId || !episodeId) return [];
    const requestScopeKey = scopeKey;
    const result = await fetchMiniStoryboardMusicCues(projectId, episodeId).catch(() => ({ cues: [] }));
    const cues = Array.isArray(result?.cues) ? result.cues : [];
    if (!isCurrentScope(requestScopeKey)) return [];
    setMusicCues(cues);
    return cues;
  }, [episodeId, isCurrentScope, projectId, scopeKey]);

  const loadRevisionIssues = useCallback(async () => {
    if (!projectId || !episodeId) return [];
    const requestScopeKey = scopeKey;
    setRevisionIssuesLoading(true);
    try {
      const result = await authenticatedFetch(`${API_HOST}/api/episode-revision-issues?projectId=${projectId}&episodeId=${episodeId}`);
      const issues = Array.isArray(result?.issues) ? result.issues : [];
      if (!isCurrentScope(requestScopeKey)) return [];
      setRevisionIssues(issues);
      if (issues.length) setSelectedIssueId((current) => current || issues[0].id);
      return issues;
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return [];
      toast.error(error.message || "加载问题反馈失败", 0);
      setRevisionIssues([]);
      return [];
    } finally {
      if (isCurrentScope(requestScopeKey)) setRevisionIssuesLoading(false);
    }
  }, [episodeId, isCurrentScope, projectId, scopeKey, toast]);

  const loadBlockingData = useCallback(async () => {
    if (!projectId || !episodeId) return { plans: [], assignments: [] };
    const requestScopeKey = scopeKey;
    const result = await fetchEpisodeBlocking(projectId, episodeId).catch(() => ({ plans: [], assignments: [] }));
    if (!isCurrentScope(requestScopeKey)) return { plans: [], assignments: [] };
    const next = {
      plans: Array.isArray(result?.plans) ? result.plans : [],
      assignments: Array.isArray(result?.assignments) ? result.assignments : [],
    };
    setBlockingPlans(next.plans);
    setBlockingAssignments(next.assignments);
    return next;
  }, [episodeId, isCurrentScope, projectId, scopeKey]);

  const loadData = useCallback(async () => {
    if (!projectId || !episodeId) return;
    const requestScopeKey = scopeKey;
    const requestId = ++dataLoadRequestRef.current;
    segmentsLoadedRef.current = false;
    setSegmentsLoadReady(false);
    setSegmentsLoadError("");
    setEpisodeLoadError("");
    setLoading("load");
    try {
      const [episodeListResult, saved, music, blocking] = await Promise.all([
        authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`)
          .then((rows) => {
            if (!Array.isArray(rows)) throw new Error("剧集列表返回格式异常");
            return { rows, error: "" };
          })
          .catch((error) => ({ rows: [], error: error.message || "剧集资料加载失败" })),
        authenticatedFetch(`${API_HOST}/api/episode-mini-storyboard/list?projectId=${projectId}&episodeId=${episodeId}`, { cache: "no-store" }),
        fetchMiniStoryboardMusicCues(projectId, episodeId).catch(() => ({ cues: [] })),
        fetchEpisodeBlocking(projectId, episodeId).catch(() => ({ plans: [], assignments: [] })),
      ]);
      if (!isCurrentScope(requestScopeKey) || requestId !== dataLoadRequestRef.current) return;
      if (!Array.isArray(saved?.segments)) {
        throw new Error("小故事板列表返回格式异常，无法确认已有分镜");
      }
      const currentEpisode = episodeListResult.rows.find((item) => String(item.id) === String(episodeId));
      setEpisodeLoadError(episodeListResult.error);
      setEpisode(currentEpisode || null);
      if (!normalizeEpisodeScript(currentEpisode)) {
        const loadedStoryboard = await authenticatedFetch(`${API_HOST}/api/storyboard/load?episodeId=${episodeId}`).catch(() => null);
        if (!isCurrentScope(requestScopeKey) || requestId !== dataLoadRequestRef.current) return;
        const storyboardRows = Array.isArray(loadedStoryboard) ? loadedStoryboard : [];
        setFallbackScript(storyboardRows.map((row) => firstText(
          row?.source_text,
          row?.sourceText,
          row?.visual_content,
          row?.visualContent,
          row?.content,
          row?.action,
        )).filter(Boolean).join("\n"));
      } else {
        setFallbackScript("");
      }
      void loadAssets().catch((error) => {
        if (isCurrentScope(requestScopeKey)) toast.warning(error.message || "素材加载失败，拆分前请重试");
      });
      const list = saved.segments;
      setSegments(list);
      segmentsRef.current = list;
      segmentsLoadedRef.current = true;
      setSegmentsLoadReady(true);
      setMusicCues(Array.isArray(music?.cues) ? music.cues : []);
      setBlockingPlans(Array.isArray(blocking?.plans) ? blocking.plans : []);
      setBlockingAssignments(Array.isArray(blocking?.assignments) ? blocking.assignments : []);
      void loadRevisionIssues();
      const loadedMode = firstText(list.find((item) => firstText(item?.storyMode))?.storyMode, saved?.storyMode);
      if (loadedMode) setStoryMode(loadedMode === "narration" ? "narration" : "drama");
      setGlobalLightingPresetId(inferGlobalLightingPresetId(list, wan3Project));
      setSelectedIndex(list[0]?.segmentIndex || 1);
      setPlayheadSeconds(0);
      setPlaying(false);
    } catch (error) {
      if (!isCurrentScope(requestScopeKey) || requestId !== dataLoadRequestRef.current) return;
      segmentsLoadedRef.current = false;
      setSegmentsLoadReady(false);
      setSegmentsLoadError(error.message || "小故事板加载失败");
      toast.error(error.message || "加载小故事板失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey) && requestId === dataLoadRequestRef.current) setLoading("");
    }
  }, [episodeId, isCurrentScope, loadAssets, loadRevisionIssues, projectId, scopeKey, toast, wan3Project]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => () => {
    subtitlePollsRef.current.forEach((stop) => {
      if (typeof stop === "function") stop();
    });
    subtitlePollsRef.current.clear();
  }, []);

  useEffect(() => {
    if (!timelineSegments.length) {
      setPlayheadSeconds(0);
      setPlaying(false);
      return;
    }
    setPlayheadSeconds((current) => clamp(current, 0, totalDuration));
  }, [timelineSegments.length, totalDuration]);

  useEffect(() => {
    if (!playing || !currentTimelineSegment) return;
    if (Number(currentTimelineSegment.segmentIndex) !== Number(selectedIndex)) {
      setSelectedIndex(currentTimelineSegment.segmentIndex);
    }
  }, [currentTimelineSegment, playing, selectedIndex]);

  useEffect(() => {
    if (!nextPreloadUrl || !nextVideoPreloadRef.current) return;
    nextVideoPreloadRef.current.load();
  }, [nextPreloadUrl]);

  useEffect(() => {
    if (musicAudioRef.current) {
      musicAudioRef.current.volume = clamp(musicVolume, 0, 1);
    }
  }, [musicVolume]);

  useEffect(() => {
    const audio = musicAudioRef.current;
    if (!audio) return;
    if (!playing || !musicEnabled || !activeMusicCue?.audioUrl) {
      audio.pause();
      return;
    }
    const cueStart = Number(activeMusicCue.startSeconds) || 0;
    const localTime = Math.max(0, playheadSeconds - cueStart);
    if (audio.src !== activeMusicCue.audioUrl) {
      audio.src = activeMusicCue.audioUrl;
    }
    if (Math.abs((audio.currentTime || 0) - localTime) > 1.2) {
      audio.currentTime = localTime;
    }
    audio.volume = clamp(musicVolume, 0, 1);
    const promise = audio.play();
    if (promise?.catch) {
      promise.catch(() => {});
    }
  }, [activeMusicCue?.audioUrl, activeMusicCue?.id, activeMusicCue?.startSeconds, musicEnabled, musicVolume, playing, playheadSeconds, seekNonce]);

  const hasUnsavedDraft = Boolean(editing && draft && draftBaselineRef.current
    && Number(draft.segmentIndex) === Number(draftBaselineRef.current.segmentIndex)
    && (draft.seedancePrompt !== draftBaselineRef.current.seedancePrompt
      || String(draft.durationSeconds ?? "") !== draftBaselineRef.current.durationSeconds));

  const seekToTimeline = useCallback((seconds, { autoPlay = playing } = {}) => {
    if (!timelineSegments.length) return;
    const nextTime = clamp(Number(seconds) || 0, 0, totalDuration);
    const target = findTimelineSegmentAt(nextTime);
    const shouldAutoPlay = Boolean(autoPlay && target?.videoUrl);
    const switchingSegment = target && Number(target.segmentIndex) !== Number(draft?.segmentIndex);

    const applySeek = () => {
      if (autoPlay && target && !target.videoUrl) {
        toast.warning(`分镜组 ${target.segmentIndex} 还没有视频，播放停在这里`);
      }
      pendingSeekRef.current = { seconds: nextTime, autoPlay: shouldAutoPlay };
      setPlayheadSeconds(nextTime);
      if (target) setSelectedIndex(target.segmentIndex);
      if (switchingSegment && editing) {
        setEditing(false);
        setDraft(null);
        draftBaselineRef.current = null;
      }
      setPlaying(shouldAutoPlay);
      if (!shouldAutoPlay) previewVideoRef.current?.pause();
      setSeekNonce((value) => value + 1);
    };

    if (switchingSegment && hasUnsavedDraft) {
      if (operationConfirm?.title === "未保存的分镜编辑") return;
      setPlaying(false);
      setOperationConfirm({
        title: "未保存的分镜编辑",
        message: "当前分镜的提示词或时长尚未保存。切换片段会丢弃这些修改；取消后可先保存。",
        confirmLabel: "丢弃并切换",
        onConfirm: () => {
          setOperationConfirm(null);
          applySeek();
        },
      });
      return;
    }
    applySeek();
  }, [draft?.segmentIndex, editing, findTimelineSegmentAt, hasUnsavedDraft, operationConfirm?.title, playing, timelineSegments.length, toast, totalDuration]);

  useEffect(() => {
    const segmentId = new URLSearchParams(location.search).get('segmentId');
    if (!segmentId || loading || !episode || !location.pathname.endsWith('/mini-storyboard')) return;
    const linkKey = `${scopeKey}:${location.key}:${segmentId}`;
    if (appliedSegmentLinkRef.current === linkKey) return;
    appliedSegmentLinkRef.current = linkKey;
    const target = findLinkedMiniStoryboardSegment(timelineSegments, location.search);
    if (!target) {
      toast.warning('链接中的片段已不存在，请在这一集的小故事板中重新选择');
      return;
    }
    seekToTimeline(target.startSeconds, { autoPlay: false });
  }, [episode, loading, location.key, location.pathname, location.search, scopeKey, seekToTimeline, timelineSegments, toast]);

  useEffect(() => {
    const pending = pendingSeekRef.current;
    if (!pending) return undefined;
    if (!selectedSegment?.videoUrl) {
      pendingSeekRef.current = null;
      return undefined;
    }

    const video = previewVideoRef.current;
    if (!video) return undefined;
    const localTime = clamp(
      pending.seconds - (Number(selectedSegment.startSeconds) || 0),
      0,
      Math.max(0, Number(selectedSegment.durationSeconds) || durationSeconds),
    );

    const applySeek = () => {
      video.currentTime = localTime;
      if (pending.autoPlay) {
        const playPromise = video.play();
        if (playPromise?.catch) {
          playPromise.catch(() => setPlaying(false));
        }
      }
    };

    pendingSeekRef.current = null;
    if (video.readyState >= 1) {
      applySeek();
      return undefined;
    }
    video.addEventListener("loadedmetadata", applySeek, { once: true });
    return () => video.removeEventListener("loadedmetadata", applySeek);
  }, [durationSeconds, seekNonce, selectedSegment?.durationSeconds, selectedSegment?.segmentIndex, selectedSegment?.startSeconds, selectedSegment?.videoUrl]);

  useEffect(() => {
    if (!playing) previewVideoRef.current?.pause();
  }, [playing, selectedSegment?.segmentIndex]);

  const handlePreviewTimeUpdate = useCallback(() => {
    const video = previewVideoRef.current;
    if (!video || !selectedSegment) return;
    const segmentDuration = Math.max(1, Number(selectedSegment.durationSeconds) || durationSeconds);
    const localTime = clamp(video.currentTime, 0, Math.max(0, segmentDuration - 0.05));
    const nextTime = (Number(selectedSegment.startSeconds) || 0) + localTime;
    setPlayheadSeconds(clamp(nextTime, 0, totalDuration));
  }, [durationSeconds, selectedSegment, totalDuration]);

  const handlePreviewEnded = useCallback(() => {
    if (!selectedSegment || !timelineSegments.length) {
      setPlaying(false);
      return;
    }
    const nextTime = Number(selectedSegment.endSeconds) || totalDuration;
    const nextSegment = timelineSegments.find((item) => item.startSeconds >= nextTime - 0.001
      && Number(item.segmentIndex) !== Number(selectedSegment.segmentIndex));

    if (!nextSegment) {
      setPlayheadSeconds(totalDuration);
      setPlaying(false);
      return;
    }

    if (hasUnsavedDraft) {
      setPlaying(false);
      toast.warning("当前分镜有未保存的修改，播放已停在片段末尾；保存后可继续播放");
      return;
    }
    if (!nextSegment.videoUrl) {
      seekToTimeline(nextSegment.startSeconds, { autoPlay: true });
      return;
    }
    seekToTimeline(nextSegment.startSeconds, { autoPlay: true });
  }, [hasUnsavedDraft, seekToTimeline, selectedSegment, timelineSegments, toast, totalDuration]);

  const toggleTimelinePlayback = useCallback(() => {
    if (!timelineSegments.length) return;
    if (playing) {
      previewVideoRef.current?.pause();
      setPlaying(false);
      return;
    }
    const startTime = playheadSeconds >= totalDuration ? 0 : playheadSeconds;
    const target = findTimelineSegmentAt(startTime);
    if (!target?.videoUrl) {
      if (target) {
        toast.warning(`分镜组 ${target.segmentIndex} 还没有视频，无法从这里播放`);
      }
      return;
    }
    seekToTimeline(startTime, { autoPlay: true });
  }, [findTimelineSegmentAt, playing, playheadSeconds, seekToTimeline, timelineSegments.length, toast, totalDuration]);

  const handleTimelineSegmentClick = useCallback((segment, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = rect.width > 0 ? clamp((event.clientX - rect.left) / rect.width, 0, 1) : 0;
    seekToTimeline(segment.startSeconds + ratio * (Number(segment.durationSeconds) || durationSeconds), { autoPlay: playing });
  }, [durationSeconds, playing, seekToTimeline]);

  const openRevisionReview = useCallback((issue = null) => {
    const issueTime = Number(issue?.timecodeSeconds);
    const requestedTime = Number.isFinite(issueTime) ? issueTime : playheadSeconds;
    navigate(`/project/${projectId}/episode/${episodeId}/review-feedback`, {
      state: {
        returnTo: location.pathname,
        timecodeSeconds: Math.max(0, requestedTime),
        issueId: issue?.id || null,
      },
    });
  }, [episodeId, location.pathname, navigate, playheadSeconds, projectId]);

  const openIssueDrawer = useCallback(() => {
    setIssueDrawerOpen(true);
    if (!issueDraft && !selectedIssueId) {
      setIssueDraft({
        projectId,
        episodeId,
        segmentIndex: selectedSegment?.segmentIndex || 1,
        timecodeSeconds: Math.round(playheadSeconds),
        priority: "P2",
        type: "story",
        status: "open",
        title: "",
        problem: "",
        suggestion: "",
        referenceImages: [],
      });
    }
  }, [episodeId, issueDraft, playheadSeconds, projectId, selectedIssueId, selectedSegment?.segmentIndex]);

  const newRevisionIssueDraft = useCallback(() => {
    setSelectedIssueId(null);
    setIssueDraft({
      projectId,
      episodeId,
      segmentIndex: selectedSegment?.segmentIndex || 1,
      timecodeSeconds: Math.round(playheadSeconds),
      priority: "P2",
      type: "story",
      status: "open",
      title: "",
      problem: "",
      suggestion: "",
      referenceImages: [],
    });
  }, [episodeId, playheadSeconds, projectId, selectedSegment?.segmentIndex]);

  const selectRevisionIssue = useCallback((issue) => {
    setSelectedIssueId(issue?.id || null);
    setIssueDraft({
      ...issue,
      referenceImages: splitReferenceUrls(issue?.referenceImages),
    });
    if (Number.isFinite(Number(issue?.timecodeSeconds))) {
      seekToTimeline(Number(issue.timecodeSeconds), { autoPlay: false });
    }
  }, [seekToTimeline]);

  const patchRevisionIssueDraft = useCallback((patch) => {
    setIssueDraft((current) => ({
      ...(current || {
        projectId,
        episodeId,
        segmentIndex: selectedSegment?.segmentIndex || 1,
        timecodeSeconds: Math.round(playheadSeconds),
        priority: "P2",
        type: "story",
        status: "open",
      }),
      ...patch,
    }));
  }, [episodeId, playheadSeconds, projectId, selectedSegment?.segmentIndex]);

  const uploadRevisionIssueReferenceImages = useCallback(async (fileList) => {
    const requestScopeKey = scopeKey;
    const files = Array.from(fileList || []).filter((file) => file?.type?.startsWith("image/"));
    if (!files.length) return;
    setIssueReferenceUploading(true);
    try {
      const uploadedUrls = await Promise.all(files.map((file) => uploadFileToCos(file)));
      if (!isCurrentScope(requestScopeKey)) return;
      setIssueDraft((current) => {
        const base = current || {
          projectId,
          episodeId,
          segmentIndex: selectedSegment?.segmentIndex || 1,
          timecodeSeconds: Math.round(playheadSeconds),
          priority: "P2",
          type: "story",
          status: "open",
        };
        return {
          ...base,
          referenceImages: [
            ...splitReferenceUrls(base.referenceImages),
            ...uploadedUrls.filter(Boolean),
          ].slice(0, 12),
        };
      });
      toast.success(`已上传 ${uploadedUrls.filter(Boolean).length} 张参考图`);
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      toast.error(error.message || "参考图上传失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) setIssueReferenceUploading(false);
    }
  }, [episodeId, isCurrentScope, playheadSeconds, projectId, scopeKey, selectedSegment?.segmentIndex, toast]);

  const saveRevisionIssue = useCallback(async () => {
    const requestScopeKey = scopeKey;
    const payload = {
      ...(issueDraft || {}),
      projectId,
      episodeId,
      timecodeSeconds: Math.max(0, Number(issueDraft?.timecodeSeconds) || 0),
      segmentIndex: Number(issueDraft?.segmentIndex) || selectedSegment?.segmentIndex || 1,
      referenceImages: splitReferenceUrls(issueDraft?.referenceImages),
      reason: "",
      title: firstText(issueDraft?.title, issueDraft?.problem, "未命名问题").slice(0, 120),
    };
    setRevisionIssuesLoading(true);
    try {
      const saved = await authenticatedFetch(`${API_HOST}/api/episode-revision-issues`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!isCurrentScope(requestScopeKey)) return;
      setRevisionIssues((current) => {
        const exists = current.some((item) => Number(item.id) === Number(saved.id));
        const next = exists
          ? current.map((item) => Number(item.id) === Number(saved.id) ? saved : item)
          : [...current, saved];
        return next.sort((a, b) => Number(a.timecodeSeconds || 0) - Number(b.timecodeSeconds || 0) || Number(a.id || 0) - Number(b.id || 0));
      });
      setSelectedIssueId(saved.id);
      setIssueDraft(saved);
      toast.success("问题反馈已保存");
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      toast.error(error.message || "保存问题反馈失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) setRevisionIssuesLoading(false);
    }
  }, [episodeId, isCurrentScope, issueDraft, projectId, scopeKey, selectedSegment?.segmentIndex, toast]);

  const deleteRevisionIssue = useCallback(async (issue) => {
    if (!issue?.id) return;
    if (!(await appConfirm(`确定删除「${firstText(issue.title, "未命名问题")}」吗？`))) return;
    const requestScopeKey = scopeKey;
    setRevisionIssuesLoading(true);
    try {
      await authenticatedFetch(`${API_HOST}/api/episode-revision-issues?id=${issue.id}&projectId=${projectId}&episodeId=${episodeId}`, {
        method: "DELETE",
      });
      if (!isCurrentScope(requestScopeKey)) return;
      setRevisionIssues((current) => current.filter((item) => Number(item.id) !== Number(issue.id)));
      setSelectedIssueId(null);
      setIssueDraft(null);
      toast.success("问题反馈已删除");
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      toast.error(error.message || "删除问题反馈失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) setRevisionIssuesLoading(false);
    }
  }, [episodeId, isCurrentScope, projectId, scopeKey, toast]);

  const mergeSegment = useCallback((segment, options = {}) => {
    if (!segment || !segmentBelongsToCurrentScope(segment)) return;
    setSegments((current) => {
      const exists = current.some((item) => Number(item.segmentIndex) === Number(segment.segmentIndex));
      const next = !exists
        ? [...current, segment].sort((a, b) => Number(a.segmentIndex) - Number(b.segmentIndex))
        : current.map((item) => {
        if (Number(item.segmentIndex) !== Number(segment.segmentIndex)) return item;
        if (!options.preserveEditableFields) return segment;
        return {
          ...segment,
          title: item.title ?? segment.title,
          durationSeconds: item.durationSeconds ?? segment.durationSeconds,
          timeRange: item.timeRange ?? segment.timeRange,
          location: item.location ?? segment.location,
          weather: item.weather ?? segment.weather,
          sourceText: item.sourceText ?? segment.sourceText,
          storyMode: item.storyMode ?? segment.storyMode,
          narrationText: item.narrationText ?? segment.narrationText,
          seedancePrompt: item.seedancePrompt ?? segment.seedancePrompt,
          shots: Array.isArray(item.shots) ? item.shots : segment.shots,
          spatial: item.spatial && typeof item.spatial === "object" ? item.spatial : segment.spatial,
          assetRefs: item.assetRefs && typeof item.assetRefs === "object" ? item.assetRefs : segment.assetRefs,
          lightingPrompt: item.lightingPrompt ?? segment.lightingPrompt,
        };
      });
      segmentsRef.current = next;
      return next;
    });
  }, [segmentBelongsToCurrentScope]);

  const pollSegmentVideo = useCallback(async (segmentIndex, expectedTaskId = "", expectedProvider = "") => {
    const taskId = firstText(expectedTaskId);
    const currentSegment = segmentsRef.current.find((item) => Number(item?.segmentIndex) === Number(segmentIndex));
    const pollingProvider = firstText(expectedProvider, currentSegment?.videoProvider, currentSegment?.provider,
      resolveSegmentSeedance25Provider(currentSegment), activeVideoProvider);
    if (getSeedance25Capabilities(pollingProvider)?.retired) {
      toast.warning("历史 LAS 线路已停用；重新生成请使用海外 Dreamina 代理。");
      return;
    }
    const usesDurablePolling = isDurableVideoPollingProvider(pollingProvider);
    const startedAt = Date.now();
    const requestScopeKey = scopeKey;
    const pollKey = `${segmentIndex}:${taskId || "unknown"}`;
    if (!projectId || !episodeId || activePollsRef.current.has(pollKey)) return;
    activePollsRef.current.add(pollKey);
    try {
      for (let i = 0; usesDurablePolling || i < 180; i += 1) {
        if (!isCurrentScope(requestScopeKey)) return;
        if (taskId && stoppedVideoTaskIdsRef.current.has(taskId)) return;
        if (Date.now() - startedAt >= VIDEO_POLLING_MAX_MS) {
          toast.warning(`分镜组 ${segmentIndex} 查询已达到120分钟，任务仍可能在后台生成，请稍后刷新查看`);
          return;
        }
        let updated;
        try {
          updated = await authenticatedFetch(`${API_HOST}/api/episode-mini-storyboard/video/status?projectId=${projectId}&episodeId=${episodeId}&segmentIndex=${segmentIndex}`);
        } catch (error) {
          if (!usesDurablePolling) throw error;
          await new Promise((resolve) => window.setTimeout(resolve, 15000));
          continue;
        }
        if (!isCurrentScope(requestScopeKey)) return;
        if (taskId && firstText(updated?.taskId) !== taskId) return;
        mergeSegment(updated, { preserveEditableFields: true });
        if (isVideoSuccess(updated) || isVideoFailed(updated)) return;
        await new Promise((resolve) => window.setTimeout(resolve, usesDurablePolling ? 15000 : i < 10 ? 5000 : 12000));
      }
    } catch (error) {
      if (isCurrentScope(requestScopeKey) && (!taskId || !stoppedVideoTaskIdsRef.current.has(taskId))) {
        toast.error(error.message || `分镜组 ${segmentIndex} 视频状态查询失败`, 0);
      }
    } finally {
      activePollsRef.current.delete(pollKey);
    }
  }, [activeVideoProvider, episodeId, isCurrentScope, mergeSegment, projectId, scopeKey, toast]);

  useEffect(() => {
    segments
      .filter((segment) => isVideoProcessing(segment) && segment?.taskId)
      .forEach((segment) => pollSegmentVideo(segment.segmentIndex, segment.taskId, segment.videoProvider || segment.provider));
  }, [pollSegmentVideo, segments]);

  const confirmBoardBeforeGeneration = async (requestScopeKey) => {
    await waitForPendingSegmentMutations();
    if (!isCurrentScope(requestScopeKey)) return null;
    if (!segmentsLoadedRef.current) {
      throw new Error("尚未确认当前集已有分镜，请重新加载后再拆分");
    }
    const board = await authenticatedFetch(
      `${API_HOST}/api/episode-mini-storyboard/list?projectId=${projectId}&episodeId=${episodeId}`,
      { cache: "no-store" },
    );
    if (!isCurrentScope(requestScopeKey)) return null;
    if (!Array.isArray(board?.segments) || !firstText(board?.boardRevision)) {
      throw new Error("后端尚未提供分镜版本校验，请更新后端后再拆分，避免覆盖已有内容");
    }
    if (!matchesMiniStoryboardBoardSnapshot(board.segments, segmentsRef.current)) {
      throw new Error("当前显示的分镜与服务器内容已不同，请先刷新小故事板并核对，再决定是否重新拆分");
    }
    return board;
  };

  const generatePromptSegments = async ({ silent = false } = {}) => {
    const requestScopeKey = scopeKey;
    const script = normalizeEpisodeScript(episode);
    if (!script) {
      toast.error("当前集没有剧本内容，先保存剧本再生成小故事板", 0);
      return [];
    }
    setProgressText("正在提交任务");
    try {
      const board = await confirmBoardBeforeGeneration(requestScopeKey);
      if (!board) return [];
      const assetGroupsForRequest = await getAssetsForUse();
      if (!isCurrentScope(requestScopeKey)) return [];
      const { result } = await runTaskToCompletion("EPISODE_MINI_STORYBOARD_GENERATE", {
        projectId,
        episodeId,
        expectedBoardRevision: board.boardRevision,
        ...(globalLightingChoice ? { defaultLightingPrompt: String(globalLightingChoice.prompt ?? "") } : {}),
        episodeTitle: firstText(episode?.title, episode?.name),
        script,
        aspectRatio,
        storyMode,
        storyboardEnhancementModes,
        storyboard_enhancement_modes: storyboardEnhancementModes,
        storyboardEnhancementMode,
        storyboard_enhancement_mode: storyboardEnhancementMode,
        directorDeepStoryboard,
        director_deep_storyboard: directorDeepStoryboard,
        style: stylePrompt,
        userRequest,
        assetNames: {
          // Send the complete name set. The backend performs script-aware, per-group
          // prioritization before applying its bounded planner catalog, so DB row order cannot
          // push a relevant scene/prop out of the first 80 entries.
          characters: (assetGroupsForRequest.characters || []).map(getAssetName).filter(Boolean),
          scenes: (assetGroupsForRequest.scenes || []).map(getAssetName).filter(Boolean),
          props: (assetGroupsForRequest.props || []).map(getAssetName).filter(Boolean),
        },
      }, {
        projectId,
        episodeId,
        source: "episode_mini_storyboard",
        timeoutMs: 30 * 60 * 1000,
        onProgress: (status) => {
          if (isCurrentScope(requestScopeKey)) {
            setProgressText(firstText(status?.stageText, status?.stage, status?.status, "任务执行中"));
          }
        },
      });
      if (!isCurrentScope(requestScopeKey)) return [];
      const list = Array.isArray(result?.segments) ? result.segments : [];
      // The backend stores the chosen lighting with the new segments in the same
      // transaction. A second client-side save could leave only some segments set.
      segmentsRef.current = list;
      setSegments(list);
      setSelectedIndex(list[0]?.segmentIndex || 1);
      setEditing(false);
      setDraft(null);
      draftBaselineRef.current = null;
      if (!isCurrentScope(requestScopeKey)) return [];
      setGlobalLightingPresetId(wan3Project ? "" : inferGlobalLightingPresetId(list));
      if (!silent) {
        toast.success(`已生成 ${list.length} 个${activeStoryMode.name}片段`);
      }
      return list;
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return [];
      toast.error(error.message || "生成小故事板失败", 0);
      return [];
    } finally {
      if (isCurrentScope(requestScopeKey)) setProgressText("");
    }
  };

  const executeGeneratePromptSegments = async () => {
    if (!segmentsLoadedRef.current) {
      toast.error("尚未确认当前集已有分镜，请先重新加载，不能直接拆分", 0);
      return;
    }
    const requestScopeKey = scopeKey;
    setLoading("script");
    try {
      await generatePromptSegments();
    } finally {
      if (isCurrentScope(requestScopeKey)) setLoading("");
    }
  };

  const requestGeneratePromptSegments = () => {
    if (!segmentsLoadedRef.current) {
      toast.error("小故事板列表尚未成功加载，请先重试后再拆分", 0);
      return;
    }
    const existingSegments = segments.filter((segment) => firstText(segment?.seedancePrompt) || segment?.shots?.length || isVideoSuccess(segment));
    if (existingSegments.length) {
      setOperationConfirm({
        title: "重新拆分分镜",
        message: `当前集已经有 ${existingSegments.length} 个小故事板分镜组。继续会重新拆分并覆盖现有分镜提示词，已生成的视频也会失去对应关系。确定继续？`,
        confirmLabel: "重新拆分",
        onConfirm: executeGeneratePromptSegments,
      });
      return;
    }
    executeGeneratePromptSegments();
  };

  // A separate entry point deliberately bypasses the existing mode/enhancement,
  // fallback and prompt-rewriting flow. Only asset matching, task transport and
  // the already-saved result display are shared with the original split feature.
  const executeGenerateOneClickFlowSegments = async () => {
    if (!segmentsLoadedRef.current) {
      toast.error("尚未确认当前集已有分镜，请先重新加载，不能直接使用一键流分镜", 0);
      return;
    }
    const requestScopeKey = scopeKey;
    const script = normalizeEpisodeScript(episode);
    if (!script) {
      toast.error("当前集没有剧本内容，先保存剧本再使用一键流分镜", 0);
      return;
    }
    setLoading("one-click-flow");
    setProgressText("正在提交一键流分镜任务");
    try {
      // A stale backend would ignore this new payload field and run the original
      // destructive split. Probe read-only support before creating any AI task.
      const board = await confirmBoardBeforeGeneration(requestScopeKey);
      if (!board) return;
      if (!supportsOneClickFlowRule(board)) {
        throw new Error("后端尚未启用一键流分镜，请重启或更新后端后重试");
      }
      const assetGroupsForRequest = await getAssetsForUse();
      if (!isCurrentScope(requestScopeKey)) return;
      const payload = buildOneClickFlowSplitPayload({
        projectId,
        episodeId,
        episodeTitle: firstText(episode?.title, episode?.name),
        script,
        aspectRatio,
        videoProvider: activeVideoProvider,
        videoModel,
        ...(oneClickFlowLightingChoice ? { defaultLightingPrompt: String(oneClickFlowLightingChoice.prompt ?? "") } : {}),
        assetNames: {
          characters: (assetGroupsForRequest.characters || []).map(getAssetName).filter(Boolean),
          scenes: (assetGroupsForRequest.scenes || []).map(getAssetName).filter(Boolean),
          props: (assetGroupsForRequest.props || []).map(getAssetName).filter(Boolean),
        },
      });
      payload.expectedBoardRevision = board.boardRevision;
      const { result } = await runTaskToCompletion("EPISODE_MINI_STORYBOARD_GENERATE", payload, {
        projectId,
        episodeId,
        source: "episode_mini_storyboard",
        timeoutMs: 30 * 60 * 1000,
        onProgress: (status) => {
          if (isCurrentScope(requestScopeKey)) {
            setProgressText(firstText(status?.stageText, status?.stage, status?.status, "一键流分镜执行中"));
          }
        },
      });
      if (!isCurrentScope(requestScopeKey)) return;
      const list = Array.isArray(result?.segments) ? result.segments : [];
      if (!list.length) throw new Error("一键流分镜没有返回可用片段，请重试；不会自动切回原拆分规则");
      if (!isOneClickFlowGenerationResult(result)) {
        throw new Error("后端返回的分镜规则不匹配，未将其识别为一键流结果；请检查后端版本，并刷新核对当前分镜");
      }
      // The backend has saved the whole result already. Never reinterpret a saved
      // success as failure through old prompt policies or post-generation requests.
      segmentsRef.current = list;
      setSegments(list);
      setSelectedIndex(list[0]?.segmentIndex || 1);
      setEditing(false);
      setDraft(null);
      draftBaselineRef.current = null;
      setGlobalLightingPresetId(inferGlobalLightingPresetId(list, wan3Selected));
      toast.success(`一键流分镜已生成并保存，共 ${list.length} 个片段`);
    } catch (error) {
      if (isCurrentScope(requestScopeKey)) toast.error(error.message || "一键流分镜生成失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) {
        setLoading("");
        setProgressText("");
      }
    }
  };

  const requestGenerateOneClickFlowSegments = () => {
    if (!segmentsLoadedRef.current) {
      toast.error("小故事板列表尚未成功加载，请先重试后再使用一键流分镜", 0);
      return;
    }
    if (hasProcessingVideoSegments) {
      toast.warning("当前集仍有视频生成中，完成后再使用一键流分镜，避免影响生成结果对应关系");
      return;
    }
    const existingSegments = segments.filter((segment) => firstText(segment?.seedancePrompt) || segment?.shots?.length || isVideoSuccess(segment));
    if (existingSegments.length) {
      setOperationConfirm({
        title: "使用一键流分镜",
        message: `将按独立的一键流规则重新拆分当前集，替换已有的 ${existingSegments.length} 个分镜组及其视频对应关系。原「拆分分镜」的规则和设置不会改变。确定继续？`,
        confirmLabel: "一键流分镜",
        onConfirm: executeGenerateOneClickFlowSegments,
      });
      return;
    }
    executeGenerateOneClickFlowSegments();
  };

  const persistSegmentPatch = useCallback(async (segment) => {
    const requestScopeKey = scopeKey;
    const {
      __syncAssetRefsFromPrompt,
      ...payloadSegment
    } = segment || {};
    const hasSeedancePromptField = Object.prototype.hasOwnProperty.call(payloadSegment, "seedancePrompt")
      && payloadSegment.seedancePrompt !== undefined;
    const requestBody = {
      ...payloadSegment,
      projectId,
      episodeId,
    };
    if (hasSeedancePromptField) {
      const storedSegment = segmentsRef.current.find((item) => (
        payloadSegment.id != null
          ? Number(item.id) === Number(payloadSegment.id)
          : Number(item.segmentIndex) === Number(payloadSegment.segmentIndex)
      ));
      const preserveOneClickFlow = isOneClickFlowSegment(payloadSegment) || isOneClickFlowSegment(storedSegment);
      const seedancePrompt = formatSeedancePrompt(preserveOneClickFlow
        ? payloadSegment.seedancePrompt
        : stripStandaloneLightingBlocks(payloadSegment.seedancePrompt));
      const references = reorderReferencesByPromptBinding(
        seedancePrompt,
        mergeGuaMetaForReferences(buildSegmentReferenceItems({ ...payloadSegment, seedancePrompt }, assets, referenceImageLimit), assetMetaMap),
      );
      const shouldSyncAssetRefs = __syncAssetRefsFromPrompt === true;
      requestBody.seedancePrompt = seedancePrompt;
      if (shouldSyncAssetRefs) {
        requestBody.assetRefs = extractAssetRefsFromEditablePrompt(seedancePrompt, references);
      }
    } else {
      delete requestBody.seedancePrompt;
    }
    const mutationKey = `${requestScopeKey}:${Number(requestBody.segmentIndex) || 0}`;
    const previousMutation = segmentMutationChainsRef.current.get(mutationKey) || Promise.resolve();
    const mutation = previousMutation
      .catch(() => undefined)
      .then(() => authenticatedFetch(`${API_HOST}/api/episode-mini-storyboard/segment`, {
        method: "POST",
        body: JSON.stringify(requestBody),
      }));
    segmentMutationChainsRef.current.set(mutationKey, mutation);
    let saved;
    try {
      saved = await mutation;
    } finally {
      if (segmentMutationChainsRef.current.get(mutationKey) === mutation) {
        segmentMutationChainsRef.current.delete(mutationKey);
      }
    }
    if (!isCurrentScope(requestScopeKey) || !segmentBelongsToCurrentScope(saved)) return null;
    mergeSegment(saved);
    return saved;
  }, [assetMetaMap, assets, episodeId, isCurrentScope, mergeSegment, projectId, referenceImageLimit, scopeKey, segmentBelongsToCurrentScope]);

  const waitForPendingSegmentMutations = useCallback(async () => {
    const mutationPrefix = `${scopeKey}:`;
    const pending = [...segmentMutationChainsRef.current.entries()]
      .filter(([key]) => key.startsWith(mutationPrefix))
      .map(([, promise]) => promise);
    if (!pending.length) return;
    await Promise.allSettled(pending);
  }, [scopeKey]);

  const buildEditableSegmentPatch = useCallback((segment = {}, { includeDuration } = {}) => {
    const patch = {
      id: segment.id,
      segmentIndex: segment.segmentIndex,
      title: segment.title,
      location: segment.location,
      weather: segment.weather,
      sourceText: segment.sourceText,
      storyMode: segment.storyMode,
      narrationText: segment.narrationText,
      seedancePrompt: segment.seedancePrompt,
      shots: segment.shots,
      spatial: segment.spatial,
      assetRefs: segment.assetRefs,
      lightingPrompt: segment.lightingPrompt,
    };
    // Prompt/shots and duration are independent edits.  Most callers are prompt-only;
    // the duration fields are sent only after the user touched the duration input.
    const shouldIncludeDuration = includeDuration ?? (segment.__durationDirty === true);
    if (shouldIncludeDuration) {
      if (wan3Selected) {
        patch.durationSeconds = normalizeWanxVideoDuration(
          segment.durationSeconds,
          WAN3_PLANNING_MAX_DURATION,
        );
        patch.durationProfile = MINI_STORYBOARD_DURATION_PROFILE_WAN3;
      } else {
        Object.assign(patch, buildMiniStoryboardDurationSaveFields(segment, durationProfile));
      }
    }
    if (segment.__syncAssetRefsFromPrompt !== undefined) {
      patch.__syncAssetRefsFromPrompt = segment.__syncAssetRefsFromPrompt;
    }
    Object.keys(patch).forEach((key) => {
      if (patch[key] === undefined) delete patch[key];
    });
    return patch;
  }, [durationProfile, wan3Selected]);

  const insertActingDirectionIntoPrompt = useCallback(async (direction) => {
    const baseSegment = activeReferenceSegment;
    if (!baseSegment?.segmentIndex || !firstText(direction?.prompt)) return null;
    const requestScopeKey = scopeKey;
    const nextPrompt = upsertActingDirectionPrompt(baseSegment.seedancePrompt, direction);
    setActingDirectionSaving(true);
    try {
      const saved = await persistSegmentPatch({
        id: baseSegment.id,
        segmentIndex: baseSegment.segmentIndex,
        seedancePrompt: nextPrompt,
        __syncAssetRefsFromPrompt: false,
      });
      if (!saved || !isCurrentScope(requestScopeKey)) return null;
      setDraft((current) => (
        current && Number(current.segmentIndex) === Number(saved.segmentIndex)
          ? { ...current, seedancePrompt: firstText(saved.seedancePrompt, nextPrompt) }
          : current
      ));
      toast.success("人物表演指导已写入当前 Prompt");
      return saved;
    } catch (error) {
      if (isCurrentScope(requestScopeKey)) toast.error(error.message || "写入人物表演指导失败");
      return null;
    } finally {
      if (isCurrentScope(requestScopeKey)) setActingDirectionSaving(false);
    }
  }, [activeReferenceSegment, isCurrentScope, persistSegmentPatch, scopeKey, toast]);

  const saveReferenceBindings = useCallback(async (nextReferences, baseSegment = null) => {
    const requestedBase = baseSegment || activeReferenceSegment;
    if (!requestedBase?.segmentIndex) return null;
    const requestScopeKey = scopeKey;
    const targetSegmentId = requestedBase.id;
    const targetSegmentIndex = Number(requestedBase.segmentIndex);
    await waitForPendingSegmentMutations();
    if (!isCurrentScope(requestScopeKey)) return null;
    const latestPersistedSegment = segmentsRef.current.find((item) => (
      targetSegmentId != null
        ? Number(item.id) === Number(targetSegmentId)
        : Number(item.segmentIndex) === targetSegmentIndex
    ));
    const editingDraft = editing && draft && Number(draft.segmentIndex) === targetSegmentIndex
      ? draft
      : null;
    const base = editingDraft || latestPersistedSegment || requestedBase;
    const references = (nextReferences || []).filter((item) => firstText(item?.url)).slice(0, referenceImageLimit);
    const basePromptWithReferences = buildSegmentPromptWithReferences(base, assets, assetMetaMap, referenceImageLimit).prompt;
    const promptBase = firstText(
      editingDraft?.seedancePrompt,
      basePromptWithReferences,
      base.seedancePrompt,
    );
    const previousReferences = buildSegmentPromptWithReferences(base, assets, assetMetaMap, referenceImageLimit).references;
    const remappedPrompt = remapSegmentPromptReferenceMarkers(base, promptBase, previousReferences, references);
    const nextSegment = {
      id: base.id,
      segmentIndex: base.segmentIndex,
      seedancePrompt: setPromptReferenceBinding(remappedPrompt, references),
      assetRefs: assetRefsFromReferenceItems(references),
      __syncAssetRefsFromPrompt: false,
    };
    const saved = await persistSegmentPatch(nextSegment);
    if (!saved) return null;
    if (editing && draft && Number(draft.segmentIndex) === Number(saved?.segmentIndex)) {
      setDraft((current) => (
        current && Number(current.segmentIndex) === Number(saved.segmentIndex)
          ? {
              ...current,
              seedancePrompt: firstText(saved.seedancePrompt, nextSegment.seedancePrompt),
              assetRefs: saved.assetRefs || nextSegment.assetRefs,
            }
          : current
      ));
    }
    return saved;
  }, [activeReferenceSegment, assetMetaMap, assets, draft, editing, isCurrentScope, persistSegmentPatch, referenceImageLimit, scopeKey, waitForPendingSegmentMutations]);

  const toggleReferenceSelectionAsset = useCallback((asset, groupKey) => {
    if (!referenceSelectionDraft) return;
    const nextItem = buildReferenceItemFromAsset(asset, groupKey);
    if (!firstText(nextItem.url)) {
      toast.error("这个资产没有可用图片，无法绑定为参考图");
      return;
    }
    const draftReferences = Array.isArray(referenceSelectionDraft.references) ? referenceSelectionDraft.references : [];
    const draftExistingIndex = findReferenceIndex(nextItem, draftReferences);
    if (draftExistingIndex < 0 && draftReferences.length >= referenceImageLimit) {
      toast.warning(`单个片段最多绑定 ${referenceImageLimit} 张参考图`);
      return;
    }
    setReferenceSelectionDraft((current) => {
      if (!current) return current;
      const currentReferences = Array.isArray(current.references) ? current.references : [];
      const existingIndex = findReferenceIndex(nextItem, currentReferences);
      if (existingIndex >= 0) {
        return {
          ...current,
          references: currentReferences.filter((_, index) => index !== existingIndex),
        };
      }
      if (currentReferences.length >= referenceImageLimit) return current;
      return {
        ...current,
        references: [...currentReferences, nextItem],
      };
    });
  }, [referenceImageLimit, referenceSelectionDraft, toast]);

  const removeReferenceSelectionDraft = useCallback((index) => {
    setReferenceSelectionDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        references: (current.references || []).filter((_, itemIndex) => itemIndex !== index),
      };
    });
  }, []);

  const cancelReferenceSelection = useCallback(() => {
    setReferenceSelectionDraft(null);
    setReferenceTargetSegmentIndex(null);
  }, []);

  const confirmReferenceSelection = useCallback(async () => {
    if (!referenceSelectionDraft) return;
    const requestScopeKey = scopeKey;
    const base = resolveReferenceTargetSegment();
    if (!base?.segmentIndex) return;
    setLoading("reference");
    try {
      const references = Array.isArray(referenceSelectionDraft.references) ? referenceSelectionDraft.references : [];
      await saveReferenceBindings(references, base);
      if (!isCurrentScope(requestScopeKey)) return;
      setReferenceSelectionDraft(null);
      setReferenceTargetSegmentIndex(null);
      toast.success(`已保存 ${references.length} 张参考图`);
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      toast.error(error.message || "保存参考图失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) setLoading("");
    }
  }, [isCurrentScope, referenceSelectionDraft, resolveReferenceTargetSegment, saveReferenceBindings, scopeKey, toast]);

  const assetSelectedInReferenceDraft = useCallback((asset, groupKey) => {
    if (!referenceSelectionDraft) return false;
    const item = buildReferenceItemFromAsset(asset, groupKey);
    return findReferenceIndex(item, referenceSelectionDraft.references || []) >= 0;
  }, [referenceSelectionDraft]);

  const openReferenceUpload = useCallback(() => {
    if (!activeReferenceSegment?.segmentIndex) {
      toast.warning("请先选择一个分镜组");
      return;
    }
    setReferenceDrawerOpen(true);
    referenceUploadInputRef.current?.click();
  }, [activeReferenceSegment?.segmentIndex, toast]);

  const handleReferenceUploadFiles = useCallback(async (fileList) => {
    const requestScopeKey = scopeKey;
    const mediaEnabled = supportsSeedance25MediaReferences(activeVideoProvider);
    const files = Array.from(fileList || []);
    if (files.some((file) => !(mediaEnabled ? /^(image|video|audio)\//.test(file?.type || "") : file?.type?.startsWith("image/")))) {
      toast.warning(mediaEnabled ? "请选择图片、视频或音频文件" : "当前线路只支持参考图片");
      return;
    }
    if (!files.length) return;
    const base = resolveReferenceTargetSegment();
    if (!base?.segmentIndex) {
      toast.warning("请先选择一个分镜组");
      return;
    }
    const currentReferences = referenceSelectionDraft
      ? (referenceSelectionDraft.references || [])
      : buildSegmentPromptWithReferences(base, assets, assetMetaMap, referenceImageLimit).references;
    const remaining = Math.max(0, referenceImageLimit - currentReferences.length);
    if (remaining <= 0) {
      toast.warning(`单个片段最多绑定 ${referenceImageLimit} 张参考图`);
      return;
    }
    const uploadFiles = files.slice(0, remaining);
    if (files.length > uploadFiles.length) {
      toast.warning(`最多还能添加 ${remaining} 张参考图，已忽略多余图片`);
    }
    setReferenceUploading(true);
    try {
      const uploadedUrls = await Promise.all(uploadFiles.map((file) => uploadFileToCos(file)));
      if (!isCurrentScope(requestScopeKey)) return;
      const uploadedReferences = uploadedUrls
        .map((url, index) => (firstText(url) ? buildReferenceItemFromUpload(url, uploadFiles[index], index) : null))
        .filter(Boolean);
      if (!uploadedReferences.length) throw new Error("没有上传成功的图片");
      if (referenceSelectionDraft) {
        setReferenceSelectionDraft((current) => {
          if (!current) return current;
          const refs = Array.isArray(current.references) ? current.references : [];
          return {
            ...current,
            references: [...refs, ...uploadedReferences].slice(0, referenceImageLimit),
          };
        });
        toast.success(`已上传 ${uploadedReferences.length} 张参考图，点确定后保存`);
      } else {
        await saveReferenceBindings([...currentReferences, ...uploadedReferences].slice(0, referenceImageLimit), base);
        if (!isCurrentScope(requestScopeKey)) return;
        toast.success(`已上传并绑定 ${uploadedReferences.length} 张参考图`);
      }
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      toast.error(error.message || "参考图上传失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) {
        setReferenceUploading(false);
        if (referenceUploadInputRef.current) referenceUploadInputRef.current.value = "";
      }
    }
  }, [activeVideoProvider, assetMetaMap, assets, isCurrentScope, referenceImageLimit, referenceSelectionDraft, resolveReferenceTargetSegment, saveReferenceBindings, scopeKey, toast]);

  const handleReferenceUploadInputChange = useCallback((event) => {
    handleReferenceUploadFiles(event.target.files);
  }, [handleReferenceUploadFiles]);

  const pollReferenceAssetStatus = useCallback(async ({ url, assetId, provider, requestScopeKey }) => {
    const normalizedUrl = normalizeImageUrl(url);
    if (!supportsVideoAssetIngest(provider) || !normalizedUrl || !assetId || !projectId) return;
    try {
      const result = await pollVideoAssetUntilTerminal({
        provider,
        projectId,
        url: normalizedUrl,
        assetId,
        shouldContinue: () => isCurrentScope(requestScopeKey),
      });
      if (!isCurrentScope(requestScopeKey)) return;
      await refreshStatusMap?.();
      if (!isCurrentScope(requestScopeKey)) return;
      if (result.status === "Active") {
        window.dispatchEvent(new CustomEvent("asset-library-change", { detail: { projectId } }));
        toast.success(`参考图已入库 ${getVideoProviderLabel(provider)}`);
      } else {
        toast.error(`${getVideoProviderLabel(provider)} 参考图入库失败，可点击重试`);
      }
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      // 超时只停止前端等待，后端仍保留 Processing，不误标失败。
      toast.warning(error.message || "查询参考图入库状态失败");
    }
  }, [isCurrentScope, projectId, refreshStatusMap, toast]);

  const ingestReferenceAsset = useCallback(async (item) => {
    const url = normalizeImageUrl(item?.url);
    if (!projectId || !url || referenceMediaType(item) === "video") return;
    const provider = activeVideoProvider;
    if (!supportsVideoAssetIngest(provider)) {
      toast.error("当前项目的视频服务不支持素材入库");
      return;
    }
    const apiBase = getVideoAssetApiBase(provider);
    const requestScopeKey = scopeKey;
    setReferenceAssetIngestingUrls((current) => new Set(current).add(url));
    try {
      const result = await authenticatedFetch(`${API_HOST}${apiBase}/create`, {
        method: "POST",
        body: JSON.stringify({
          projectId: Number(projectId),
          episodeId: episodeId ? Number(episodeId) : undefined,
          shotNumber: activeReferenceSegment?.segmentIndex ? Number(activeReferenceSegment.segmentIndex) : undefined,
          url,
          name: getReferencePromptName(item, "小故事板参考图"),
          assetType: "Image",
          typeCode: "storyboard_image",
          source: "storyboard",
          sourceDetail: "episode_mini_storyboard_reference",
        }),
      });
      if (!isCurrentScope(requestScopeKey)) return;
      const rawResultProvider = firstText(result?.provider, result?.assetProvider);
      if (rawResultProvider && !isSameVideoAssetLibrary(rawResultProvider, provider)) {
        throw new Error(`素材平台返回不一致：期望 ${getVideoProviderLabel(provider)}，实际 ${rawResultProvider}`);
      }
      const applied = applyAssetCreateResult({}, result, provider);
      if (!applied.accepted || applied.provider !== provider) throw new Error("参考图入库返回格式不正确");
      const effective = getEffectiveAssetMeta(applied.target, provider);
      const assetId = effective.assetId;
      const status = effective.status;
      if (!assetId) throw new Error("参考图入库未返回素材 ID");
      const baseSegment = activeReferenceSegment;
      if (baseSegment?.segmentIndex) {
        const currentReferences = buildSegmentPromptWithReferences(baseSegment, assets, assetMetaMap, referenceImageLimit).references;
        const nextReferences = currentReferences.map((reference) => {
          if (normalizeImageUrl(reference?.url) !== url) return reference;
          return {
            ...reference,
            ...applied.target,
            assetProvider: provider,
          };
        });
        try {
          await saveReferenceBindings(nextReferences, baseSegment);
        } catch (persistError) {
          toast.warning(`入库已提交，但参考图状态写回失败：${persistError.message || "请稍后刷新"}`);
        }
      }
      await refreshStatusMap?.();
      if (status === "Active") {
        toast.success(`参考图已入库 ${getVideoProviderLabel(provider)}`);
      } else {
        toast.success(result?.reused ? "已同步参考图入库任务" : "参考图已提交入库");
      }
      if (assetId && status !== "Active" && status !== "Failed") {
        void pollReferenceAssetStatus({ url, assetId, provider, requestScopeKey });
      }
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      toast.error(error.message || "参考图入库失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) {
        setReferenceAssetIngestingUrls((current) => {
          const next = new Set(current);
          next.delete(url);
          return next;
        });
      }
    }
  }, [activeReferenceSegment, activeVideoProvider, assetMetaMap, assets, episodeId, isCurrentScope, pollReferenceAssetStatus, projectId, referenceImageLimit, refreshStatusMap, saveReferenceBindings, scopeKey, toast]);

  const replaceReferenceWithAsset = useCallback(async (asset, groupKey) => {
    if (referenceReplaceIndex == null) return;
    const requestScopeKey = scopeKey;
    const nextItem = buildReferenceItemFromAsset(asset, groupKey);
    if (!firstText(nextItem.url)) {
      toast.error("这个资产没有可用图片，无法绑定为参考图");
      return;
    }
    const base = resolveReferenceTargetSegment();
    if (!base?.segmentIndex) return;
    const currentReferences = buildSegmentPromptWithReferences(base, assets, assetMetaMap, referenceImageLimit).references;
    const nextReferences = [...currentReferences];
    nextReferences[referenceReplaceIndex] = nextItem;
    setLoading("reference");
    try {
      await saveReferenceBindings(nextReferences, base);
      if (!isCurrentScope(requestScopeKey)) return;
      setReferenceReplaceIndex(null);
      setReferenceTargetSegmentIndex(null);
      toast.success(`已替换 @图片${referenceReplaceIndex + 1} 为「${getAssetName(asset)}」`);
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      toast.error(error.message || "替换参考图失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) setLoading("");
    }
  }, [assetMetaMap, assets, isCurrentScope, referenceImageLimit, referenceReplaceIndex, resolveReferenceTargetSegment, saveReferenceBindings, scopeKey, toast]);

  const removeReferenceBinding = useCallback(async (index) => {
    const requestScopeKey = scopeKey;
    const base = activeReferenceSegment;
    if (!base?.segmentIndex) return;
    const currentReferences = buildSegmentPromptWithReferences(base, assets, assetMetaMap, referenceImageLimit).references;
    const nextReferences = currentReferences.filter((_, itemIndex) => itemIndex !== index);
    setLoading("reference");
    try {
      await saveReferenceBindings(nextReferences, base);
      if (!isCurrentScope(requestScopeKey)) return;
      setReferenceReplaceIndex(null);
      setReferenceTargetSegmentIndex(null);
      toast.success(`已移除 @图片${index + 1}`);
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      toast.error(error.message || "移除参考图失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) setLoading("");
    }
  }, [activeReferenceSegment, assetMetaMap, assets, isCurrentScope, referenceImageLimit, saveReferenceBindings, scopeKey, toast]);

  const selectHistoryVideo = useCallback(async (item) => {
    const requestScopeKey = scopeKey;
    const targetSegment = selectedSegment;
    const targetKey = getMiniStoryboardHistoryKey(projectId, episodeId, targetSegment);
    if (
      !historyDrawerOpenRef.current
      || videoHistoryLoading
      || videoHistoryError
      || historyLoadedSegmentKey !== targetKey
      || selectedHistorySegmentKeyRef.current !== targetKey
      || !matchesMiniStoryboardHistoryItem(item, projectId, episodeId, targetSegment)
    ) {
      toast.warning("历史记录与当前分镜不一致，请重新打开生成历史后再选择");
      return;
    }
    const videoUrl = firstText(item?.videoUrl, item?.resultUrl, item?.url);
    if (!videoUrl || !targetSegment?.segmentIndex) return;
    setLoading("history-switch");
    setPlaying(false);
    try {
      const saved = await persistSegmentPatch({
        id: targetSegment.id,
        segmentIndex: targetSegment.segmentIndex,
        videoUrl,
        thumbnailUrl: firstText(item.thumbnailUrl, targetSegment.thumbnailUrl),
        taskId: firstText(item.providerTaskId, item.taskId, targetSegment.taskId),
        internalTaskId: firstText(item.internalTaskId, targetSegment.internalTaskId),
        videoProvider: firstText(item.provider, targetSegment.videoProvider),
        videoModel: firstText(item.model, targetSegment.videoModel),
        videoResolution: firstText(item.resolution, item.videoResolution, targetSegment.videoResolution, videoResolution),
        videoRatio: firstText(item.ratio, item.videoRatio, targetSegment.videoRatio),
        // 历史任务记录的是供应商实际成片时长，不能用它覆盖片段时长。
        status: "video_success",
        error: "",
        errorMessage: "",
      });
      if (!isCurrentScope(requestScopeKey) || !saved) return;
      if (selectedHistorySegmentKeyRef.current !== targetKey) {
        toast.success("历史视频已切换，当前查看的分镜已变化");
        return;
      }
      if (!(editing && draft && Number(draft.segmentIndex) === Number(targetSegment.segmentIndex))) {
        setEditing(false);
        setDraft(null);
      }
      setHistoryDrawerOpen(false);
      const startSeconds = Number(targetSegment.startSeconds) || 0;
      setSelectedIndex(saved.segmentIndex || targetSegment.segmentIndex);
      setPlayheadSeconds(startSeconds);
      pendingSeekRef.current = { seconds: startSeconds, autoPlay: false };
      setSeekNonce((value) => value + 1);
      toast.success("已切换到这条历史视频");
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      toast.error(error.message || "切换历史视频失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) setLoading("");
    }
  }, [draft, editing, episodeId, historyLoadedSegmentKey, isCurrentScope, persistSegmentPatch, projectId, scopeKey, selectedSegment, toast, videoHistoryError, videoHistoryLoading, videoResolution]);

  const applyGlobalLightingPreset = useCallback(async (preset) => {
    if (!preset) return;
    const requestScopeKey = scopeKey;
    const targets = segments.filter((segment) => firstText(segment?.seedancePrompt) && (!wan3Selected || isOneClickFlowSegment(segment)));
    if (!targets.length) {
      toast.warning("当前没有可设置打光的分镜");
      return;
    }

    setLoading("global-lighting");
    setProgressText(`正在设置全局打光：${preset.name}`);
    try {
      const outcomes = await Promise.allSettled(targets.map((segment) => persistSegmentPatch({
        id: segment.id,
        segmentIndex: segment.segmentIndex,
        lightingPrompt: firstText(preset.prompt),
      })));
      if (!isCurrentScope(requestScopeKey)) return;
      const saved = outcomes.filter((result) => result.status === "fulfilled" && result.value);
      const failed = targets.length - saved.length;
      if (editing && draft && saved.some((result) => Number(result.value.segmentIndex) === Number(draft.segmentIndex))) {
        setDraft((current) => current ? { ...current, lightingPrompt: firstText(preset.prompt) } : current);
      }
      if (failed) {
        setGlobalLightingPresetId("");
        toast.warning(`「${preset.name}」已保存 ${saved.length}/${targets.length} 个片段，${failed} 个失败；可再次选择重试`);
      } else {
        setGlobalLightingPresetId(preset.id);
        toast.success(`已将 ${targets.length} 个片段设为「${preset.name}」并自动保存`);
      }
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      toast.error(error.message || "全局打光保存失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) {
        setLoading("");
        setProgressText("");
      }
    }
  }, [draft, editing, isCurrentScope, persistSegmentPatch, scopeKey, segments, toast, wan3Selected]);

  const insertSegmentWithPayload = useCallback(async (insertIndex, segmentPayload = {}, successMessage = "") => {
    if (!projectId || !episodeId) return;
    const requestScopeKey = scopeKey;
    setLoading("insert-segment");
    setPlaying(false);
    try {
      await waitForPendingSegmentMutations();
      if (!isCurrentScope(requestScopeKey)) return null;
      const insertDurationFields = wan3Selected
        ? {
            durationSeconds: normalizeWanxVideoDuration(
              segmentPayload.durationSeconds ?? durationSeconds,
              WAN3_PLANNING_MAX_DURATION,
            ),
            durationProfile: MINI_STORYBOARD_DURATION_PROFILE_WAN3,
          }
        : buildMiniStoryboardDurationSaveFields({
            durationSeconds: segmentPayload.durationSeconds ?? durationSeconds,
          }, durationProfile);
      const result = await authenticatedFetch(`${API_HOST}/api/episode-mini-storyboard/segment/insert`, {
        method: "POST",
        body: JSON.stringify({
          projectId,
          episodeId,
          insertIndex,
          status: "draft",
          storyMode,
          narrationText: "",
          title: `分镜组 ${String(insertIndex).padStart(2, "0")}`,
          sourceText: "",
          seedancePrompt: "",
          shots: [],
          spatial: { manual: true },
          assetRefs: {},
          ...segmentPayload,
          ...insertDurationFields,
        }),
      });
      if (!isCurrentScope(requestScopeKey)) return null;
      const list = Array.isArray(result?.segments) ? result.segments : [];
      if (list.length) setSegments(list);
      const safeIndex = Number(result?.insertIndex || insertIndex);
      const inserted = result?.segment || list.find((item) => Number(item.segmentIndex) === safeIndex) || buildEmptySegment(safeIndex);
      const startSeconds = list
        .filter((item) => Number(item.segmentIndex) < safeIndex)
        .reduce((sum, item) => sum + (Number(item.durationSeconds) || durationSeconds), 0);
      setSelectedIndex(safeIndex);
      setPlayheadSeconds(startSeconds);
      const insertedDraft = {
        ...inserted,
        seedancePrompt: formatSeedancePrompt(stripStandaloneLightingBlocks(inserted?.seedancePrompt)),
      };
      draftBaselineRef.current = {
        segmentIndex: insertedDraft.segmentIndex,
        seedancePrompt: insertedDraft.seedancePrompt,
        durationSeconds: String(insertedDraft.durationSeconds ?? ""),
      };
      setDraft(insertedDraft);
      setEditing(true);
      toast.success(successMessage || `已插入分镜组 ${String(safeIndex).padStart(2, "0")}`);
      return inserted;
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return null;
      toast.error(error.message || "插入分镜组失败", 0);
      return null;
    } finally {
      if (isCurrentScope(requestScopeKey)) setLoading("");
    }
  }, [durationProfile, durationSeconds, episodeId, isCurrentScope, projectId, scopeKey, storyMode, toast, waitForPendingSegmentMutations, wan3Selected]);

  const insertManualSegment = useCallback((insertIndex) => {
    if (hasProcessingVideoSegments) {
      toast.warning("当前集仍有视频生成中，完成后再插入段落，避免生成结果落到错误片段");
      return;
    }
    const selectedHasVideo = selectedSegment && isVideoSuccess(selectedSegment) && firstText(selectedSegment.videoUrl);
    const sourceSegment = selectedHasVideo ? selectedSegment : generatedVideoSegments[0];
    const localVideoSecond = selectedHasVideo
      ? clamp(
        Number(previewVideoRef.current?.currentTime) || Math.max(0, Number(playheadSeconds || 0) - Number(selectedSegment?.startSeconds || 0)),
        0,
        Math.max(0, Number(selectedSegment?.durationSeconds || durationSeconds || 0)),
      )
      : 0;
    setInsertSegmentDialog({
      insertIndex,
      defaultSourceIndex: sourceSegment?.segmentIndex || "",
      defaultFrameSecond: Number(localVideoSecond.toFixed(1)),
    });
  }, [durationSeconds, generatedVideoSegments, hasProcessingVideoSegments, playheadSeconds, selectedSegment, toast]);

  const duplicateSelectedSegment = useCallback(async () => {
    if (!segments.length || !selectedSegment?.segmentIndex) return;
    if (editing) {
      toast.warning("请先保存当前修改，再复制段落");
      return;
    }
    if (hasProcessingVideoSegments) {
      toast.warning("当前集仍有视频生成中，完成后再复制段落，避免生成结果落到错误片段");
      return;
    }

    const previousReferences = buildSegmentPromptWithReferences(selectedSegment, assets, assetMetaMap, referenceImageLimit).references;
    const imageReferences = previousReferences.filter((item) => referenceMediaType(item) === "image");
    const remappedPrompt = remapSegmentPromptReferenceMarkers(
      selectedSegment,
      selectedSegment.seedancePrompt,
      previousReferences,
      imageReferences,
    );
    const copiedPrompt = setPromptReferenceBinding(remappedPrompt, imageReferences);
    const copiedShots = cloneSegmentValue(selectedSegment.shots, []);
    await insertSegmentWithPayload(Number(selectedSegment.segmentIndex) + 1, {
      durationSeconds: selectedSegment.durationSeconds ?? durationSeconds,
      location: firstText(selectedSegment.location),
      weather: firstText(selectedSegment.weather),
      sourceText: firstText(selectedSegment.sourceText),
      storyMode: firstText(selectedSegment.storyMode, storyMode),
      narrationText: firstText(selectedSegment.narrationText),
      seedancePrompt: copiedPrompt,
      shots: copiedShots,
      spatial: copySemanticSpatialPlan(selectedSegment.spatial),
      assetRefs: assetRefsFromReferenceItems(imageReferences, "manual", {
        omitProviderAssetMetadata: seedance25Selected,
      }),
      lightingPrompt: firstText(selectedSegment.lightingPrompt),
    }, `已复制分镜组 ${String(selectedSegment.segmentIndex).padStart(2, "0")} 的文字和 ${imageReferences.length} 张参考图`);
  }, [assetMetaMap, assets, durationSeconds, editing, hasProcessingVideoSegments, insertSegmentWithPayload, referenceImageLimit, seedance25Selected, segments.length, selectedSegment, storyMode, toast]);

  const upscaleCapturedFrameReference = useCallback(async (imageUrl) => {
    const sourceUrl = firstText(imageUrl);
    if (!sourceUrl) return sourceUrl;
    setProgressText("正在超分截帧参考图");
    try {
      const { result, url } = await runTaskToCompletion("SUPER_RESOLUTION", {
        imageUrl: sourceUrl,
        resolution: "4k",
        scale: 50,
        projectId,
        episodeId,
        source: "mini_storyboard_captured_frame",
      }, {
        projectId,
        episodeId,
        source: "mini_storyboard_captured_frame",
        timeoutMs: 10 * 60 * 1000,
      });
      return firstText(url, result?.imageUrl, result?.resultUrl, sourceUrl);
    } catch (error) {
      toast.warning(`截帧超分失败，已使用原图：${error.message || "未知错误"}`);
      return sourceUrl;
    }
  }, [episodeId, projectId, toast]);

  const confirmInsertSegment = useCallback(async ({ mode, sourceSegment, frameSecond, mosaicFrameUrl }) => {
    const insertIndex = Number(insertSegmentDialog?.insertIndex || 1);
    setInsertSegmentLoading(true);
    try {
      let payload = {};
      if (mode === "capture") {
        if (!sourceSegment?.videoUrl) throw new Error("请选择一个已生成视频的分镜组");
        let capturedUrl = firstText(mosaicFrameUrl);
        if (!capturedUrl) {
          setProgressText("正在截取视频帧");
          const file = await captureVideoFrameToFile(sourceSegment.videoUrl, frameSecond);
          setProgressText("正在上传截帧参考图");
          capturedUrl = await uploadFileToCos(file);
        } else {
          setProgressText("正在处理打码截帧参考图");
        }
        const url = await upscaleCapturedFrameReference(capturedUrl);
        const reference = {
          id: `captured-frame-${sourceSegment.segmentIndex}-${Date.now()}`,
          type: "截帧",
          role: "reference_image",
          mediaType: "image",
          name: `截帧参考：分镜组 ${String(sourceSegment.segmentIndex).padStart(2, "0")}`,
          displayName: `截帧参考：分镜组 ${String(sourceSegment.segmentIndex).padStart(2, "0")}`,
          url,
        };
        const prompt = formatSeedancePrompt(`参考@图片1\n\n参考图绑定：@图片1=${getReferencePromptName(reference, "截帧参考")}`);
        payload = {
          sourceText: `从分镜组 ${String(sourceSegment.segmentIndex).padStart(2, "0")} 截取 ${Number(frameSecond || 0).toFixed(1)} 秒${mosaicFrameUrl ? "并打码" : ""}作为参考图`,
          seedancePrompt: prompt,
          spatial: {
            manual: true,
            source: "captured_frame",
            guaVideoMode: "ref2v",
            sourceSegmentIndex: sourceSegment.segmentIndex,
            frameSecond: Number(frameSecond || 0),
            mosaic: Boolean(mosaicFrameUrl),
          },
          assetRefs: assetRefsFromReferenceItems([reference]),
        };
      } else if (mode === "extend" || mode === "extend_auto") {
        if (!sourceSegment?.videoUrl) throw new Error("请选择一个已生成视频的分镜组");
        const reference = {
          id: `extend-video-${sourceSegment.segmentIndex}-${Date.now()}`,
          type: "视频",
          role: "reference_video",
          mediaType: "video",
          name: `延长来源：分镜组 ${String(sourceSegment.segmentIndex).padStart(2, "0")}`,
          displayName: `分镜组 ${String(sourceSegment.segmentIndex).padStart(2, "0")} 视频`,
          url: sourceSegment.videoUrl,
        };
        const prompt = mode === "extend_auto"
          ? buildAutoExtendPrompt(sourceSegment, reference)
          : formatSeedancePrompt(`延长@视频1\n\n参考图绑定：@视频1=${getReferencePromptName(reference, "参考视频")}`);
        payload = {
          sourceText: mode === "extend_auto"
            ? `自动延长分镜组 ${String(sourceSegment.segmentIndex).padStart(2, "0")} 的视频`
            : `延长分镜组 ${String(sourceSegment.segmentIndex).padStart(2, "0")} 的视频`,
          seedancePrompt: prompt,
          spatial: {
            manual: true,
            source: mode === "extend_auto" ? "extend_video_auto_prompt" : "extend_video",
            guaVideoMode: "extend",
            sourceSegmentIndex: sourceSegment.segmentIndex,
          },
          assetRefs: assetRefsFromReferenceItems([reference]),
        };
      }
      const inserted = await insertSegmentWithPayload(insertIndex, payload);
      if (inserted) setInsertSegmentDialog(null);
    } catch (error) {
      toast.error(error.message || "创建段落失败", 0);
    } finally {
      setInsertSegmentLoading(false);
      setProgressText("");
    }
  }, [insertSegmentDialog?.insertIndex, insertSegmentWithPayload, toast, upscaleCapturedFrameReference]);

  const requestDeleteSelectedSegment = useCallback(() => {
    if (!selectedSegment || !segments.length) return;
    if (hasProcessingVideoSegments) {
      toast.warning("当前集仍有视频生成中，完成后再删除段落，避免生成结果落到错误片段");
      return;
    }
    setDeleteTarget({
      segmentIndex: selectedSegment.segmentIndex,
      title: selectedSegment.title,
      taskId: selectedSegment.taskId,
    });
  }, [hasProcessingVideoSegments, segments.length, selectedSegment, toast]);

  const confirmDeleteSegment = useCallback(async () => {
    if (!projectId || !episodeId || !deleteTarget?.segmentIndex) return;
    const requestScopeKey = scopeKey;
    const deletedIndex = Number(deleteTarget.segmentIndex);
    setLoading("delete-segment");
    setPlaying(false);
    if (deleteTarget.taskId) stoppedVideoTaskIdsRef.current.add(firstText(deleteTarget.taskId));
    const stopSubtitlePolling = subtitlePollsRef.current.get(deletedIndex);
    if (typeof stopSubtitlePolling === "function") stopSubtitlePolling();
    subtitlePollsRef.current.delete(deletedIndex);
    setErasingSubtitleIndexes((current) => {
      const next = new Set(current);
      next.delete(deletedIndex);
      return next;
    });
    try {
      await waitForPendingSegmentMutations();
      if (!isCurrentScope(requestScopeKey)) return;
      const result = await authenticatedFetch(`${API_HOST}/api/episode-mini-storyboard/segment?projectId=${projectId}&episodeId=${episodeId}&segmentIndex=${deletedIndex}`, {
        method: "DELETE",
      });
      if (!isCurrentScope(requestScopeKey)) return;
      const list = Array.isArray(result?.segments) ? result.segments : [];
      setSegments(list);
      setEditing(false);
      setDraft(null);
      draftBaselineRef.current = null;

      const nextIndex = list.length ? Math.min(deletedIndex, list.length) : 1;
      const nextSegment = list.find((item) => Number(item.segmentIndex) === nextIndex) || list[0] || null;
      setSelectedIndex(nextSegment?.segmentIndex || 1);
      setPlayheadSeconds(list
        .filter((item) => Number(item.segmentIndex) < Number(nextSegment?.segmentIndex || 1))
        .reduce((sum, item) => sum + (Number(item.durationSeconds) || durationSeconds), 0));
      toast.success(`已删除分镜组 ${String(deletedIndex).padStart(2, "0")}`);
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      toast.error(error.message || "删除分镜组失败", 0);
      throw error;
    } finally {
      if (isCurrentScope(requestScopeKey)) setLoading("");
    }
  }, [deleteTarget, durationSeconds, episodeId, isCurrentScope, projectId, scopeKey, toast, waitForPendingSegmentMutations]);

  const submitSegmentVideo = useCallback(async (segment, { silent = false } = {}) => {
    const requestScopeKey = scopeKey;
    if (!segment?.seedancePrompt) {
      if (!silent) toast.error("当前分镜组没有视频提示词");
      return null;
    }
    const oneClickFlowDurationError = getOneClickFlowDurationError(
      segment,
      getSubmissionVideoDuration(segment, activeVideoProvider, durationSeconds),
    );
    if (oneClickFlowDurationError) throw new Error(oneClickFlowDurationError);
    // 当前设置面板是本项目/集的权威生成线路；用户切换到2.5后，已有分段也应能按新线路重生。
    // 设置按 projectId + episodeId 持久化，所以刷新不会无声切回项目默认 Seedance。
    const requestedProvider = activeVideoProvider;
    const requestedModel = videoModel;
    if (wan3Selected || vegaSelected) {
      const savedBindings = Array.isArray(segment?.assetRefs?._bindings)
        ? segment.assetRefs._bindings : [];
      if (vegaSelected && savedBindings.some((item) => item && !firstText(item.url) && firstText(
        item.assetId, item.guaVideoAssetId, item.engGuaVideoAssetId,
        item.guaOminiAssetId, item.dreaminaProxyAssetId, item.volcengineAssetId,
      ))) {
        throw new Error("Wand Vega 分镜中存在只有素材库 ID、没有原图 URL 的参考项，请重新绑定原图");
      }
      const assetGroupsForRequest = await getAssetsForUse();
      if (!isCurrentScope(requestScopeKey)) return null;
      const { references: matchedReferences } = buildSegmentPromptWithReferences(
        segment,
        assetGroupsForRequest,
        assetMetaMap,
        vegaSelected ? Number.MAX_SAFE_INTEGER : WAN3_MAX_REFERENCE_IMAGES,
      );
      const unsupportedReferences = matchedReferences.filter(isSeedance25NonImageReference);
      if (unsupportedReferences.length) {
        throw new Error(`${vegaSelected ? "Wand Vega" : "万相 3.0"} 小故事板当前只接收参考图片，请移除视频或音频参考素材`);
      }
      if (vegaSelected && matchedReferences.some((item) => !firstText(item?.url))) {
        throw new Error("Wand Vega 参考图缺少原始图片 URL，请重新绑定；不能仅用旧素材库 ID");
      }
      if (vegaSelected && matchedReferences.length > WAND_VEGA_STORYBOARD_REFERENCE_LIMIT) {
        throw new Error(`Wand Vega 小故事板当前最多提交 ${WAND_VEGA_STORYBOARD_REFERENCE_LIMIT} 张参考图；当前匹配到 ${matchedReferences.length} 张，请减少绑定`);
      }
      const directImageReferences = matchedReferences
        .filter((item) => firstText(item?.url))
        .slice(0, vegaSelected ? matchedReferences.length : WAN3_MAX_REFERENCE_IMAGES);
      const prompt = appendDetachedLightingPrompt(
        applySegmentReferenceMarkersToPrompt(segment, directImageReferences),
        !wan3Selected || isOneClickFlowSegment(segment) ? inferLightingPrompt(segment) : "",
      );
      const generationMode = directImageReferences.length ? "reference" : "text2video";
      const vegaRequest = vegaSelected ? buildWandVegaStoryboardRequest({
        model: requestedModel,
        prompt,
        references: directImageReferences,
        duration: segment.durationSeconds,
        resolution: videoResolution,
        ratio: videoRatio,
      }) : null;
      const wanxRequest = vegaSelected ? null : validateWanxVideoRequest({
        model: normalizeWanxVideoModel(requestedModel),
        prompt,
        media: directImageReferences.map((item) => ({
          type: WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE,
          url: firstText(item.url),
        })),
        mode: generationMode,
        duration: normalizeWanxVideoDuration(segment.durationSeconds, durationSeconds),
        resolution: normalizeWanxVideoResolution(videoResolution, "1080P"),
        ratio: normalizeWanxVideoRatio(videoRatio, "adaptive"),
      });
      if (!directImageReferences.length && !silent) {
        toast.warning("当前分镜组没有匹配到参考图，将按纯文本视频生成");
      }
      const generationRequest = vegaRequest || wanxRequest;
      const task = await (vegaSelected ? submitWandStoryboardTask : submitVideoTask)({
        provider: generationRequest.provider || WANX_VIDEO_PROVIDER,
        model: generationRequest.model,
        projectId,
        episodeId,
        miniStoryboardSegmentId: segment.id,
        shotNumber: segment.segmentIndex,
        source: "episode_mini_storyboard_video",
        resourceTypeCode: "storyboard_video",
        prompt: generationRequest.prompt,
        duration: generationRequest.duration,
        resolution: generationRequest.resolution,
        ratio: generationRequest.ratio,
        aspectRatio: generationRequest.ratio,
        mode: generationMode,
        generationMode,
        generateAudio: true,
        watermark: false,
        ...(vegaSelected
          ? { referenceUrls: vegaRequest.referenceUrls }
          : {
              audio: true,
              promptExtend: false,
              wanxMedia: wanxRequest.media.map((item) => ({ type: item.type, url: item.url })),
            }),
        clientSubmissionScope: `episode-mini:${projectId}:${episodeId}:${segment.segmentIndex}`,
      });
      if (!isCurrentScope(requestScopeKey)) return null;
      const updated = await persistSegmentPatch({
        id: segment.id,
        segmentIndex: segment.segmentIndex,
        taskId: task.taskId,
        internalTaskId: task.internalTaskId,
        videoProvider: generationRequest.provider || WANX_VIDEO_PROVIDER,
        videoModel: generationRequest.model,
        videoResolution: generationRequest.resolution,
        videoRatio: generationRequest.ratio,
        // Ordinary Wan3 lighting lives in [美学前缀]. One-click lighting stays detached
        // from the saved storyboard and is appended only to the outbound prompt above.
        lightingPrompt: !wan3Selected || isOneClickFlowSegment(segment) ? firstText(segment.lightingPrompt) : "",
        videoUrl: null,
        originalVideoUrl: null,
        thumbnailUrl: null,
        status: "video_processing",
        errorMessage: null,
      });
      if (!updated || !isCurrentScope(requestScopeKey)) return null;
      pollSegmentVideo(
        updated.segmentIndex,
        task.taskId,
        generationRequest.provider || WANX_VIDEO_PROVIDER,
      );
      if (!silent) toast.success(`分镜组 ${updated.segmentIndex} 视频已提交`);
      return updated;
    }
    const requestedSeedance25Provider = normalizeSeedance25SubmissionProvider(requestedProvider);
    // Old localStorage values used hyphenated aliases. Always put the canonical vendor ID on
    // the wire so a 2.0 project can never interpret an explicit 2.5 selection as its 2.0 line.
    const submissionProvider = firstText(requestedSeedance25Provider, requestedProvider, activeVideoProvider);
    assertSeedance25SubmissionProvider(submissionProvider);
    const submissionCapabilities = getSeedance25Capabilities(submissionProvider);
    const submissionIsSeedance25 = Boolean(submissionCapabilities);
    if (!submissionIsSeedance25 && !isSeedanceVideoProvider(submissionProvider)) {
      throw new Error("项目视频厂商配置尚未加载，请刷新项目后重试");
    }
    if (requestedModel.toLowerCase() === SEEDANCE25_MODEL && !submissionIsSeedance25) {
      throw new Error("即梦 2.5 缺少明确厂商线路，请在视频设置中重新选择厂商");
    }
    const submissionModel = submissionIsSeedance25
      ? submissionCapabilities.model
      : firstText(requestedModel, videoModel);
    const submissionProviderLabel = getVideoProviderLabel(submissionProvider);
    const assetGroupsForRequest = await getAssetsForUse();
    if (!isCurrentScope(requestScopeKey)) return null;
    const savedBindings = segment?.assetRefs?._bindings;
    if (isDomesticOfficialSeedance(submissionProvider) && Array.isArray(savedBindings)) {
      buildGuaVideoContents(savedBindings, '', { provider: submissionProvider });
    }
    if (submissionIsSeedance25 && validateSeedance25References(submissionProvider, savedBindings).rejected.length) {
      throw new Error('分镜参考素材缺少可用原始 URL，请重新绑定原始图片、视频或音频；素材库 ID 不能替代原图');
    }
    const referenceSegment = submissionIsSeedance25 && Array.isArray(savedBindings)
      ? { ...segment, assetRefs: { ...segment.assetRefs, _bindings: savedBindings.map(item => ({ ...item, url: getSeedance25ReferenceUrl(item) })) } }
      : segment;
    let { references } = buildSegmentPromptWithReferences(referenceSegment, assetGroupsForRequest, assetMetaMap, referenceImageLimit);
    if (!submissionIsSeedance25) {
      const latestMaps = await refreshStatusMap?.();
      if (!isCurrentScope(requestScopeKey)) return null;
      references = mergeGuaMetaForReferences(references, latestMaps?.assetMetaMap || assetMetaMap);
    }
    const promptWithReferences = applySegmentReferenceMarkersToPrompt(segment, references);
    const prompt = isOneClickFlowSegment(segment)
      ? promptWithReferences
      : stripStandaloneLightingBlocks(promptWithReferences);
    const nonImageReferences = references.filter(isSeedance25NonImageReference);
    if (submissionIsSeedance25 && !supportsSeedance25MediaReferences(submissionProvider) && (nonImageReferences.length || /@(视频|音频)\s*\d+/i.test(prompt))) {
      const names = nonImageReferences.map((item) => firstText(item?.name, "未命名媒体")).join("、");
      throw new Error(
        `${submissionProviderLabel} 只支持参考图片，不支持视频引用${names ? `（${names}）` : ""}。请移除 @视频N/视频素材，或改用项目原视频模型`,
      );
    }
    const directImageReferences = references;
    if (submissionIsSeedance25) {
      const validation = validateSeedance25References(submissionProvider, directImageReferences);
      if (validation.rejected.length > 0) {
        throw new Error(`${submissionProviderLabel} 检测到 ${validation.rejected.length} 个无效参考项；请使用当前线路支持的原始素材 URL 或图片数据，不能传其他厂商的素材库 ID`);
      }
      if (references.filter((item) => referenceMediaType(item) === "image").length > submissionCapabilities.maxReferenceImages) {
        throw new Error(
          `${submissionProviderLabel} 单次最多支持 ${submissionCapabilities.maxReferenceImages} 张参考图，当前匹配到 ${validation.accepted.length} 张，请减少人物、场景或道具参考图`,
        );
      }
    }
    const seedance25ReferencePayload = submissionIsSeedance25
      ? buildSeedance25ReferencePayload(submissionProvider, directImageReferences)
      : null;
    const rawGuaVideoContents = submissionIsSeedance25
      ? []
      : buildGuaVideoContents(references, prompt, { provider: submissionProvider });
    const guaVideoContents = submissionIsSeedance25
      ? rawGuaVideoContents
      : await preflightVideoAssetContents({
          provider: submissionProvider,
          projectId,
          contents: rawGuaVideoContents,
          context: {
            episodeId,
            shotNumber: segment.segmentIndex,
            name: firstText(segment.title, `小故事板 ${segment.segmentIndex}`),
            source: "storyboard",
            sourceDetail: "episode_mini_storyboard_video_preflight",
          },
          shouldContinue: () => isCurrentScope(requestScopeKey),
          onStatus: ({ status }) => {
            if (status === "Processing" && isCurrentScope(requestScopeKey)) {
              setProgressText("正在等待参考图入库");
            }
          },
        });
    if (!submissionIsSeedance25) {
      references = mergePreparedVideoAssetMetadata(references, guaVideoContents, submissionProvider);
    }
    const submittedPrompt = submissionIsSeedance25
      ? normalizeSeedance25ReferencePrompt(submissionProvider, prompt, references)
      : guaVideoContents.find((item) => item.type === "text")?.text || prompt;
    const mediaRefs = submissionIsSeedance25
      ? (seedance25ReferencePayload.guaVideoContents || seedance25ReferencePayload.referenceUrls)
      : guaVideoContents.filter((item) => item.type === "image_url" || item.type === "video_url" || (isDomesticOfficialSeedance(submissionProvider) && item.type === 'audio_url'));
    const preferredGuaVideoMode = firstText(segment?.guaVideoMode, segment?.spatial?.guaVideoMode);
    const requestedResolution = videoResolution;
    const requestedRatio = videoRatio;
    const submittedResolution = submissionIsSeedance25
      ? normalizeSeedance25Resolution(submissionProvider, requestedResolution)
      : isDomesticOfficialSeedance(submissionProvider)
      ? normalizeDomesticSeedanceResolution(submissionModel, requestedResolution)
      : normalizeMiniStoryboardVideoResolution(requestedResolution, submissionModel);
    const submittedRatio = submissionIsSeedance25
      ? normalizeSeedance25Ratio(submissionProvider, requestedRatio)
      : requestedRatio;
    if (!mediaRefs.length && !silent) {
      toast.warning("当前分镜组没有匹配到参考图或参考视频，将按纯文本视频生成");
    }
    const updated = await authenticatedFetch(`${API_HOST}/api/episode-mini-storyboard/video/submit`, {
      method: "POST",
      body: JSON.stringify({
        projectId,
        episodeId,
        id: segment.id,
        segmentIndex: segment.segmentIndex,
        provider: submissionProvider,
        model: submissionModel,
        resolution: submittedResolution,
        ratio: submittedRatio,
        lightingPrompt: firstText(segment.lightingPrompt),
        prompt: submittedPrompt,
        ...(submissionIsSeedance25
          ? {
              ...seedance25ReferencePayload,
              generationMode: mediaRefs.length ? "reference" : "text2video",
              ...(submissionCapabilities.supportsGenerateAudioControl ? { generateAudio: true } : {}),
            }
          : {
              generateAudio: true,
              referenceImages: sanitizeVideoAuxiliaryReferenceItems(references.map((item) => ({
                id: item.id,
                type: item.type,
                name: item.name,
                role: item.role,
                mediaType: referenceMediaType(item),
                url: item.url,
                sourceAssetId: item.sourceAssetId,
                variantIndex: item.variantIndex,
                variantTitle: item.variantTitle,
                parentName: item.parentName,
                isVariant: item.isVariant,
                guaVideoAssetId: item.guaVideoAssetId,
                guaVideoAssetStatus: item.guaVideoAssetStatus,
                engGuaVideoAssetId: item.engGuaVideoAssetId,
                engGuaVideoAssetStatus: item.engGuaVideoAssetStatus,
                guaOminiAssetId: item.guaOminiAssetId,
                guaOminiAssetStatus: item.guaOminiAssetStatus,
                dreaminaProxyAssetId: item.dreaminaProxyAssetId,
                dreaminaProxyAssetStatus: item.dreaminaProxyAssetStatus,
                assetProvider: item.assetProvider,
              })), submissionProvider),
              guaVideoMode: preferredGuaVideoMode || (mediaRefs.length ? "ref2v" : "t2v"),
              guaVideoContents,
            }),
      }),
    });
    if (!isCurrentScope(requestScopeKey)) return null;
    mergeSegment(updated, { preserveEditableFields: true });
    pollSegmentVideo(updated.segmentIndex, updated.taskId, updated.videoProvider || updated.provider || submissionProvider);
    if (!silent) toast.success(`分镜组 ${updated.segmentIndex} 视频已提交`);
    return updated;
  }, [activeVideoProvider, assetMetaMap, durationSeconds, episodeId, getAssetsForUse, isCurrentScope, mergeSegment, persistSegmentPatch, pollSegmentVideo, projectId, referenceImageLimit, refreshStatusMap, scopeKey, toast, vegaSelected, videoModel, videoRatio, videoResolution, wan3Selected]);

  const generateCurrentVideo = async () => {
    if (!currentGenerationPrompt) {
      toast.error("当前分镜组没有视频提示词");
      return;
    }
    const requestScopeKey = scopeKey;
    setLoading("video-current");
    try {
      let segmentForSubmit = selectedSegment;
      if (editing && draft) {
        segmentForSubmit = await persistSegmentPatch(buildEditableSegmentPatch(draft));
        if (!segmentForSubmit) return;
        setEditing(false);
        setDraft(null);
        draftBaselineRef.current = null;
      }
      await submitSegmentVideo(segmentForSubmit);
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      toast.error(error.message || "提交当前视频失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) setLoading("");
    }
  };

  const requestGenerateCurrentVideo = () => {
    if (!currentGenerationPrompt) {
      toast.error("当前分镜组没有视频提示词");
      return;
    }
    if (isVideoProcessing(selectedSegment)) {
      toast.warning("当前分镜组的视频正在生成中，请等待完成后再操作，避免重复计费");
      return;
    }
    if (isVideoSuccess(selectedSegment)) {
      setOperationConfirm({
        title: "重新生成当前视频",
        message: "当前分镜组已经有视频。继续会重新提交生成任务，完成后会替换当前预览并写入生成历史。确定继续？",
        confirmLabel: "重新生成",
        onConfirm: generateCurrentVideo,
      });
      return;
    }
    generateCurrentVideo();
  };

  const generateRemaining = async () => {
    const requestScopeKey = scopeKey;
    setLoading("generate");
    try {
      let list = segments;
      if (!list.length) {
        list = await generatePromptSegments({ silent: true });
      }
      if (!isCurrentScope(requestScopeKey)) return;
      const targets = list.filter((segment) => (
        segment?.seedancePrompt && !isVideoSuccess(segment) && !isVideoProcessing(segment)
      ));
      let submittedCount = 0;
      const failedSegments = [];
      for (const segment of targets) {
        if (!isCurrentScope(requestScopeKey)) return;
        try {
          const submitted = await submitSegmentVideo(segment, { silent: true });
          if (submitted) submittedCount += 1;
        } catch (submitError) {
          failedSegments.push({
            segmentIndex: segment.segmentIndex,
            message: submitError.message || "提交失败",
          });
        }
        await new Promise((resolve) => window.setTimeout(resolve, 600));
      }
      if (!isCurrentScope(requestScopeKey)) return;
      if (!targets.length) {
        toast.success("没有需要生成的视频");
      } else if (failedSegments.length) {
        const failedIndexes = failedSegments.map((item) => item.segmentIndex).join("、");
        toast.warning(`已提交 ${submittedCount}/${targets.length} 个视频；片段 ${failedIndexes} 提交失败，可稍后单独重试`);
      } else {
        toast.success(`已提交 ${submittedCount} 个剩余视频`);
      }
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      toast.error(error.message || "生成分镜视频失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) setLoading("");
    }
  };

  const requestGenerateRemaining = () => {
    const generatedCount = segments.filter((segment) => isVideoSuccess(segment)).length;
    const processingCount = segments.filter((segment) => isVideoProcessing(segment)).length;
    if (generatedCount || processingCount) {
      setOperationConfirm({
        title: "生成分镜视频",
        message: `当前已有 ${generatedCount} 个已生成视频、${processingCount} 个生成中视频。本次只会提交未生成的视频片段，不会覆盖已完成视频。确定继续？`,
        confirmLabel: "继续生成",
        onConfirm: generateRemaining,
      });
      return;
    }
    generateRemaining();
  };

  const generateEpisodeMusic = async () => {
    if (!timelineSegments.length) {
      toast.warning("先拆分小故事板，再生成当前集音乐");
      return;
    }
    const requestScopeKey = scopeKey;
    setMusicGenerating(true);
    setMusicProgressText("正在规划音乐段落");
    try {
      const provider = localStorage.getItem("ai_provider") || "WANGSUGEM";
      const completion = await planMiniStoryboardMusicCues({
        projectId,
        episodeId,
        provider,
        totalDurationSeconds: totalDuration,
        userRequest: "为当前集生成能覆盖完整粗剪时间线的纯音乐，按情绪转折切换，不要人声、歌词、口播或旁白。",
      }, {
        onProgress: (status) => {
          if (isCurrentScope(requestScopeKey)) {
            setMusicProgressText(firstText(status?.stageText, status?.stage, status?.status, "正在规划音乐段落"));
          }
        },
      });
      const planned = Array.isArray(completion?.result?.cues)
        ? completion.result.cues
        : Array.isArray(completion?.cues)
          ? completion.cues
          : [];
      let cues = planned.length ? planned : await loadMusicCues();
      if (isCurrentScope(requestScopeKey)) setMusicCues(cues);
      for (let index = 0; index < cues.length; index += 1) {
        const cue = cues[index];
        if (!cue?.id && !cue?.cueIndex) continue;
        if (isCurrentScope(requestScopeKey)) setMusicProgressText(`正在生成音乐 ${index + 1}/${cues.length}`);
        try {
          const result = getMusicCompletionResult(await generateMusicAudio({
            projectId,
            episodeId,
            title: firstText(cue.title, `第${index + 1}段情绪音乐`),
            prompt: firstText(cue.musicPrompt, cue.prompt),
            style: firstText(cue.styleTags, cue.style, "短剧BGM，纯音乐，情绪递进"),
            tags: getMusicCueTags(cue),
            instrumental: true,
            durationSeconds: getMusicCueDurationForGeneration(cue),
            language: "zh",
            model: "suno-v5.5",
            source: "mini_storyboard_music",
            miniStoryboardCueId: cue.id,
            miniStoryboardCueIndex: cue.cueIndex,
            cueStartSeconds: cue.startSeconds,
            cueEndSeconds: cue.endSeconds,
          }, {
            onProgress: (status) => {
              if (isCurrentScope(requestScopeKey)) {
                setMusicProgressText(firstText(status?.stageText, status?.stage, status?.status, `正在生成音乐 ${index + 1}/${cues.length}`));
              }
            },
          }));
          const saved = await updateMiniStoryboardMusicCue({
            ...cue,
            audioUrl: firstText(result.audioUrl, result.url, result.resultUrl),
            thumbnailUrl: firstText(result.thumbnailUrl, result.coverUrl),
            taskId: firstText(result.taskId, result.internalTaskId),
            providerTaskId: firstText(result.providerTaskId),
            provider: "xingtu_suno",
            model: firstText(result.model, "suno-v5.5"),
            status: "audio_success",
            errorMessage: "",
          });
          if (isCurrentScope(requestScopeKey)) {
            setMusicCues((current) => current.map((item) => (
              Number(item.id) === Number(saved.id) || Number(item.cueIndex) === Number(saved.cueIndex) ? saved : item
            )));
          }
          cues = cues.map((item) => (
            Number(item.id) === Number(saved.id) || Number(item.cueIndex) === Number(saved.cueIndex) ? saved : item
          ));
        } catch (cueError) {
          const saved = await updateMiniStoryboardMusicCue({
            ...cue,
            status: "audio_failed",
            errorMessage: cueError.message || "音乐生成失败",
          }).catch(() => null);
          if (saved && isCurrentScope(requestScopeKey)) {
            setMusicCues((current) => current.map((item) => (
              Number(item.id) === Number(saved.id) || Number(item.cueIndex) === Number(saved.cueIndex) ? saved : item
            )));
          }
        }
      }
      await loadMusicCues();
      if (isCurrentScope(requestScopeKey)) toast.success("当前集纯音乐已生成");
    } catch (error) {
      if (isCurrentScope(requestScopeKey)) toast.error(error.message || "生成当前集音乐失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) {
        setMusicGenerating(false);
        setMusicProgressText("");
      }
    }
  };

  const beginEdit = () => {
    setPlaying(false);
    const nextDraft = {
      ...selectedSegment,
      // Prompt editing is text-only. Reference bindings have their own explicit picker
      // and must never be rebuilt merely because an old prompt contains @图片 markers.
      __syncAssetRefsFromPrompt: false,
      __durationDirty: false,
      // One-click flow keeps its saved prompt byte-for-byte. The submission preview
      // may contain reference markers, but opening and saving the editor must not
      // turn that derived preview into a new prompt.
      seedancePrompt: isOneClickFlowSegment(selectedSegment)
        ? String(selectedSegment?.seedancePrompt ?? "")
        : formatSeedancePrompt(selectedSubmissionPrompt || selectedSegment?.seedancePrompt),
    };
    draftBaselineRef.current = {
      segmentIndex: nextDraft.segmentIndex,
      seedancePrompt: nextDraft.seedancePrompt,
      durationSeconds: String(nextDraft.durationSeconds ?? ""),
    };
    setDraft(nextDraft);
    setEditing(true);
  };

  const saveDraft = async () => {
    if (!draft) return;
    const requestScopeKey = scopeKey;
    setLoading("save");
    try {
      const saved = await persistSegmentPatch(buildEditableSegmentPatch(draft));
      if (!isCurrentScope(requestScopeKey) || !saved) return;
      setEditing(false);
      setDraft(null);
      draftBaselineRef.current = null;
      toast.success("已保存当前分镜组");
    } catch (error) {
      toast.error(error.message || "保存失败，请检查 JSON 格式", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) setLoading("");
    }
  };

  const applyLightingPreset = async (preset) => {
    if (!preset) return;
    const requestScopeKey = scopeKey;
    const base = editing && draft ? draft : selectedSegment;
    if (wan3Selected && !isOneClickFlowSegment(base)) return;
    if (!base?.seedancePrompt) {
      toast.error("当前分镜组没有提示词，先拆分分镜再选择打光");
      return;
    }
    const next = {
      id: base.id,
      segmentIndex: base.segmentIndex,
      lightingPrompt: firstText(preset.prompt),
    };
    setLoading("lighting");
    try {
      const saved = await persistSegmentPatch(next);
      if (!isCurrentScope(requestScopeKey) || !saved) return;
      if (editing) {
        setDraft((current) => Number(current?.segmentIndex) === Number(base.segmentIndex)
          ? { ...current, lightingPrompt: firstText(saved.lightingPrompt) }
          : current);
      }
      setGlobalLightingPresetId(inferGlobalLightingPresetId(segments.map((segment) => (
        Number(segment.segmentIndex) === Number(saved.segmentIndex) ? saved : segment
      )), wan3Selected));
      toast.success(`已设为「${preset.name}」并自动保存`);
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      toast.error(error.message || "设置打光失败", 0);
    } finally {
      if (isCurrentScope(requestScopeKey)) setLoading("");
    }
  };

  const copyPrompt = async () => {
    const lighting = (!wan3Selected || isOneClickFlowSegment(activeReferenceSegment))
      ? inferLightingPrompt(activeReferenceSegment)
      : "";
    const text = appendDetachedLightingPrompt(displaySubmissionPrompt, lighting);
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("视频提示词已复制");
    } catch {
      toast.error("复制失败，请手动选择文本复制");
    }
  };

  const triggerDownload = useCallback((url, filename) => {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.target = "_blank";
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }, []);

  const buildVideoDownloadName = useCallback((segment, url) => {
    const dramaName = sanitizeFileNamePart(`项目${projectId || ""}`);
    const episodeName = sanitizeFileNamePart(buildEpisodeDownloadLabel(episode, episodeId));
    const segmentName = `分镜组${String(Number(segment?.segmentIndex) || 0).padStart(2, "0")}`;
    return `${dramaName}_${episodeName}_${segmentName}${videoFileExtension(url)}`;
  }, [episode, episodeId, projectId]);

  const buildZipDownloadName = useCallback(() => {
    const dramaName = sanitizeFileNamePart(`项目${projectId || ""}`);
    const episodeName = sanitizeFileNamePart(buildEpisodeDownloadLabel(episode, episodeId));
    return `${dramaName}_${episodeName}_小故事板视频.zip`;
  }, [episode, episodeId, projectId]);

  const downloadVideoWithName = useCallback(async (url, filename) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      triggerDownload(objectUrl, filename);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch {
      triggerDownload(url, filename);
    }
  }, [triggerDownload]);

  const downloadCurrentVideo = useCallback(async () => {
    const url = firstText(selectedSegment?.videoUrl);
    if (!isVideoSuccess(selectedSegment) || !url) {
      toast.warning("当前分镜组还没有可下载的视频");
      return;
    }
    setDownloadingSegmentIndex(selectedSegment.segmentIndex);
    try {
      await downloadVideoWithName(url, buildVideoDownloadName(selectedSegment, url));
      toast.success(`分镜组 ${selectedSegment.segmentIndex} 视频已开始下载`);
    } finally {
      setDownloadingSegmentIndex(null);
    }
  }, [buildVideoDownloadName, downloadVideoWithName, selectedSegment, toast]);

  const downloadHistoryVideo = useCallback(async (item) => {
    const url = firstText(item?.videoUrl, item?.resultUrl, item?.url);
    if (!url) {
      toast.warning("这个历史版本没有可下载的视频");
      return;
    }
    await downloadVideoWithName(url, buildVideoDownloadName(selectedSegment, url));
  }, [buildVideoDownloadName, downloadVideoWithName, selectedSegment, toast]);

  const downloadAllVideos = useCallback(async () => {
    if (!downloadableSegments.length) {
      toast.warning("还没有已完成的视频可下载");
      return;
    }
    setDownloadingAllVideos(true);
    try {
      const params = new URLSearchParams({
        projectId: String(projectId),
        episodeId: String(episodeId),
        dramaName: `项目${projectId || ""}`,
        episodeName: buildEpisodeDownloadLabel(episode, episodeId),
      });
      const { blob, videoCount, hasFailureNote } = await fetchVerifiedVideoZip(`${API_HOST}/api/episode-mini-storyboard/videos/download?${params.toString()}`, {
        headers: downloadHeaders(),
      });
      const objectUrl = URL.createObjectURL(blob);
      triggerDownload(objectUrl, buildZipDownloadName());
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
      if (hasFailureNote) {
        toast.warning(`压缩包包含 ${videoCount}/${downloadableSegments.length} 个视频；部分视频下载失败，请查看包内的下载失败说明`);
      } else if (videoCount < downloadableSegments.length) {
        toast.warning(`压缩包仅包含 ${videoCount}/${downloadableSegments.length} 个视频，其余视频暂未打包`);
      } else {
        toast.success(`已开始下载 ${videoCount} 个小故事板视频的压缩包`);
      }
    } catch (error) {
      toast.error(error.message || "小故事板视频压缩包下载失败，请重试", 0);
    } finally {
      setDownloadingAllVideos(false);
    }
  }, [buildZipDownloadName, downloadableSegments, episode, episodeId, projectId, toast, triggerDownload]);

  const eraseCurrentSubtitle = useCallback(async () => {
    const requestScopeKey = scopeKey;
    const segmentIndex = Number(selectedSegment?.segmentIndex || 0);
    const originalVideoUrl = firstText(selectedSegment?.originalVideoUrl);
    if (!isVideoSuccess(selectedSegment)) {
      toast.warning("当前分镜组还没有可去字幕的视频");
      return;
    }
    if (!originalVideoUrl) {
      toast.warning("缺少方舟原始视频链接，重新生成当前视频后再去字幕");
      return;
    }
    if (subtitlePollsRef.current.has(segmentIndex)) {
      toast.info("当前分镜组正在去字幕");
      return;
    }

    setErasingSubtitleIndexes((current) => {
      const next = new Set(current);
      next.add(segmentIndex);
      return next;
    });
    try {
      const response = await submitEraseSubtitle({ videoUrl: originalVideoUrl });
      if (!isCurrentScope(requestScopeKey)) return;
      const taskId = firstText(response?.taskId, response?.task_id, response?.id);
      if (!taskId) throw new Error("后端未返回去字幕任务ID");
      toast.info(`分镜组 ${segmentIndex} 去字幕任务已提交`);

      const stopPolling = startPostProcessPolling("erase-subtitle", taskId, {
        interval: 5000,
        onSuccess: async (data) => {
          if (!isCurrentScope(requestScopeKey)) return;
          subtitlePollsRef.current.delete(segmentIndex);
          setErasingSubtitleIndexes((current) => {
            const next = new Set(current);
            next.delete(segmentIndex);
            return next;
          });
          const nextVideoUrl = firstText(data?.videoUrl, data?.resultVideoUrl, data?.result?.video_url, data?.result?.videoUrl);
          if (!nextVideoUrl) {
            toast.error("去字幕完成但没有返回视频地址", 0);
            return;
          }
          try {
            await persistSegmentPatch({
              id: selectedSegment?.id,
              segmentIndex,
              videoUrl: nextVideoUrl,
              originalVideoUrl: "",
              thumbnailUrl: firstText(selectedSegment?.thumbnailUrl, nextVideoUrl),
              status: "video_success",
              error: "",
              errorMessage: "",
            });
            if (!isCurrentScope(requestScopeKey)) return;
            toast.success(`分镜组 ${segmentIndex} 去字幕完成`);
          } catch (error) {
            if (!isCurrentScope(requestScopeKey)) return;
            toast.error(error.message || "去字幕结果保存失败", 0);
          }
        },
        onError: (message) => {
          if (!isCurrentScope(requestScopeKey)) return;
          subtitlePollsRef.current.delete(segmentIndex);
          setErasingSubtitleIndexes((current) => {
            const next = new Set(current);
            next.delete(segmentIndex);
            return next;
          });
          toast.error(message || "去字幕失败", 0);
        },
      });
      subtitlePollsRef.current.set(segmentIndex, stopPolling);
    } catch (error) {
      if (!isCurrentScope(requestScopeKey)) return;
      subtitlePollsRef.current.delete(segmentIndex);
      setErasingSubtitleIndexes((current) => {
        const next = new Set(current);
        next.delete(segmentIndex);
        return next;
      });
      toast.error(error.message || "提交去字幕失败", 0);
    }
  }, [isCurrentScope, persistSegmentPatch, scopeKey, selectedSegment, toast]);

  const openBlockingEditor = useCallback((plan) => {
    setPlaying(false);
    setBlockingEditorPlan(plan === undefined ? selectedBlockingPlan : plan);
    setBlockingEditorOpen(true);
  }, [selectedBlockingPlan]);

  const handleBlockingSaved = useCallback(async ({ plan, applied, unassigned, deleted, referenceAsset }) => {
    await loadBlockingData();
    // 站位参考图入库后刷新状态地图，保证参考图角标（已入库/处理中）显示最新状态
    await refreshStatusMap?.();
    // 站位参考图自身的状态轮询：否则后端资源一直停在 Processing，角标永远不显示"已入库"
    if (applied && referenceAsset?.assetId && !["Active", "Failed"].includes(firstText(referenceAsset?.status))) {
      void pollReferenceAssetStatus({
        url: firstText(plan?.modelReferenceUrl, plan?.directorPreviewUrl),
        assetId: referenceAsset.assetId,
        provider: firstText(referenceAsset?.provider, defaultVideoProvider),
        requestScopeKey: scopeKey,
      });
    }
    if (deleted) {
      toast.success("站位方案已删除");
      setBlockingEditorOpen(false);
      setBlockingEditorPlan(null);
      return;
    }
    if (unassigned) {
      toast.success("已取消该方案的片段应用");
      setBlockingEditorPlan(plan || null);
      return;
    }
    if (applied) {
      toast.success("站位参考图已确认并应用到当前片段");
      setBlockingEditorOpen(false);
      setBlockingEditorPlan(null);
      return;
    }
    setBlockingEditorPlan(plan || null);
    toast.success("站位方案草稿已保存");
  }, [defaultVideoProvider, loadBlockingData, pollReferenceAssetStatus, refreshStatusMap, scopeKey, toast]);

  const openBlockingTrackPlan = useCallback((block) => {
    openBlockingEditor(block?.plan || null);
  }, [openBlockingEditor]);

  const currentShots = Array.isArray(selectedSegment?.shots) ? selectedSegment.shots : [];
  const oneClickFlowTimingLabel = getOneClickFlowTimingLabel(
    activeReferenceSegment,
    getSubmissionVideoDuration(activeReferenceSegment, activeVideoProvider, durationSeconds),
  );
  const oneClickFlowDurationError = getOneClickFlowDurationError(
    selectedSegment,
    getSubmissionVideoDuration(selectedSegment, activeVideoProvider, durationSeconds),
  );
  const oneClickFlowWarnings = isOneClickFlowSegment(selectedSegment)
    ? [...new Set([
        oneClickFlowDurationError,
        ...(Array.isArray(selectedSegment?.spatial?.qualityWarnings) ? selectedSegment.spatial.qualityWarnings : [])
          .filter((message) => message !== selectedSegment?.spatial?.generationBlockedReason),
      ].map((message) => firstText(message)).filter(Boolean))]
    : [];
  const hasPromptSegments = segments.some((segment) => firstText(segment?.seedancePrompt));
  const hasOneClickLightingEligibleSegments = segments.some((segment) => firstText(segment?.seedancePrompt) && isOneClickFlowSegment(segment));
  const hasLightingEligibleSegments = !wan3Selected
    ? hasPromptSegments
    : hasOneClickLightingEligibleSegments;
  const needsSplit = segmentsLoadReady && !hasPromptSegments;
  const currentErasingSubtitle = erasingSubtitleIndexes.has(Number(selectedSegment?.segmentIndex || 0));
  const currentCanEraseSubtitle = isVideoSuccess(selectedSegment) && Boolean(firstText(selectedSegment?.originalVideoUrl));
  const openRevisionIssueCount = revisionIssues.filter((issue) => !["resolved", "ignored"].includes(firstText(issue?.status).toLowerCase())).length;
  const shellDisabled = loading === "load";
  const formalScript = normalizeEpisodeScript(episode);
  const sourceViewPage = resolveMiniStoryboardSourcePage(timelineSegments, selectedSegment, sourceViewKey);
  const sourceViewSegment = sourceViewPage.segment;
  const sourceViewOriginal = firstText(sourceViewSegment?.sourceText);
  const episodeSourceFallback = firstText(formalScript, fallbackScript);
  const selectedSourceText = firstText(sourceViewOriginal, episodeSourceFallback);
  const sourceFallbackToEpisodeScript = sourceViewPage.total > 0 && !sourceViewOriginal && Boolean(episodeSourceFallback);
  const sourceViewTitle = sourceViewSegment
    ? firstText(sourceViewSegment.title, `分镜组 ${String(sourceViewSegment.segmentIndex || sourceViewPage.index + 1).padStart(2, "0")}`)
    : "本集原文";
  const openSourceViewer = () => {
    setSourceViewKey(getMiniStoryboardSourcePageKey(selectedSegment));
    if (!showInlineAssetPanel) setAssetDrawerOpen(true);
    setSidePanelSection("source");
  };
  const toggleSourceViewer = () => {
    if (sourceDrawerOpen) setSidePanelSection("assets");
    else {
      setSourceViewKey((current) => current || getMiniStoryboardSourcePageKey(selectedSegment));
      if (!showInlineAssetPanel) setAssetDrawerOpen(true);
      setSidePanelSection("source");
    }
  };
  const toggleAssetSection = () => {
    setSidePanelSection((current) => current === "assets" ? "source" : "assets");
  };
  const moveSourcePage = (offset) => {
    const target = timelineSegments[sourceViewPage.index + offset];
    if (target) setSourceViewKey(getMiniStoryboardSourcePageKey(target));
  };
  const scriptMissing = loading !== "load" && (!episode || !formalScript);
  const remainingVideoSegments = segments.filter((segment) => (
    firstText(segment?.seedancePrompt)
    && !isVideoSuccess(segment)
    && !isVideoProcessing(segment)
  ));
  const splitCostRequest = formalScript
    ? {
        type: "text",
        model: localStorage.getItem("ai_provider") || "wangsugem",
        promptCharacters: formalScript.length,
        expectedOutputCharacters: Math.max(1_200, Math.round(formalScript.length * 0.7)),
        count: 1,
        source: "episode_mini_storyboard_split",
      }
    : null;
  const remainingVideoCostRequest = remainingVideoSegments.length
    ? {
        type: "video",
        model: activeVideoProvider,
        subModel: seedance25Selected ? seedance25Capabilities.model : videoModel,
        resolution: videoResolution,
        count: remainingVideoSegments.length,
        totalDurationSeconds: remainingVideoSegments.reduce(
          (sum, segment) => sum + getSubmissionVideoDuration(
            segment,
            activeVideoProvider,
            durationSeconds,
          ),
          0,
        ),
        source: "episode_mini_storyboard_video",
      }
    : null;
  const initialLoading = loading === "load" && !episode && !segments.length;
  const activeVideoModelName = videoModelOptions.find((item) => item.id === videoModel)?.name || "视频模型";
  const videoResolutionOptions = vegaSelected
    ? WAND_VEGA_RESOLUTIONS
    : wan3Selected
    ? WANX_VIDEO_RESOLUTIONS
    : seedance25Selected
    ? seedance25Capabilities.resolutions
    : isDomesticOfficialSeedance(activeVideoProvider)
    ? getDomesticSeedanceResolutions(videoModel)
    : isFastVideoModel(videoModel) ? FAST_VIDEO_RESOLUTIONS : VIDEO_RESOLUTIONS;
  const videoRatioOptions = vegaSelected
    ? WAND_VEGA_RATIOS
    : wan3Selected
    ? WANX_VIDEO_RATIOS
    : seedance25Selected ? seedance25Capabilities.ratios : VIDEO_RATIOS;
  const splitSettingsSummary = `${activeStoryMode?.name || "拆分设置"} · ${splitDurationSeconds}s/段`;
  const videoSettingsSummary = wan3Selected
    ? `${activeVideoModelName} · ${videoResolution} · ${videoRatio} · 时长${durationLimits.min}-${durationLimits.max}s`
    : seedance25Selected
    ? `${videoProviderLabel} · ${videoResolution} · ${videoRatio} · 时长${durationLimits.min}-${durationLimits.max}s · 成片最长${seedance25Capabilities.maxDuration}s`
    : `${activeVideoModelName} · ${videoResolution} · ${videoRatio} · 时长${durationLimits.min}-${durationLimits.max}s`;
  const workspaceMode = useMemo(() => {
    const width = workspaceSize.width || (typeof window !== "undefined" ? window.innerWidth : 1600);
    const height = workspaceSize.height || (typeof window !== "undefined" ? window.innerHeight : 900);
    if (width < 1180 || height < 720) return "focus";
    if (width < 1480 || height < 820) return "compact";
    return "full";
  }, [workspaceSize]);
  const compactWorkspace = workspaceMode !== "full";
  const focusWorkspace = workspaceMode === "focus";
  const mobileWorkspace = (workspaceSize.width || (typeof window !== "undefined" ? window.innerWidth : 1600)) < 760;
  const showInlineAssetPanel = workspaceMode === "full";
  const normalizedMiniColumnPercents = normalizeMiniWorkspaceColumnPercents(miniColumnPercents);
  const miniRightColumnPercent = normalizedMiniColumnPercents[1] + normalizedMiniColumnPercents[2];
  const miniMainColumns = `minmax(${MINI_WORKSPACE_COLUMN_MIN_WIDTHS[0]}px, ${normalizedMiniColumnPercents[0]}fr) 8px minmax(${MINI_WORKSPACE_COLUMN_MIN_WIDTHS[1] + MINI_WORKSPACE_COLUMN_MIN_WIDTHS[2] + 8}px, ${miniRightColumnPercent}fr)`;
  const miniPromptColumns = `minmax(${MINI_WORKSPACE_COLUMN_MIN_WIDTHS[1]}px, ${normalizedMiniColumnPercents[1]}fr) 8px minmax(${MINI_WORKSPACE_COLUMN_MIN_WIDTHS[2]}px, ${normalizedMiniColumnPercents[2]}fr)`;
  const mainWorkspaceClass = mobileWorkspace
    ? "flex min-h-0 flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto p-2 custom-scrollbar"
    : focusWorkspace
    ? "grid min-h-0 flex-1 grid-cols-[minmax(190px,240px)_minmax(0,1fr)] gap-2 overflow-hidden p-2"
    : compactWorkspace
      ? "grid min-h-0 flex-1 grid-cols-[minmax(220px,300px)_minmax(0,1fr)] gap-2 overflow-hidden p-2"
      : "grid min-h-0 flex-1 grid-cols-[var(--mini-main-columns)] gap-0 overflow-hidden p-2";
  const promptShellClass = mobileWorkspace
    ? "relative grid min-h-[520px] shrink-0 grid-cols-1 gap-2 overflow-visible"
    : showInlineAssetPanel
      ? "relative grid min-h-0 grid-cols-[var(--mini-prompt-columns)] gap-0 overflow-visible"
      : "relative grid min-h-0 grid-cols-1 gap-2 overflow-visible";
  const miniMainGridStyle = workspaceMode === "full" ? { "--mini-main-columns": miniMainColumns } : undefined;
  const miniPromptGridStyle = workspaceMode === "full" ? { "--mini-prompt-columns": miniPromptColumns } : undefined;
  const previewFrameWidth = showInlineAssetPanel
    ? "100%"
    : mobileWorkspace
    ? "min(100%, 176px, calc((100vh - 390px) * 9 / 16))"
    : focusWorkspace
    ? "min(100%, 260px, calc((100vh - 210px) * 9 / 16))"
    : "min(100%, 320px, calc((100vh - 200px) * 9 / 16))";
  useLayoutEffect(() => {
    if (!showInlineAssetPanel || typeof ResizeObserver === "undefined") return undefined;
    const stage = previewStageRef.current;
    const frame = previewFrameRef.current;
    if (!stage || !frame) return undefined;

    const centerPreview = () => {
      stage.scrollTop = Math.max(0, (stage.scrollHeight - stage.clientHeight) / 2);
    };
    const observer = new ResizeObserver(centerPreview);
    observer.observe(stage);
    observer.observe(frame);
    centerPreview();
    return () => observer.disconnect();
  }, [showInlineAssetPanel, initialLoading, segments.length, selectedSegment?.id]);
  const promptContentSizeClass = mobileWorkspace
    ? "min-h-[260px] rounded-xl p-3"
    : focusWorkspace
    ? "min-h-[260px] rounded-xl p-3"
    : compactWorkspace
      ? "min-h-[340px] rounded-xl p-4"
      : "min-h-[420px] rounded-xl p-4";
  const promptTextBoxClass = `${promptContentSizeClass} box-border min-h-full w-full`;
  const promptTextareaClass = `${promptContentSizeClass} h-full w-full resize-none outline-none`;
  const promptTextStyle = mobileWorkspace
    ? { fontSize: "14px", lineHeight: "24px" }
    : focusWorkspace
    ? { fontSize: "12.5px", lineHeight: "22px" }
    : compactWorkspace
      ? { fontSize: "13.5px", lineHeight: "24px" }
      : { fontSize: "15px", lineHeight: "29px" };
  const promptFooterClass = mobileWorkspace
    ? "grid shrink-0 grid-cols-1 gap-2 px-3 py-2 text-xs"
    : focusWorkspace
    ? "grid shrink-0 grid-cols-[96px_minmax(0,1fr)] gap-2 px-3 py-2 text-xs"
    : "grid shrink-0 grid-cols-[120px_minmax(0,1fr)] gap-2 px-4 py-2 text-xs";
  const assetStackClass = showInlineAssetPanel
    ? "mini-workbench__asset-stack flex min-h-0 min-w-0 flex-col gap-2"
    : assetDrawerOpen
      ? "mini-workbench__asset-stack absolute inset-y-0 right-0 z-50 flex min-h-0 w-[390px] max-w-[calc(100%_-_12px)] flex-col gap-2 rounded-xl p-2 shadow-2xl"
      : "hidden";
  const assetStackStyle = showInlineAssetPanel ? undefined : {
    backgroundColor: "var(--color-bg-primary)",
    border: "1px solid var(--color-border-primary)",
  };

  return (
    <div ref={workspaceRef} className="mini-workbench flex h-full min-h-0 flex-col overflow-hidden" style={{ backgroundColor: "var(--color-bg-primary)", color: "var(--color-text-primary)" }}>
      {nextPreloadUrl && (
        <video
          ref={nextVideoPreloadRef}
          src={nextPreloadUrl}
          preload="auto"
          muted
          playsInline
          aria-hidden="true"
          tabIndex={-1}
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            opacity: 0,
            pointerEvents: "none",
          }}
        />
      )}
      <audio ref={musicAudioRef} preload="auto" aria-hidden="true" />
      <div className="mini-workbench__toolbar flex shrink-0 items-center gap-2 px-2.5 py-1.5" style={{ borderBottom: "1px solid var(--color-border-primary)" }}>
        <div className="mini-workbench__toolbar-scroll flex min-w-0 flex-1 flex-nowrap items-center justify-end gap-2 overflow-x-auto pb-1 custom-scrollbar">
          <button
            type="button"
            onClick={() => setSplitSettingsOpen(true)}
            disabled={Boolean(loading === "load")}
            className="inline-flex h-9 shrink-0 items-center gap-2 rounded-full px-3 text-xs disabled:opacity-45"
            style={ghostButtonStyle}
            title="分镜设置"
          >
            <SlidersHorizontal size={15} />
            <span className="font-medium">分镜设置</span>
            <span className="hidden max-w-[110px] truncate opacity-70 md:inline">{splitSettingsSummary}</span>
          </button>
          <div className="inline-flex shrink-0 items-center gap-1">
            <button
              onClick={requestGeneratePromptSegments}
              disabled={Boolean(loading) || scriptMissing || !segmentsLoadReady}
              data-primary-action={needsSplit}
              className="inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
              style={needsSplit ? primaryButtonStyle : ghostButtonStyle}
            >
              {loading === "script" ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              拆分分镜
            </button>
            <EstimatedPoints request={splitCostRequest} compact />
          </div>

          <button
            type="button"
            onClick={requestGenerateOneClickFlowSegments}
            disabled={Boolean(loading) || scriptMissing || !segmentsLoadReady || hasProcessingVideoSegments}
            className="inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
            style={ghostButtonStyle}
            title="使用独立的一键流分镜规则，自动匹配参考图；原拆分规则保持不变"
          >
            {loading === "one-click-flow" ? <Loader2 size={16} className="animate-spin" /> : <Film size={16} />}
            一键流分镜
          </button>

          <span className="h-7 w-px shrink-0" style={{ backgroundColor: "var(--color-border-primary)" }} />

          <button
            type="button"
            onClick={() => setVideoSettingsOpen(true)}
            disabled={Boolean(loading === "load")}
            className="inline-flex h-9 shrink-0 items-center gap-2 rounded-full px-3 text-xs disabled:opacity-45"
            style={ghostButtonStyle}
            title="视频设置"
          >
            <Settings2 size={15} />
            <span className="font-medium">视频设置</span>
            <span className="hidden max-w-[190px] truncate opacity-70 lg:inline">{videoSettingsSummary}</span>
          </button>
          {(!wan3Selected || hasOneClickLightingEligibleSegments) && (
            <LightingPresetPicker
              disabled={Boolean(loading) || !hasLightingEligibleSegments}
              label="全局打光"
              activeId={globalLightingPresetId === "none" ? "none" : globalLightingPreset?.id || ""}
              activeName={globalLightingPresetId === "none" ? "全局：无打光" : globalLightingPreset ? `全局：${globalLightingPreset.name}` : ""}
              menuPlacement="down"
              onSelect={applyGlobalLightingPreset}
            />
          )}
          <div className="inline-flex shrink-0 items-center gap-1">
            <button
              onClick={requestGenerateRemaining}
              disabled={Boolean(loading) || (scriptMissing && !hasPromptSegments)}
              data-primary-action={!needsSplit}
              className="inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
              style={needsSplit ? ghostButtonStyle : primaryButtonStyle}
            >
              {loading === "generate" ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              生成视频
            </button>
            <EstimatedPoints request={remainingVideoCostRequest} compact />
          </div>

          <MiniActionMenu label="更多" icon={<MoreHorizontal size={15} />}>
            {(close) => (
              <>
                <MiniMenuItem
                  icon={<Move3d size={15} />}
                  title="站位关系"
                  badge={blockingPlans.length || null}
                  disabled={Boolean(loading === "load") || !timelineSegments.length}
                  onClick={() => openBlockingEditor()}
                  close={close}
                />
                <MiniMenuItem
                  icon={<MessageSquareWarning size={15} />}
                  title="问题反馈"
                  badge={openRevisionIssueCount || null}
                  disabled={Boolean(loading === "load")}
                  onClick={() => openRevisionReview()}
                  close={close}
                />
                <MiniMenuItem
                  icon={musicGenerating ? <Loader2 size={15} className="animate-spin" /> : <Music size={15} />}
                  title="生成音乐"
                  disabled={Boolean(loading) || musicGenerating || !timelineSegments.length}
                  onClick={generateEpisodeMusic}
                  close={close}
                />
                <MiniMenuItem
                  icon={downloadingAllVideos ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                  title="下载全部"
                  badge={downloadableSegments.length || null}
                  disabled={!downloadableSegments.length || downloadingAllVideos}
                  onClick={downloadAllVideos}
                  close={close}
                />
              </>
            )}
          </MiniActionMenu>
        </div>
      </div>

      <MiniSettingsModal
        open={splitSettingsOpen}
        title="分镜设置"
        onClose={() => setSplitSettingsOpen(false)}
      >
        <div className="space-y-5">
          <section>
            <div className="mb-2 text-xs font-semibold" style={{ color: "var(--color-text-muted)" }}>生成模式</div>
            <div className="grid grid-cols-2 gap-2">
              {STORY_MODES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setStoryMode(item.id)}
                  disabled={Boolean(loading)}
                  className="h-11 rounded-xl px-3 text-sm font-medium transition disabled:opacity-45"
                  style={storyMode === item.id ? primaryButtonStyle : ghostButtonStyle}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </section>
          <section>
            <div className="mb-2 text-xs font-semibold" style={{ color: "var(--color-text-muted)" }}>增强提示词</div>
            <StoryboardEnhancementPicker
              disabled={Boolean(loading)}
              selectedModes={storyboardEnhancementModes}
              onToggle={toggleStoryboardEnhancementMode}
            />
          </section>
          <button
            onClick={() => {
              setSplitSettingsOpen(false);
              requestGeneratePromptSegments();
            }}
            disabled={Boolean(loading) || scriptMissing || !segmentsLoadReady}
            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
            style={primaryButtonStyle}
          >
            {loading === "script" ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            拆分分镜
          </button>
        </div>
      </MiniSettingsModal>

      <MiniSettingsModal
        open={videoSettingsOpen}
        title="视频设置"
        onClose={() => setVideoSettingsOpen(false)}
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="min-w-0 sm:col-span-3">
              <span className="mb-1.5 block text-xs font-semibold" style={{ color: "var(--color-text-muted)" }}>视频模型</span>
              <AppSelect
                value={videoModel}
                onChange={(event) => {
                  const nextModel = event.target.value;
                  const nextVega = isWandVegaModel(nextModel);
                  if (nextVega || wan3Project) {
                    setVideoModel(nextVega ? nextModel : normalizeWanxVideoModel(nextModel));
                    setVideoProvider(nextVega ? WAND_VEGA_PROVIDER : WANX_VIDEO_PROVIDER);
                    setVideoResolution((current) => nextVega
                      ? normalizeWandVegaResolution(current)
                      : normalizeWanxVideoResolution(current, "1080P"));
                    setVideoRatio((current) => nextVega
                      ? normalizeWandVegaRatio(current)
                      : normalizeWanxVideoRatio(current, "adaptive"));
                    return;
                  }
                  setVideoModel(nextModel);
                  if (nextModel === SEEDANCE25_MODEL) {
                    const nextProvider = isSeedance25Provider(videoProvider)
                      ? videoProvider
                      : DEFAULT_SEEDANCE25_PROVIDER;
                    setVideoProvider(nextProvider);
                    setVideoResolution(normalizeSeedance25Resolution(nextProvider, videoResolution));
                    setVideoRatio((current) => normalizeSeedance25Ratio(nextProvider, current, getProjectDefaultVideoRatio(projectId)));
                  } else {
                    const nextProvider = isDomesticOfficialSeedance(videoProvider) || nextModel === DOMESTIC_SEEDANCE_MODELS[2].id ? VIDEO_PROVIDER_VOLCENGINE_SEEDANCE : defaultVideoProvider;
                    setVideoProvider(nextProvider);
                    setVideoResolution((current) => isDomesticOfficialSeedance(nextProvider) ? normalizeDomesticSeedanceResolution(nextModel, current) : normalizeMiniStoryboardVideoResolution(current, nextModel));
                  }
                }}
                className="h-10 w-full rounded-xl px-3 text-sm outline-none"
                style={inputSurfaceStyle}
              >
                {videoModelOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </AppSelect>
            </label>
            {!overseasProject && !wan3Project && !vegaSelected && videoModel !== SEEDANCE25_MODEL && (
              <label className="min-w-0 sm:col-span-3">
                <span className="mb-1.5 block text-xs font-semibold" style={{ color: 'var(--color-text-muted)' }}>2.0 厂商线路</span>
                <AppSelect value={activeVideoProvider} onChange={(event) => {
                  const nextProvider = event.target.value;
                  setVideoProvider(nextProvider);
                  if (!isDomesticOfficialSeedance(nextProvider) && videoModel === DOMESTIC_SEEDANCE_MODELS[2].id) setVideoModel(DOMESTIC_SEEDANCE_MODELS[0].id);
                }} className="h-10 w-full rounded-xl px-3 text-sm outline-none" style={inputSurfaceStyle}>
                  {!isDomesticOfficialSeedance(defaultVideoProvider) && <option value={defaultVideoProvider}>{getVideoProviderLabel(defaultVideoProvider)}（项目原线路）</option>}
                  <option value={VIDEO_PROVIDER_VOLCENGINE_SEEDANCE}>Seedance 2.0 / Fast / Mini（国内官方）</option>
                </AppSelect>
              </label>
            )}
            {videoModel === SEEDANCE25_MODEL && (
              <label className="min-w-0 sm:col-span-3">
                <span className="mb-1.5 block text-xs font-semibold" style={{ color: "var(--color-text-muted)" }}>2.5 厂商线路</span>
                <AppSelect
                  value={videoProvider}
                  onChange={(event) => {
                    const nextProvider = event.target.value;
                    const capabilities = getSeedance25Capabilities(nextProvider);
                    if (!capabilities) return;
                    setVideoProvider(nextProvider);
                    setVideoResolution((current) => normalizeSeedance25Resolution(nextProvider, current));
                    setVideoRatio((current) => normalizeSeedance25Ratio(nextProvider, current, getProjectDefaultVideoRatio(projectId)));
                  }}
                  className="h-10 w-full rounded-xl px-3 text-sm outline-none"
                  style={inputSurfaceStyle}
                >
                  {getSeedance25Capabilities(videoProvider)?.retired && <option value={videoProvider} disabled>{getVideoProviderLabel(videoProvider)} · 已停用，请选择官方渠道</option>}
                  {SEEDANCE25_PROVIDER_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </AppSelect>
              </label>
            )}
            <label className="min-w-0">
              <span className="mb-1.5 block text-xs font-semibold" style={{ color: "var(--color-text-muted)" }}>清晰度</span>
              <AppSelect value={videoResolution} onChange={(event) => setVideoResolution(event.target.value)} className="h-10 w-full rounded-xl px-3 text-sm outline-none" style={inputSurfaceStyle}>
                {videoResolutionOptions.map((item) => <option key={item} value={item}>{item}</option>)}
              </AppSelect>
            </label>
            <label className="min-w-0">
              <span className="mb-1.5 block text-xs font-semibold" style={{ color: "var(--color-text-muted)" }}>视频比例</span>
              <AppSelect value={videoRatio} onChange={(event) => setVideoRatio(event.target.value)} className="h-10 w-full rounded-xl px-3 text-sm outline-none" style={inputSurfaceStyle}>
                {videoRatioOptions.map((item) => <option key={item} value={item}>{item}</option>)}
              </AppSelect>
            </label>
          </div>
          {(wan3Selected || vegaSelected) && (
            <div className="rounded-xl px-3 py-2 text-xs leading-5" style={subtleSurfaceStyle}>
              {vegaSelected
                ? `分镜保留本项目的 ${splitDurationSeconds} 秒拆分节奏；当前选择 ${activeVideoModelName}，单段视频支持 ${durationLimits.min}–${durationLimits.max} 秒。参考图需原始公网 URL，最多 ${WAND_VEGA_STORYBOARD_REFERENCE_LIMIT} 张。Vega 测试期间使用站内暂定价格，已开放 720P、1080P、2K 和 4K。`
                : "Wan 3.0 项目的分镜仍按 30 秒节奏拆分；视频可选万相标准版/高速版或 Wand Vega。万相支持 2–30 秒、最多 10 张参考图，参考图直接传 URL。"}
            </div>
          )}
          {seedance25Selected && (
            <div className="rounded-xl px-3 py-2 text-xs leading-5" style={subtleSurfaceStyle}>
              {sd25Project
                ? `本项目的 AI 分镜按 30 秒连续剧情编排；${videoProviderLabel} 支持 ${seedance25Capabilities.minDuration}-${seedance25Capabilities.maxDuration} 秒、最多 ${seedance25Capabilities.maxReferenceImages} 张参考图。${seedance25Capabilities.referenceNotice || "历史线路已停止新生成，请选择官方渠道。"}`
                : `普通项目的 AI 分镜仍按原来的 15 秒节奏；选择 ${videoProviderLabel} 后，已有或新增段落可编辑为 ${seedance25Capabilities.minDuration}-30 秒，生成时最长 ${seedance25Capabilities.maxDuration} 秒。${seedance25Capabilities.referenceNotice || "历史线路已停止新生成，请选择官方渠道。"}`}
              {seedance25Capabilities.referenceNotice || ''}
            </div>
          )}
        </div>
      </MiniSettingsModal>

      {initialLoading ? (
        <MiniStoryboardLoadingState />
      ) : segmentsLoadError ? (
        <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-3 rounded-xl border px-6 py-8 text-center" style={panelStyle} role="alert">
          <AlertTriangle size={26} style={{ color: "var(--color-error)" }} />
          <div className="text-base font-semibold">小故事板暂时无法加载</div>
          <p className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>
            {segmentsLoadError}。为避免覆盖已保存的分镜，已暂停拆分操作。
          </p>
          <button type="button" onClick={loadData} disabled={Boolean(loading)} className="inline-flex h-9 items-center gap-2 rounded-full px-4 text-sm font-medium disabled:opacity-50" style={primaryButtonStyle}>
            <RefreshCw size={15} /> 重新加载
          </button>
        </div>
      ) : episodeLoadError && !segments.length ? (
        <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-3 rounded-xl border px-6 py-8 text-center" style={panelStyle} role="alert">
          <AlertTriangle size={26} style={{ color: "var(--color-error)" }} />
          <div className="text-base font-semibold">剧集资料暂时无法加载</div>
          <p className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>
            {episodeLoadError}。无法确认剧本，已暂停拆分；请重试加载。
          </p>
          <button type="button" onClick={loadData} disabled={Boolean(loading)} className="inline-flex h-9 items-center gap-2 rounded-full px-4 text-sm font-medium disabled:opacity-50" style={primaryButtonStyle}>
            <RefreshCw size={15} /> 重新加载
          </button>
        </div>
      ) : scriptMissing && !segments.length ? (
        <MissingScriptPanel projectId={projectId} episodeId={episodeId} onRefresh={loadData} />
      ) : (
      <>
      {episodeLoadError && (
        <div className="mx-2 mt-2 flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-warning)" }} role="status">
          <span>剧集资料加载失败，现有分镜仍可查看；拆分需先重新加载剧本。</span>
          <button type="button" onClick={loadData} disabled={Boolean(loading)} className="inline-flex items-center gap-1 disabled:opacity-50">
            <RefreshCw size={13} /> 重试
          </button>
        </div>
      )}
      <div ref={miniWorkspaceGridRef} className={`${mainWorkspaceClass} animate-in fade-in duration-300`} style={miniMainGridStyle}>
        <section className={`mini-workbench__panel mini-workbench__preview-panel relative flex min-h-0 flex-col overflow-y-auto rounded-xl p-2.5 custom-scrollbar ${mobileWorkspace ? "h-[min(42vh,380px)] min-h-[260px] shrink-0" : ""}`} style={panelStyle}>
          <div className="mini-workbench__panel-heading mb-2 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="mini-workbench__section-label text-xs" style={{ color: "var(--color-text-muted)" }}>当前预览</div>
              <div className="truncate text-base font-semibold">{selectedSegment?.title || "分镜组 01"}</div>
            </div>
            <div className="mini-workbench__panel-actions flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={openVideoHistory}
                disabled={!selectedSegment?.segmentIndex}
                className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs disabled:opacity-40"
                style={historyDrawerOpen ? primaryButtonStyle : ghostButtonStyle}
                title="查看当前片段的视频生成历史"
              >
                <History size={14} />
                {!focusWorkspace && "生成历史"}
              </button>
              <span className="mini-workbench__duration-badge rounded-full px-2 py-1 text-xs" style={statusStyle(selectedSegment)}>{selectedSegment?.durationSeconds || durationSeconds}s</span>
            </div>
          </div>
          <div ref={previewStageRef} className={`mini-workbench__preview-stage flex flex-1 ${showInlineAssetPanel ? "min-h-0 flex-col items-center overflow-y-auto custom-scrollbar" : "min-h-0 items-center justify-center overflow-hidden"}`}>
            <div
              ref={previewFrameRef}
              className={`mini-workbench__viewer-frame overflow-hidden rounded-lg ${showInlineAssetPanel ? "my-auto shrink-0" : ""}`}
              style={{
                aspectRatio: "9 / 16",
                width: previewFrameWidth,
                maxHeight: showInlineAssetPanel ? undefined : "100%",
                backgroundColor: "var(--color-bg-overlay)",
              }}
            >
              <SegmentPreview
                segment={selectedSegment}
                videoRef={previewVideoRef}
                onTimeUpdate={handlePreviewTimeUpdate}
                onEnded={handlePreviewEnded}
              />
            </div>
          </div>
          {historyDrawerOpen && (
            <VideoHistoryDrawer
              items={historyLoadedSegmentKey === selectedHistorySegmentKey ? videoHistory : []}
              stats={historyLoadedSegmentKey === selectedHistorySegmentKey ? videoHistoryStats : null}
              loading={videoHistoryLoading || historyLoadedSegmentKey !== selectedHistorySegmentKey}
              switching={loading === "history-switch"}
              error={historyLoadedSegmentKey === selectedHistorySegmentKey ? videoHistoryError : ""}
              segment={selectedSegment}
              onClose={() => setHistoryDrawerOpen(false)}
              onRetry={() => loadSegmentHistory(selectedSegment.segmentIndex, selectedSegment.id)}
              onSelect={selectHistoryVideo}
              onDownload={downloadHistoryVideo}
            />
          )}
        </section>

        {workspaceMode === "full" && (
          <MiniColumnResizeHandle
            title="调整预览区和编辑区宽度"
            onPointerDown={(event) => startMiniColumnResize("main", event)}
          />
        )}

        <div ref={miniPromptGridRef} className={promptShellClass} style={miniPromptGridStyle}>
        <section className="mini-workbench__panel mini-workbench__prompt-panel flex min-h-0 flex-col rounded-xl" style={panelStyle}>
          <div className={`mini-workbench__panel-heading mini-workbench__prompt-heading flex shrink-0 items-start justify-between gap-2 px-4 py-3 ${mobileWorkspace ? "flex-col" : ""}`} style={{ borderBottom: "1px solid var(--color-border-primary)" }}>
            <div className="min-w-0">
              <div className="truncate text-lg font-semibold">{selectedSegment?.title || "分镜组"}</div>
              <div className="mini-workbench__prompt-meta mt-1 flex flex-wrap items-center gap-2 text-xs" style={{ color: "var(--color-text-muted)" }}>
                <span>{selectedTimelineRange} · {currentShots.length || 0} 个镜头</span>
                <span>参考图 {displayReferenceItems.length}</span>
                <span>{isOneClickFlowSegment(selectedSegment) ? "一键流分镜" : selectedSegment?.storyMode === "narration" ? "解说剧" : activeStoryMode.name}</span>
                {selectedSegment?.storyMode === "narration" && firstText(selectedSegment?.narrationText) && (
                  <span>解说 {firstText(selectedSegment.narrationText).length} 字</span>
                )}
                <span className="rounded-full px-2 py-0.5" style={statusStyle(selectedSegment)}>{statusLabel(selectedSegment)}</span>
              </div>
            </div>
            <div className={`mini-workbench__panel-actions flex shrink-0 items-center justify-end gap-1.5 ${mobileWorkspace ? "w-full flex-wrap justify-start" : ""}`}>
              {!showInlineAssetPanel && (
                <button
                  type="button"
                  onClick={() => {
                    setSidePanelSection("assets");
                    setAssetDrawerOpen(true);
                  }}
                  className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs"
                  style={ghostButtonStyle}
                  title="打开参考图和资产库"
                >
                  <ImageIcon size={15} />
                  {focusWorkspace ? displayReferenceItems.length : `参考图 ${displayReferenceItems.length}`}
                </button>
              )}
              <button
                type="button"
                onClick={() => setActingDirectionOpen(true)}
                disabled={!selectedSegment?.segmentIndex}
                className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-40"
                style={ghostButtonStyle}
                title="为本段角色设置表情、呼吸和反应停顿"
              >
                <Users size={15} />
                表演
              </button>
              {editing ? (
                <button onClick={saveDraft} disabled={Boolean(loading)} data-primary-action="true" className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs font-medium disabled:opacity-40" style={primaryButtonStyle}>
                  {loading === "save" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                  {!focusWorkspace && "保存"}
                </button>
              ) : (
                <button onClick={beginEdit} className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs font-medium" style={ghostButtonStyle}>
                  <Clipboard size={15} />
                  {focusWorkspace ? "编辑" : "编辑 Prompt"}
                </button>
              )}
              <button
                onClick={requestGenerateCurrentVideo}
                disabled={!currentGenerationPrompt || Boolean(loading)}
                className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs font-medium disabled:opacity-40"
                style={ghostButtonStyle}
              >
                {loading === "video-current" ? <Loader2 size={15} className="animate-spin" /> : <Film size={15} />}
                {focusWorkspace ? "生成" : isVideoFailed(selectedSegment) ? "重试视频" : isVideoSuccess(selectedSegment) ? "重新生成" : "生成当前"}
              </button>
              <MiniActionMenu label="更多" icon={<MoreHorizontal size={15} />}>
                {(close) => (
                  <>
                    <MiniMenuItem
                      icon={<BookOpen size={15} />}
                      title="查看原文"
                      onClick={openSourceViewer}
                      close={close}
                    />
                    <MiniMenuItem
                      icon={<History size={15} />}
                      title="生成历史"
                      disabled={!selectedSegment?.segmentIndex}
                      onClick={openVideoHistory}
                      close={close}
                    />
                    <MiniMenuItem
                      icon={<Copy size={15} />}
                      title="复制 Prompt"
                      disabled={!selectedSegment?.seedancePrompt}
                      onClick={copyPrompt}
                      close={close}
                    />
                    <MiniMenuItem
                      icon={<Copy size={15} />}
                      title="复制段落"
                      description="复制当前文字、镜头和参考图，插入到下一段"
                      disabled={!segments.length || !selectedSegment?.segmentIndex || Boolean(loading)}
                      onClick={duplicateSelectedSegment}
                      close={close}
                    />
                    <MiniMenuItem
                      icon={downloadingSegmentIndex === selectedSegment?.segmentIndex ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                      title="下载视频"
                      disabled={!isVideoSuccess(selectedSegment) || downloadingSegmentIndex === selectedSegment?.segmentIndex}
                      onClick={downloadCurrentVideo}
                      close={close}
                    />
                    <MiniMenuItem
                      icon={currentErasingSubtitle ? <Loader2 size={15} className="animate-spin" /> : <RemoveFormatting size={15} />}
                      title="去字幕"
                      disabled={!currentCanEraseSubtitle || currentErasingSubtitle}
                      onClick={eraseCurrentSubtitle}
                      close={close}
                    />
                    <MiniMenuItem
                      icon={<Trash2 size={15} />}
                      title="删除当前段落"
                      danger
                      disabled={!segments.length || Boolean(loading)}
                      onClick={requestDeleteSelectedSegment}
                      close={close}
                    />
                  </>
                )}
              </MiniActionMenu>
            </div>
          </div>

          {oneClickFlowWarnings.length > 0 && (
            <details
              key={`${selectedSegment?.id || selectedIndex}:${Boolean(oneClickFlowDurationError)}`}
              open={Boolean(oneClickFlowDurationError) || undefined}
              className="mx-4 mt-3 shrink-0 rounded-lg border px-3 py-2 text-xs"
              style={{ color: "var(--color-warning)", borderColor: "var(--color-border-primary)" }}
            >
              <summary className="cursor-pointer select-none">
                一键流检查提示 · {oneClickFlowWarnings.length} 项（提示词已保存）
              </summary>
              <ul className="mt-2 max-h-28 list-disc space-y-1 overflow-y-auto pl-4 leading-relaxed custom-scrollbar">
                {oneClickFlowWarnings.map((message) => <li key={message}>{message}</li>)}
              </ul>
            </details>
          )}

          {shellDisabled ? (
            <div className="flex flex-1 items-center justify-center text-sm" style={{ color: "var(--color-text-muted)" }}>
              <Loader2 size={18} className="mr-2 animate-spin" />
              正在加载
            </div>
          ) : (
            <div className="mini-workbench__prompt-scroll min-h-0 flex-1 overflow-y-auto px-4 py-3 custom-scrollbar">
              {editing ? (
                <textarea
                  value={draft?.seedancePrompt || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, seedancePrompt: e.target.value }))}
                  disabled={loading === "save"}
                  className={promptTextareaClass}
                  style={{ ...inputSurfaceStyle, ...promptTextStyle }}
                  placeholder={storyMode === "narration"
                    ? "这里编辑完整解说剧视频提示词。解说是画外音，不要写字幕，不要让人物对口型。"
                    : "这里编辑完整视频提示词。电影感、无字幕、日 内、地点、分镜画面、声音、文本都属于这一整段 prompt。"}
                />
              ) : segments.length === 0 ? (
                <div className="flex h-80 flex-col items-center justify-center rounded-xl border border-dashed text-center" style={{ ...subtleSurfaceStyle, color: "var(--color-text-muted)" }}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}>
                    <Film size={26} />
                  </div>
                  <div className="mt-4 text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>还没有小故事板</div>
                  <button
                    type="button"
                    onClick={requestGeneratePromptSegments}
                    disabled={Boolean(loading) || scriptMissing || !segmentsLoadReady}
                    className="mt-4 inline-flex h-10 items-center justify-center gap-2 rounded-full px-5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                    style={primaryButtonStyle}
                  >
                    {loading === "script" ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                    拆分分镜
                  </button>
                </div>
              ) : (
                <PromptWithReferenceChips
                  prompt={displaySubmissionPrompt || "无"}
                  references={displayReferenceItems}
                  className={promptTextBoxClass}
                  textStyle={promptTextStyle}
                />
              )}
            </div>
          )}

          <div className={`mini-workbench__prompt-footer ${promptFooterClass}`} style={{ borderTop: "1px solid var(--color-border-primary)", color: "var(--color-text-secondary)" }}>
            <label className="min-w-0">
              <span className="mb-1 block" style={{ color: "var(--color-text-muted)" }}>时长</span>
              {editing ? (
                <div className="flex h-8 items-center rounded-full px-2" style={inputSurfaceStyle}>
                  <input
                    type="number"
                    min={durationLimits.min}
                    max={durationLimits.max}
                    step="1"
                    value={draft?.durationSeconds ?? selectedSegment?.durationSeconds ?? durationSeconds}
                    onChange={(e) => {
                      const nextDurationInput = e.target.value;
                      setDraft((current) => {
                        const base = current || selectedSegment;
                        return {
                          ...base,
                          durationSeconds: nextDurationInput,
                          __durationDirty: true,
                        };
                      });
                    }}
                    disabled={loading === "save"}
                    className="min-w-0 flex-1 bg-transparent text-xs outline-none"
                    style={{ color: "var(--color-text-primary)" }}
                  />
                  <span style={{ color: "var(--color-text-muted)" }}>s</span>
                </div>
              ) : (
                <div className="flex h-8 items-center rounded-full px-3" style={subtleSurfaceStyle}>
                  {Number(selectedSegment?.durationSeconds) || durationSeconds}s
                </div>
              )}
            </label>
            <label className="min-w-0">
              <span className="mb-1 flex min-w-0 items-center justify-between gap-2" style={{ color: "var(--color-text-muted)" }}>
                <span>打光</span>
                {(progressText || musicProgressText) && (
                  <span className="shrink-0 truncate" style={{ color: "var(--color-accent-primary)" }}>
                    {musicProgressText || progressText}
                  </span>
                )}
              </span>
              <LightingPresetPicker
                disabled={(wan3Selected && !isOneClickFlowSegment(selectedSegment)) || !selectedSegment?.seedancePrompt || Boolean(loading)}
                activeId={inferLightingPrompt(activeReferenceSegment) ? selectedLightingPreset?.id || "" : "none"}
                activeName={inferLightingPrompt(activeReferenceSegment) ? selectedLightingPreset?.name || "自定义打光" : "无打光"}
                onSelect={applyLightingPreset}
              />
            </label>
            {oneClickFlowTimingLabel && (
              <span
                className={`${mobileWorkspace ? "col-span-1" : "col-span-2"} text-[10px] leading-4`}
                style={{ color: "var(--color-text-muted)" }}
                title="镜头保留原规则的 0.1 秒预估；视频生成按当前线路的整数时长与最低时长要求提交，不改写镜头预算。编辑提示词后保存会更新镜头预估。"
              >
                {oneClickFlowTimingLabel}
              </span>
            )}
          </div>
        </section>

        {workspaceMode === "full" && (
          <MiniColumnResizeHandle
            title="调整 Prompt 和资产库宽度"
            onPointerDown={(event) => startMiniColumnResize("inner", event)}
          />
        )}

        <div className={assetStackClass} style={assetStackStyle}>
          {!showInlineAssetPanel && (
            <div className="flex shrink-0 items-center justify-between gap-2 px-1">
              <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>原文与资产</span>
              <button
                type="button"
                onClick={() => {
                  setAssetDrawerOpen(false);
                  setSidePanelSection("assets");
                }}
                className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-full"
                style={ghostButtonStyle}
                aria-label="关闭原文与资产侧栏"
                title="关闭侧栏"
              >
                <X size={15} />
              </button>
            </div>
          )}
          <SourceTextAccordion
            open={sourceDrawerOpen}
            title={sourceViewTitle}
            selectedTitle={firstText(selectedSegment?.title, "当前分镜")}
            text={selectedSourceText}
            page={sourceViewPage}
            pageKey={sourceViewPage.key}
            fallbackToEpisodeScript={sourceFallbackToEpisodeScript}
            onToggle={toggleSourceViewer}
            onPrevious={() => moveSourcePage(-1)}
            onNext={() => moveSourcePage(1)}
          />
          <aside className={`mini-workbench__panel mini-workbench__side-accordion mini-workbench__asset-panel flex min-h-0 flex-col overflow-hidden rounded-xl ${assetSectionOpen ? "flex-1" : "shrink-0"}`} style={panelStyle}>
            <SideAccordionHeader
              open={assetSectionOpen}
              controls="mini-storyboard-asset-content"
              icon={<ImageIcon size={16} />}
              title="资产库"
              subtitle="角色、场景、道具和参考图"
              detail={`${allAssets.length} 项`}
              onToggle={toggleAssetSection}
            />
            <div id="mini-storyboard-asset-content" className={`ui-accordion-grid min-h-0 ${assetSectionOpen ? "flex-1" : ""}`} data-open={assetSectionOpen} aria-hidden={!assetSectionOpen} inert={!assetSectionOpen}>
              <div className={`ui-accordion-content flex min-h-0 flex-col ${assetSectionOpen ? "p-2.5" : ""}`} style={{ borderTop: "1px solid var(--color-border-primary)" }}>
            <div className="mini-workbench__panel-actions mb-3 flex shrink-0 items-center justify-end gap-1">
                <button
                  type="button"
                  onClick={loadAssets}
                  disabled={assetsLoading}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full disabled:opacity-50"
                  style={ghostButtonStyle}
                  aria-label="刷新资产库"
                  title="刷新资产库"
                >
                  <RefreshCw size={14} className={assetsLoading ? "animate-spin" : ""} />
                </button>
              </div>
            {referenceSelectionActive ? (
              <div className="mb-3 rounded-lg px-3 py-2 text-xs leading-5" style={{ backgroundColor: "var(--color-accent-light)", color: "var(--color-accent-primary)", border: "1px solid var(--color-accent-primary)" }}>
                多选参考图中：已选 {referencePanelItems.length} 张。点击资产可选中或取消，点下方“确定”后保存。
              </div>
            ) : referenceReplaceIndex != null && (
              <div className="mb-3 rounded-lg px-3 py-2 text-xs leading-5" style={{ backgroundColor: "var(--color-accent-light)", color: "var(--color-accent-primary)", border: "1px solid var(--color-accent-primary)" }}>
                正在替换 @图片{referenceReplaceIndex + 1}，点击下方任意资产即可绑定到当前片段。
              </div>
            )}
            <div className="mini-workbench__asset-tabs mb-3 flex gap-1 rounded-full p-1" style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-secondary)" }}>
              {[
                ["all", "全部"],
                ["characters", "角色"],
                ["scenes", "场景"],
                ["props", "道具"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => {
                    setAssetType(key);
                    setAssetPage(1);
                  }}
                  className="h-7 min-w-0 flex-1 whitespace-nowrap rounded-full text-xs"
                  aria-pressed={assetType === key}
                  style={assetType === key ? primaryButtonStyle : { color: "var(--color-text-muted)" }}
                >
                  {label}
                </button>
              ))}
            </div>
            <label className="mini-workbench__asset-search mb-3 flex h-9 items-center gap-2 rounded-md px-3" style={{ ...inputSurfaceStyle, color: "var(--color-text-muted)" }}>
              <Search size={15} />
              <input
                value={assetQuery}
                onChange={(event) => {
                  setAssetQuery(event.target.value);
                  setAssetPage(1);
                }}
                placeholder="全局搜索资产"
                aria-label="全局搜索资产"
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:opacity-70"
                style={{ color: "var(--color-text-primary)" }}
              />
            </label>
            <div className="mini-workbench__asset-grid grid min-h-0 flex-1 grid-cols-[repeat(auto-fill,minmax(76px,1fr))] gap-2 overflow-y-auto pr-1 custom-scrollbar">
              {pagedAssets.map(({ asset, type }, index) => (
                <AssetCard
                  key={`${type}-${asset?.id || index}`}
                  asset={asset}
                  type={type}
                  selectable={referenceReplaceIndex != null || referenceSelectionActive}
                  selected={referenceSelectionActive && assetSelectedInReferenceDraft(asset, type)}
                  onSelect={referenceSelectionActive ? toggleReferenceSelectionAsset : referenceReplaceIndex != null ? replaceReferenceWithAsset : undefined}
                />
              ))}
              {!assetsLoading && allAssets.length === 0 && (
                <div className="col-span-3 flex min-h-32 items-center justify-center rounded-lg border border-dashed text-xs" style={{ ...subtleSurfaceStyle, color: "var(--color-text-muted)" }}>
                  没有匹配的参考图
                </div>
              )}
            </div>
            <div className="mt-3 flex shrink-0 items-center justify-between gap-2 text-xs" style={{ color: "var(--color-text-muted)" }}>
              <span>
                {assetsLoading ? "加载中" : `共 ${allAssets.length} 个 · ${assetPage}/${assetTotalPages} 页`}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={assetPage <= 1 || assetsLoading}
                  onClick={() => setAssetPage((page) => Math.max(1, page - 1))}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md disabled:opacity-40"
                  style={ghostButtonStyle}
                  aria-label="上一页参考图"
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  type="button"
                  disabled={assetPage >= assetTotalPages || assetsLoading}
                  onClick={() => setAssetPage((page) => Math.min(assetTotalPages, page + 1))}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md disabled:opacity-40"
                  style={ghostButtonStyle}
                  aria-label="下一页参考图"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
            {timelineSegments.length > 0 && (
              <button
                type="button"
                onClick={() => openBlockingEditor(selectedBlockingPlan || null)}
                className="mini-workbench__blocking-link mt-3 flex w-full shrink-0 items-center gap-2 rounded-lg p-2 text-left"
                style={{
                  backgroundColor: "color-mix(in srgb, var(--color-accent-primary) 10%, var(--color-bg-overlay))",
                  border: "1px solid color-mix(in srgb, var(--color-accent-primary) 42%, var(--color-border-secondary))",
                }}
                title="编辑当前片段使用的站位关系"
              >
                {firstText(selectedBlockingPlan?.modelReferenceUrl) ? (
                  <img
                    src={selectedBlockingPlan.modelReferenceUrl}
                    alt="站位关系"
                    className="h-11 w-11 shrink-0 rounded-md object-cover"
                  />
                ) : (
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                    <Move3d size={18} style={{ color: "var(--color-accent-primary)" }} />
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block text-[11px]" style={{ color: "var(--color-text-muted)" }}>站位关系</span>
                  <span className="block truncate text-xs font-medium">{firstText(selectedBlockingPlan?.name, "当前片段未设置")}</span>
                </span>
                <span className="text-[11px]" style={{ color: "var(--color-accent-primary)" }}>{selectedBlockingPlan ? "编辑" : "设置"}</span>
              </button>
            )}
            <ReferenceBindingPanel
              projectId={projectId}
              references={referencePanelItems}
              activeIndex={referenceReplaceIndex}
              open={referenceDrawerOpen}
              onToggle={() => setReferenceDrawerOpen((value) => !value)}
              onReplace={beginReferenceReplace}
              onRemove={referenceSelectionActive ? removeReferenceSelectionDraft : removeReferenceBinding}
              onAdd={beginReferenceSelection}
              onUpload={openReferenceUpload}
              selectionMode={referenceSelectionActive}
              saving={loading === "reference"}
              uploading={referenceUploading}
              ingestingUrls={referenceAssetIngestingUrls}
              videoProvider={activeVideoProvider}
              onIngest={ingestReferenceAsset}
              onConfirmSelection={confirmReferenceSelection}
              onCancelSelection={cancelReferenceSelection}
            />
            <input
              ref={referenceUploadInputRef}
              type="file"
              accept={supportsSeedance25MediaReferences(activeVideoProvider) ? "image/*,video/*,audio/*" : "image/*"}
              multiple
              className="hidden"
              onChange={handleReferenceUploadInputChange}
            />
              </div>
            </div>
          </aside>
        </div>
        {issueDrawerOpen && (
          <EpisodeIssueDrawer
            issues={revisionIssues}
            draft={issueDraft}
            loading={revisionIssuesLoading}
            referenceUploading={issueReferenceUploading}
            selectedIssueId={selectedIssueId}
            onClose={() => setIssueDrawerOpen(false)}
            onNew={newRevisionIssueDraft}
            onSelect={selectRevisionIssue}
            onChange={patchRevisionIssueDraft}
            onSave={saveRevisionIssue}
            onDelete={deleteRevisionIssue}
            onUploadReferenceImages={uploadRevisionIssueReferenceImages}
          />
        )}
        </div>
      </div>

      <MiniStoryboardTimeline
        workspaceMode={workspaceMode}
        mobileWorkspace={mobileWorkspace}
        loading={loading}
        timelineSegments={timelineSegments}
        timelineLayout={timelineLayout}
        selectedSegment={selectedSegment}
        durationSeconds={durationSeconds}
        totalDuration={totalDuration}
        playheadSeconds={playheadSeconds}
        playheadPixel={playheadPixel}
        timelineScrollRef={timelineScrollRef}
        timelineDragbarRef={timelineDragbarRef}
        timelineDragThumb={timelineDragThumb}
        timelineScrollMetrics={timelineScrollMetrics}
        updateTimelineScrollMetrics={updateTimelineScrollMetrics}
        handleTimelineDragbarPointerDown={handleTimelineDragbarPointerDown}
        revisionIssues={revisionIssues}
        openRevisionIssueCount={openRevisionIssueCount}
        openRevisionReview={openRevisionReview}
        musicCues={musicCues}
        musicTrackBlocks={musicTrackBlocks}
        blockingTrackBlocks={blockingTrackBlocks}
        openBlockingTrackPlan={openBlockingTrackPlan}
        musicEnabled={musicEnabled}
        setMusicEnabled={setMusicEnabled}
        playing={playing}
        toggleTimelinePlayback={toggleTimelinePlayback}
        seekToTimeline={seekToTimeline}
        requestGenerateRemaining={requestGenerateRemaining}
        getTimelinePixelAtSeconds={getTimelinePixelAtSeconds}
        handleTimelineSegmentClick={handleTimelineSegmentClick}
        insertManualSegment={insertManualSegment}
        statusStyle={statusStyle}
        statusLabel={statusLabel}
        isVideoProcessing={isVideoProcessing}
        isVideoFailed={isVideoFailed}
        isMusicCueSuccess={isMusicCueSuccess}
        formatSeconds={formatSeconds}
        issuePriorityMeta={issuePriorityMeta}
        issueTypeName={issueTypeName}
        primaryButtonStyle={primaryButtonStyle}
        ghostButtonStyle={ghostButtonStyle}
        subtleSurfaceStyle={subtleSurfaceStyle}
        timelineInsertWidth={TIMELINE_INSERT_WIDTH}
        timelineGap={TIMELINE_GAP}
      />
      </>
      )}
      {insertSegmentDialog && (
        <MiniSegmentInsertDialog
          open
          insertIndex={Number(insertSegmentDialog.insertIndex || 1)}
          generatedSegments={generatedVideoSegments}
          defaultSourceIndex={insertSegmentDialog.defaultSourceIndex}
          defaultFrameSecond={insertSegmentDialog.defaultFrameSecond}
          loading={insertSegmentLoading || loading === "insert-segment"}
          onClose={() => {
            if (!insertSegmentLoading && loading !== "insert-segment") setInsertSegmentDialog(null);
          }}
          onConfirm={confirmInsertSegment}
        />
      )}
      <ActingDirectionModal
        open={actingDirectionOpen}
        segment={activeReferenceSegment}
        characters={actingCharacterOptions}
        overseas={overseasProject}
        saving={actingDirectionSaving}
        onClose={() => {
          if (!actingDirectionSaving) setActingDirectionOpen(false);
        }}
        onSave={insertActingDirectionIntoPrompt}
      />
      <React.Suspense fallback={null}>
        <BlockingEditorModal
          open={blockingEditorOpen}
          projectId={projectId}
          episodeId={episodeId}
          segments={timelineSegments}
          characters={assets.characters || []}
          scenes={assets.scenes || []}
          aspectRatio={videoRatio}
          videoProvider={activeVideoProvider}
          plans={blockingPlans}
          assignments={blockingAssignments}
          initialPlan={blockingEditorPlan}
          selectedSegment={selectedSegment}
          onClose={() => {
            setBlockingEditorOpen(false);
            setBlockingEditorPlan(null);
          }}
          onSelectPlan={(plan) => setBlockingEditorPlan(plan || null)}
          onCreateNew={() => setBlockingEditorPlan(null)}
          onSaved={handleBlockingSaved}
        />
      </React.Suspense>
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteSegment}
        title="删除分镜组"
        message={`确定删除 ${deleteTarget?.title || `分镜组 ${String(deleteTarget?.segmentIndex || "").padStart(2, "0")}`} 吗？删除后后续片段会自动前移，此操作不可撤销。`}
        confirmLabel="删除"
        cancelLabel="取消"
        variant="danger"
      />
      <ConfirmDialog
        isOpen={!!operationConfirm}
        onClose={() => setOperationConfirm(null)}
        onConfirm={() => operationConfirm?.onConfirm?.()}
        title={operationConfirm?.title || "确认操作"}
        message={operationConfirm?.message || "确定继续吗？"}
        confirmLabel={operationConfirm?.confirmLabel || "确认"}
        cancelLabel="取消"
        variant="default"
      />
    </div>
  );
};

export default MiniStoryboardWorkbench;
